import { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";
import * as XLSX from "xlsx";
import DOMPurify from "dompurify";
import { CONFIG } from "./config";
import { logger } from "./logger";

// ── Theme System ──
const ThemeContext = createContext("dark");
function useTheme() { return useContext(ThemeContext); }

const THEMES = {
  dark: {
    "--bg-page":        "linear-gradient(135deg,#0f172a,#1e1b4b,#0f172a)",
    "--bg-card":        "rgba(255,255,255,0.05)",
    "--bg-card-inner":  "rgba(255,255,255,0.04)",
    "--border-card":    "rgba(255,255,255,0.1)",
    "--border-input":   "rgba(255,255,255,0.12)",
    "--bg-input":       "rgba(255,255,255,0.06)",
    "--bg-progress":    "rgba(255,255,255,0.07)",
    "--text-primary":   "#ffffff",
    "--text-secondary": "#e2e8f0",
    "--text-muted":     "#94a3b8",
    "--text-faint":     "#64748b",
    "--text-input":     "#f1f5f9",
    "--btn-ghost-border":"rgba(255,255,255,0.12)",
    "--btn-ghost-color": "#94a3b8",
    "--nav-active-bg":  "rgba(129,140,248,0.15)",
    "--nav-active-color":"#818cf8",
    "--nav-active-border":"#818cf8",
    "--nav-inactive-color":"#94a3b8",
    "--nav-inactive-border":"rgba(255,255,255,0.1)",
    "--logo-filter":    "none",
    "--bg-modal":       "#1a2035",
  },
  light: {
    "--bg-page":        "linear-gradient(135deg,#f0f4ff,#e8edff,#f0f4ff)",
    "--bg-card":        "rgba(255,255,255,0.85)",
    "--bg-card-inner":  "rgba(0,0,0,0.03)",
    "--border-card":    "rgba(0,0,0,0.1)",
    "--border-input":   "rgba(0,0,0,0.18)",
    "--bg-input":       "rgba(255,255,255,0.9)",
    "--bg-progress":    "rgba(0,0,0,0.08)",
    "--text-primary":   "#0f172a",
    "--text-secondary": "#1e293b",
    "--text-muted":     "#475569",
    "--text-faint":     "#64748b",
    "--text-input":     "#0f172a",
    "--btn-ghost-border":"rgba(0,0,0,0.15)",
    "--btn-ghost-color": "#475569",
    "--nav-active-bg":  "rgba(99,102,241,0.12)",
    "--nav-active-color":"#4f46e5",
    "--nav-active-border":"#6366f1",
    "--nav-inactive-color":"#475569",
    "--nav-inactive-border":"rgba(0,0,0,0.12)",
    "--logo-filter":    "none",
    "--bg-modal":       "#ffffff",
  },
};

function applyTheme(name) {
  const vars = THEMES[name] || THEMES.dark;
  Object.entries(vars).forEach(([k, v]) => document.documentElement.style.setProperty(k, v));
  document.documentElement.setAttribute('data-theme', name);
}

const CODE_LANGUAGES = [
  { id: 'java',       label: 'Java' },
  { id: 'python',     label: 'Python 3' },
  { id: 'javascript', label: 'JavaScript (Node.js)' },
  { id: 'typescript', label: 'TypeScript' },
  { id: 'cpp',        label: 'C++' },
  { id: 'csharp',     label: 'C#' },
  { id: 'go',         label: 'Go' },
];

function ThemeToggle({ theme, setTheme }) {
  return (
    <button
      onClick={() => setTheme(t => t === "dark" ? "light" : "dark")}
      title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
      style={{ position: "fixed", top: "14px", right: "14px", zIndex: 9999, background: theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)", border: theme === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.12)", borderRadius: "50%", width: "38px", height: "38px", cursor: "pointer", fontSize: "18px", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(8px)", transition: "all 0.2s" }}
    >
      {theme === "dark" ? "☀️" : "🌙"}
    </button>
  );
}

// The application talks to an Express backend (`selectify-backend`).
// All AI calls go through dedicated backend routes (/api/ai/question,
// /api/ai/feedback, /api/ai/re-evaluate) — prompts are built server-side
// so they never travel over the wire.  The frontend sends only structured
// data (test code, answer text, etc.).  ANTHROPIC_API_KEY stays server-side.

const ISG_LOGO = CONFIG.LOGO_URL;
const TOTAL_QUESTIONS = CONFIG.TOTAL_QUESTIONS;
const TOTAL_INTERVIEW_SECS = CONFIG.TOTAL_INTERVIEW_SECS;
const API = CONFIG.API_BASE_URL;

const TRACKS = CONFIG.TRACKS;
const EXPERIENCE_LEVELS = CONFIG.EXPERIENCE_LEVELS;

// Log app initialization
logger.info('App initialized', {
  component: 'app',
  apiBase: API,
  totalQuestions: TOTAL_QUESTIONS,
  interviewDurationSecs: TOTAL_INTERVIEW_SECS
});

// ── Helpers ──
function fmt(s) { return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`; }
function fmtDate(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v); // already a formatted string (e.g. "12/03/2026, 18:41:28")
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
  } catch { return String(v); }
}
function typeStyle(t) { return ({ technical: { bg: "#6366f122", color: "#818cf8", label: "💻 Technical" }, aptitude: { bg: "#f59e0b22", color: "#fbbf24", label: "🧠 Aptitude" }, programming: { bg: "#10b98122", color: "#34d399", label: "👨‍💻 Programming" } }[t]) || { bg: "#6366f122", color: "#818cf8", label: "💻 Technical" }; }
function scoreColor(s) { return s >= 8 ? "#4ade80" : s >= 6 ? "#fbbf24" : "#f87171"; }
function scoreBg(s) { return s >= 8 ? "#22c55e22" : s >= 6 ? "#f59e0b22" : "#ef444422"; }
async function sha512(text) {
  const buf = await crypto.subtle.digest('SHA-512', new TextEncoder().encode(String(text)));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}
function genCode() {
  const arr = new Uint32Array(1);
  crypto.getRandomValues(arr);
  return CONFIG.CODE_PREFIX + "-" + String(arr[0] % 900000 + 100000);
}
function shuffle(arr) {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const tmp = new Uint32Array(1);
    crypto.getRandomValues(tmp);
    const j = tmp[0] % (i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}


function fallbackFeedback(skipped) {
  return { score: skipped ? 0 : 1, verdict: skipped ? "Skipped" : "Needs Improvement", strengths: skipped ? "Skipped." : "Unable to evaluate.", gaps: skipped ? "Not attempted." : "Review model answer.", modelAnswer: "Unavailable.", recommendation: "Hold" };
}
// fallback helper for non‑AI questions; complexity drives difficulty & wording
function fallbackQuestion(i, trackName, complexity = "medium") {
  // include track context when available to make the generic question slightly more useful
  const prefix = trackName ? `${trackName} ` : "General ";
  let diffText;
  if (complexity === "basic") {
    diffText = "Describe a straightforward technical problem you solved and your approach.";
  } else if (complexity === "complex") {
    diffText = "Describe a particularly challenging technical problem you solved, the architecture you chose, and why.";
  } else {
    diffText = "Describe a challenging technical problem you solved and your approach.";
  }
  return {
    question: `${prefix}Q${i + 1}: ${diffText}`,
    topic: "General",
    type: "technical",
    difficulty: complexity
  };
}

function fallbackAptitudeQuestion(i, complexity = "medium") {
  let phr;
  if (complexity === "basic") {
    phr = "Solve a simple mathematical or logical pattern problem and explain your reasoning:\nA sequence is: 2, 4, 8, 16, ?\nWhat comes next and why?";
  } else if (complexity === "complex") {
    phr = "Solve a multi-step logical reasoning or mathematical puzzle:\nIf 5 workers can complete a task in 6 days, how many days will it take 3 workers to complete the same task? Explain your approach.";
  } else {
    phr = "Solve a logical reasoning or mathematical puzzle and explain your thinking:\nFind the odd one out: 12, 18, 24, 36, 42\nWhy is it different from the others?";
  }
  return {
    question: `Aptitude Q${i + 1}: ${phr}`,
    topic: "Aptitude & Logic",
    type: "aptitude",
    difficulty: complexity
  };
}

function fallbackProgQuestion(i, trackName, complexity = "medium") {
  const prefix = trackName ? `${trackName} ` : "General ";
  let desc;
  if (complexity === "basic") {
    desc = `Write a simple function (3–5 lines) solving a routine task related to ${trackName || 'your field'}.`;  
  } else if (complexity === "complex") {
    desc = `Design and write a non-trivial function or code snippet (multiple steps) relevant to ${trackName || 'your field'}, with comments explaining your logic.`;
  } else {
    desc = `Write a function or small code snippet to solve a common problem related to ${trackName || 'your field'}. Include comments explaining your logic.`;
  }
  return {
    question: `${prefix}Q${i + 1}: ${desc}`,
    topic: "Programming",
    type: "programming",
    difficulty: complexity
  };

}

// Prompt construction and retries are handled server-side.
// These wrappers send only structured data — no raw prompts over the wire.
async function callAIQuestion(code, prevQuestions, needsProg, needsApt) {
  const r = await fetch(`${API}/api/ai/question`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, prevQuestions, needsProg, needsApt })
  });
  if (!r.ok) throw new Error(`AI HTTP ${r.status}`);
  const d = await r.json();
  if (d.error) throw new Error(d.error);
  return d;
}

// Scores and persists the answer server-side — AI response never travels to the browser.
async function callAIFeedbackAndSave(code, question, topic, type, mandatory, skipped, candidateAnswer, actualAnswer, startedAt, submittedAt, durationSecs) {
  const r = await fetch(`${API}/api/ai/feedbackAndSave`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, question, topic, type, mandatory, skipped: !!skipped, candidateAnswer, actualAnswer: actualAnswer || null, startedAt, submittedAt, durationSecs })
  });
  if (!r.ok) throw new Error(`AI HTTP ${r.status}`);
  const d = await r.json();
  if (d.error) throw new Error(d.error);
  return d;
}

// ── Auth token helpers ──
function getAuthToken() { try { return localStorage.getItem("authToken") || ""; } catch { return ""; } }
function authHeaders(extra = {}) {
  const token = getAuthToken();
  const h = { "Content-Type": "application/json", ...extra };
  if (token) h["Authorization"] = `Bearer ${token}`;
  return h;
}
function getStoredUser() {
  try {
    const token = localStorage.getItem("authToken");
    const user = JSON.parse(localStorage.getItem("authUser") || "null");
    if (!token || !user) return null;
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      localStorage.removeItem("authToken"); localStorage.removeItem("authUser"); return null;
    }
    return { ...user, token };
  } catch { return null; }
}

// ── MongoDB API helpers ──
const SESSION_EXPIRED_ERROR = Object.assign(new Error('Session expired. Please log in again.'), { isSessionExpired: true });
let _sessionExpiring = false; // set when reload is imminent — suppresses all UI error messages

function handleUnauthorized() {
  if (_sessionExpiring) throw SESSION_EXPIRED_ERROR; // already handling
  _sessionExpiring = true;
  logger.warn('Session expired — clearing auth and reloading', { component: 'api' });
  try { localStorage.removeItem("authToken"); localStorage.removeItem("authUser"); } catch {}
  window.location.reload();
  throw SESSION_EXPIRED_ERROR;
}

// Helper: should we show a UI error? Suppresses errors during session expiry race window
function shouldShowError(e) {
  return !e?.isSessionExpired && !_sessionExpiring;
}

async function apiGet(path) {
  try {
    logger.debug(`API GET: ${path}`, { component: 'api' });
    const r = await fetch(`${API}${path}`, { headers: authHeaders() });
    if (r.status === 401) handleUnauthorized();
    if (!r.ok) throw new Error(`GET ${path} failed with status ${r.status}`);
    const data = await r.json();
    logger.debug(`API GET success: ${path}`, { component: 'api', responseSize: JSON.stringify(data).length });
    return data;
  } catch (e) {
    if (!e?.isSessionExpired && !_sessionExpiring) logger.error(`API GET failed: ${path}`, { component: 'api', error: e.message });
    throw e;
  }
}

async function apiPost(path, body) {
  try {
    logger.debug(`API POST: ${path}`, { component: 'api', bodySize: JSON.stringify(body).length });
    const r = await fetch(`${API}${path}`, { method: "POST", headers: authHeaders(), body: JSON.stringify(body) });
    if (r.status === 401) handleUnauthorized();
    if (!r.ok) throw new Error(`POST ${path} failed with status ${r.status}`);
    const data = await r.json();
    logger.debug(`API POST success: ${path}`, { component: 'api' });
    return data;
  } catch (e) {
    if (!e?.isSessionExpired && !_sessionExpiring) logger.error(`API POST failed: ${path}`, { component: 'api', error: e.message });
    throw e;
  }
}

async function apiPatch(path, body) {
  try {
    logger.debug(`API PATCH: ${path}`, { component: 'api', bodySize: JSON.stringify(body).length });
    const r = await fetch(`${API}${path}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify(body) });
    if (r.status === 401) handleUnauthorized();
    if (!r.ok) throw new Error(`PATCH ${path} failed with status ${r.status}`);
    const data = await r.json();
    logger.debug(`API PATCH success: ${path}`, { component: 'api' });
    return data;
  } catch (e) {
    if (!e?.isSessionExpired && !_sessionExpiring) logger.error(`API PATCH failed: ${path}`, { component: 'api', error: e.message });
    throw e;
  }
}

async function apiDelete(path) {
  try {
    logger.debug(`API DELETE: ${path}`, { component: 'api' });
    const r = await fetch(`${API}${path}`, { method: "DELETE", headers: authHeaders() });
    if (r.status === 401) handleUnauthorized();
    if (!r.ok) throw new Error(`DELETE ${path} failed with status ${r.status}`);
    const data = await r.json();
    logger.debug(`API DELETE success: ${path}`, { component: 'api' });
    return data;
  } catch (e) {
    if (!e?.isSessionExpired && !_sessionExpiring) logger.error(`API DELETE failed: ${path}`, { component: 'api', error: e.message });
    throw e;
  }
}

// File upload helper — does NOT set Content-Type (let browser set multipart boundary)
async function apiPostFile(path, formData) {
  try {
    logger.debug(`API POST FILE: ${path}`, { component: 'api' });
    const token = getAuthToken();
    const headers = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const r = await fetch(`${API}${path}`, { method: "POST", headers, body: formData });
    if (r.status === 401) handleUnauthorized();
    if (!r.ok) {
      let errMsg = `POST FILE ${path} failed with status ${r.status}`;
      try { const d = await r.json(); if (d.error) errMsg = d.error; } catch {}
      throw new Error(errMsg);
    }
    const data = await r.json();
    logger.debug(`API POST FILE success: ${path}`, { component: 'api' });
    return data;
  } catch (e) {
    if (!e?.isSessionExpired && !_sessionExpiring) logger.error(`API POST FILE failed: ${path}`, { component: 'api', error: e.message });
    throw e;
  }
}

// ── Parse Excel question bank ──
function parseExcelBank(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    logger.info('Question bank upload started', {
      component: 'bank-upload',
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });
    
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        let rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
        
        logger.debug('Excel file parsed', {
          component: 'bank-upload',
          sheetCount: wb.SheetNames.length,
          rowCount: rows.length
        });
        
        // normalize header keys to lowercase so users can upload files with
        // "Question", "QUESTION", "question text" etc.
        rows = rows.map(r => {
          const nr = {};
          for (const k of Object.keys(r)) {
            const lk = String(k).trim().toLowerCase();
            if (lk.startsWith("question")) nr.question = r[k];
            else if (lk.startsWith("topic")) nr.topic = r[k];
            else if (lk.startsWith("type")) nr.type = r[k];
            else if (lk.startsWith("difficulty")) nr.difficulty = r[k];
            else if (lk.startsWith("mandatory")) nr.mandatory = r[k];
            else if (lk.startsWith("actual") || lk.includes("actual answer") || lk.includes("expected answer")) nr.actualAnswer = r[k];
            else if (lk.startsWith("sort") || lk.includes("sort order") || lk.includes("order")) nr.sortOrder = r[k];
            // ignore other columns
          }
          return nr;
        });
        
        const questions = rows
          .filter(r => r.question && String(r.question).trim())
          .map(r => ({
            question: String(r.question).trim(),
            topic: String(r.topic || "General").trim(),
            type: ["technical", "aptitude", "programming"].includes(String(r.type).toLowerCase().trim())
              ? String(r.type).toLowerCase().trim() : "technical",
            difficulty: ["basic", "medium", "complex"].includes(String(r.difficulty).toLowerCase().trim())
              ? String(r.difficulty).toLowerCase().trim() : "medium",
            mandatory: String(r.mandatory || "").trim().toUpperCase() === "Y",
            actualAnswer: r.actualAnswer ? String(r.actualAnswer).trim() : null,
            sortOrder: (r.sortOrder != null && String(r.sortOrder).trim() !== "") ? Number(r.sortOrder) : null,
          }));
        
        if (questions.length === 0) {
          logger.error('No valid questions in bank', {
            component: 'bank-upload',
            fileName: file.name
          });
          return reject(new Error("No valid questions found in file."));
        }
        
        const mandatoryCount = questions.filter(q => q.mandatory).length;
        logger.info('Question bank parsed successfully', {
          component: 'bank-upload',
          totalQuestions: questions.length,
          mandatoryQuestions: mandatoryCount,
          typeBreakdown: {
            technical: questions.filter(q => q.type === 'technical').length,
            aptitude: questions.filter(q => q.type === 'aptitude').length,
            programming: questions.filter(q => q.type === 'programming').length
          }
        });
        
        resolve(questions);
      } catch (err) {
        logger.error('Failed to parse Excel file', {
          component: 'bank-upload',
          fileName: file.name,
          error: err.message
        });
        reject(err);
      }
    };
    reader.onerror = () => {
      logger.error('FileReader error', {
        component: 'bank-upload',
        fileName: file.name
      });
      reject(new Error("Failed to read file."));
    };
    reader.readAsArrayBuffer(file);
  });
}

// Parse bulk test upload XLSX — columns: Candidate Name, Skill Track, Experience Level, Question Bank
function parseBulkXlsx(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        let rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
        rows = rows.map(r => {
          const nr = {};
          for (const k of Object.keys(r)) {
            const lk = String(k).trim().toLowerCase().replace(/\s+/g, "");
            if (lk.includes("candidate") || lk.includes("name")) nr.candidateName = String(r[k]).trim();
            else if (lk.includes("skill") || lk.includes("track")) nr.skillTrack = String(r[k]).trim();
            else if (lk.includes("exp") || lk.includes("level")) nr.experienceLevel = String(r[k]).trim();
            else if (lk.includes("question") || lk.includes("bank")) nr.questionBankFile = String(r[k]).trim();
          }
          return nr;
        });
        const valid = rows.filter(r => r.candidateName);
        if (valid.length === 0) return reject(new Error("No valid rows found. Check column headers."));
        resolve(valid);
      } catch (err) { reject(err); }
    };
    reader.onerror = () => reject(new Error("Failed to read file."));
    reader.readAsArrayBuffer(file);
  });
}

// build a queue from bank questions:
// - questions with sortOrder are placed first in ascending order
// - mandatory questions without sortOrder are always included next
// - remaining slots filled with randomized optional questions
function buildBankQueue(questions) {
  const orderedQs = questions.filter(q => q.sortOrder != null).sort((a, b) => a.sortOrder - b.sortOrder);
  const unorderedQs = questions.filter(q => q.sortOrder == null);
  const mandatoryUnordered = unorderedQs.filter(q => q.mandatory);
  const optionalUnordered = shuffle(unorderedQs.filter(q => !q.mandatory));
  const pool = [...orderedQs, ...mandatoryUnordered, ...optionalUnordered];
  return pool.slice(0, TOTAL_QUESTIONS);
}

// ── UI helpers ──
function Logo({ height = 38 }) {
  const [err, setErr] = useState(false);
  if (err) return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <div style={{ width: height * 1.8, height, borderRadius: "8px", background: "linear-gradient(135deg,#6366f1,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: height * 0.42, color: "#fff", letterSpacing: "1px" }}>ISG</div>
    </div>
  );
  return <img src={ISG_LOGO} alt="ISG" style={{ height, objectFit: "contain", filter: "var(--logo-filter)" }} onError={() => setErr(true)} />;
}

function Card({ children, style = {} }) {
  return <div style={{ background: "var(--bg-card)", borderRadius: "18px", padding: "24px", border: "1px solid var(--border-card)", ...style }}>{children}</div>;
}

function Btn({ children, onClick, disabled, variant = "primary", style = {} }) {
  const base = { padding: "11px 18px", borderRadius: "10px", fontSize: "14px", fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.55 : 1, ...style };
  const v = {
    primary: { border: "none", background: "linear-gradient(135deg,#6366f1,#8b5cf6)", color: "#fff" },
    ghost: { border: "1px solid var(--btn-ghost-border)", background: "transparent", color: "var(--btn-ghost-color)" },
    danger: { border: "1px solid #ef444433", background: "transparent", color: "#f87171" },
    warning: { border: "1px solid rgba(249,115,22,0.4)", background: "transparent", color: "#fb923c" },
  };
  return <button onClick={onClick} disabled={disabled} style={{ ...base, ...v[variant] }}>{children}</button>;
}

function Input({ value, onChange, onKeyDown, placeholder, type = "text", style = {} }) {
  return <input value={value} onChange={onChange} onKeyDown={onKeyDown} type={type} placeholder={placeholder}
    style={{ width: "100%", padding: "11px 14px", borderRadius: "10px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "14px", outline: "none", boxSizing: "border-box", ...style }} />;
}

function Tag({ children, bg, color }) {
  return <span style={{ background: bg, color, padding: "3px 9px", borderRadius: "20px", fontSize: "11px", fontWeight: 600 }}>{children}</span>;
}

// ── Status styles for all 12 test statuses ──
const STATUS_STYLES = {
  pending:            { label: "Pending",            color: "#94a3b8", bg: "rgba(148,163,184,0.15)" },
  completed:          { label: "Completed",           color: "#4ade80", bg: "rgba(74,222,128,0.15)"  },
  under_review:       { label: "Under Review",        color: "#60a5fa", bg: "rgba(96,165,250,0.15)"  },
  round1_assigned:    { label: "Round 1 Assigned",    color: "#f59e0b", bg: "rgba(245,158,11,0.15)"  },
  round1_completed:   { label: "Round 1 Done",        color: "#fb923c", bg: "rgba(251,146,60,0.15)"  },
  round2_assigned:    { label: "Round 2 Assigned",    color: "#c084fc", bg: "rgba(192,132,252,0.15)" },
  round2_completed:   { label: "Round 2 Done",        color: "#e879f9", bg: "rgba(232,121,249,0.15)" },
  hr_round_assigned:  { label: "HR Round Assigned",   color: "#38bdf8", bg: "rgba(56,189,248,0.15)"  },
  hr_round_completed: { label: "HR Round Done",       color: "#818cf8", bg: "rgba(129,140,248,0.15)" },
  round3_assigned:    { label: "Round 3 Assigned",    color: "#f97316", bg: "rgba(249,115,22,0.15)"  },
  round3_completed:   { label: "Round 3 Done",        color: "#fb923c", bg: "rgba(251,146,60,0.15)"  },
  shortlisted:        { label: "Shortlisted",         color: "#4ade80", bg: "rgba(74,222,128,0.2)"   },
  on_hold:            { label: "On Hold",              color: "#fbbf24", bg: "rgba(251,191,36,0.2)"   },
  rejected:           { label: "Rejected",             color: "#f87171", bg: "rgba(248,113,113,0.2)"  },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || { label: status || "Unknown", color: "#94a3b8", bg: "rgba(148,163,184,0.15)" };
  return (
    <span style={{ background: s.bg, color: s.color, padding: "2px 9px", borderRadius: "20px", fontSize: "11px", fontWeight: 700, whiteSpace: "nowrap" }}>
      {s.label}
    </span>
  );
}

// ── Status styles for job requisition statuses ──
const REQ_STATUS_STYLES = {
  new:                 { label: "New",                   color: "#94a3b8", bg: "rgba(148,163,184,0.15)" },
  approved_hod:        { label: "Approved - HOD",        color: "#f59e0b", bg: "rgba(245,158,11,0.15)"  },
  rejected_hod:        { label: "Rejected by HOD",       color: "#f87171", bg: "rgba(248,113,113,0.15)" },
  rejected_executive:  { label: "Rejected by Executive", color: "#f87171", bg: "rgba(248,113,113,0.15)" },
  pending_hr_action:   { label: "Pending HR Action",     color: "#818cf8", bg: "rgba(129,140,248,0.15)" },
  under_screening:     { label: "Under Screening",       color: "#38bdf8", bg: "rgba(56,189,248,0.15)"  },
  closed:              { label: "Closed",                color: "#4ade80", bg: "rgba(74,222,128,0.15)"  },
};
function ReqStatusBadge({ status }) {
  const s = REQ_STATUS_STYLES[status] || { label: status || "Unknown", color: "#94a3b8", bg: "rgba(148,163,184,0.15)" };
  return (
    <span style={{ background: s.bg, color: s.color, padding: "2px 9px", borderRadius: "20px", fontSize: "11px", fontWeight: 700, whiteSpace: "nowrap" }}>
      {s.label}
    </span>
  );
}

function Pagination({ page, totalPages, pageSize, onPage, onPageSize, pageSizeOptions = [10, 20, 50, 100] }) {
  if (totalPages <= 1 && pageSize === pageSizeOptions[0] && totalPages === 0) return null;
  const btnBase = { padding: "5px 10px", borderRadius: "6px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "12px", cursor: "pointer" };
  const pages = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (page > 3) pages.push("...");
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push("...");
    pages.push(totalPages);
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "space-between", marginTop: "14px", flexWrap: "wrap" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "4px", flexWrap: "wrap" }}>
        <button style={{ ...btnBase, opacity: page === 1 ? 0.4 : 1 }} disabled={page === 1} onClick={() => onPage(page - 1)}>‹ Prev</button>
        {pages.map((p, i) => p === "..." ? (
          <span key={`ellipsis-${i}`} style={{ color: "var(--text-muted)", padding: "0 4px", fontSize: "12px" }}>…</span>
        ) : (
          <button key={p} style={{ ...btnBase, background: p === page ? "rgba(129,140,248,0.25)" : "var(--bg-input)", color: p === page ? "#818cf8" : "var(--text-input)", borderColor: p === page ? "rgba(129,140,248,0.5)" : "var(--border-input)", fontWeight: p === page ? 700 : 400 }} onClick={() => onPage(p)}>{p}</button>
        ))}
        <button style={{ ...btnBase, opacity: page === totalPages ? 0.4 : 1 }} disabled={page === totalPages} onClick={() => onPage(page + 1)}>Next ›</button>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>Per page:</span>
        <select value={pageSize} onChange={e => { onPageSize(Number(e.target.value)); onPage(1); }}
          style={{ ...btnBase, padding: "4px 8px" }}>
          {pageSizeOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
    </div>
  );
}

function ProgressBar({ value, total }) {
  return (
    <div style={{ background: "var(--bg-progress)", borderRadius: "6px", height: "6px", overflow: "hidden", marginBottom: "14px" }}>
      <div style={{ height: "100%", width: `${Math.min(100, (value / total) * 100)}%`, background: "linear-gradient(90deg,#6366f1,#8b5cf6)", borderRadius: "6px", transition: "width 0.4s" }} />
    </div>
  );
}

function PageWrap({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-page)", padding: "24px", fontFamily: "'Segoe UI',sans-serif" }}>
      <div style={{ maxWidth: "720px", margin: "0 auto" }}>{children}</div>
    </div>
  );
}

function NavTabs({ active, onChange, role }) {
  if (role === "executive") return (
    <div style={{ display: "flex", gap: "8px", marginBottom: "24px", flexWrap: "wrap" }}>
      <button onClick={() => onChange("requisitions")}
        style={{ display: "flex", alignItems: "center", gap: "6px", padding: "9px 18px", borderRadius: "10px", border: `1px solid ${active === "requisitions" ? "var(--nav-active-border)" : "var(--nav-inactive-border)"}`, background: active === "requisitions" ? "var(--nav-active-bg)" : "transparent", color: active === "requisitions" ? "var(--nav-active-color)" : "var(--nav-inactive-color)", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
        <span>📝</span>Requisitions
      </button>
    </div>
  );
  const canCreate = ["hr", "hr-admin", "superuser"].includes(role);
  const canManageUsers = ["hr-admin", "superuser"].includes(role);
  const canManageQBanks = ["hr", "hr-admin", "superuser", "hiring-manager"].includes(role);
  const canSeeRequisitions = ["hiring-manager", "hr", "hr-admin", "superuser", "hod"].includes(role);
  const canSeeReports = role !== "hod";
  const tabs = [["dashboard", "🏠", "Dashboard"]];
  if (canCreate) tabs.push(["create", "➕", "Create Test"]);
  if (canSeeReports) tabs.push(["reports", "📋", "Reports"]);
  if (canCreate) tabs.push(["events", "📅", "Events"]);
  if (canSeeReports) tabs.push(["history", "📁", "History"]);
  if (canManageQBanks) tabs.push(["qbanks", "📚", "Question Banks"]);
  if (canSeeRequisitions) tabs.push(["requisitions", "📝", "Requisitions"]);
  if (canManageUsers) tabs.push(["users", "👥", "Users"]);
  return (
    <div style={{ display: "flex", gap: "8px", marginBottom: "24px", flexWrap: "wrap" }}>
      {tabs.map(([k, icon, label]) => (
        <button key={k} onClick={() => onChange(k)}
          style={{ display: "flex", alignItems: "center", gap: "6px", padding: "9px 18px", borderRadius: "10px", border: `1px solid ${active === k ? "var(--nav-active-border)" : "var(--nav-inactive-border)"}`, background: active === k ? "var(--nav-active-bg)" : "transparent", color: active === k ? "var(--nav-active-color)" : "var(--nav-inactive-color)", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
          <span>{icon}</span>{label}
        </button>
      ))}
    </div>
  );
}

function APIStatus() {
  const [status, setStatus] = useState("checking");
  useEffect(() => {
    fetch(`${API}/health`)
      .then(r => r.json())
      .then(d => setStatus(d.status === "ok" ? "ok" : "error"))
      .catch(() => setStatus("error"));
  }, []);
  if (status === "ok") return null;
  return (
    <div style={{ background: "#ef444422", border: "1px solid #ef444433", borderRadius: "10px", padding: "10px 14px", marginBottom: "16px", fontSize: "13px", color: "#f87171" }}>
      ⚠️ <strong>Backend API not reachable.</strong> Please start the Express server on port 3001. Data will still save locally as fallback.
    </div>
  );
}

// ── LOGIN PAGE (2FA: email + password → OTP) ──
function LoginPage({ onLogin }) {
  const [step, setStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [pendingToken, setPendingToken] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  async function handleCredentials() {
    if (!email.trim() || !password) { setErr("Email and password required."); return; }
    setLoading(true); setErr("");
    try {
      const hashedPassword = await sha512(password);
      const res = await fetch(`${API}/api/auth/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: email.trim().toLowerCase(), password: hashedPassword }) });
      const data = await res.json();
      if (!res.ok) { setErr(data.error || "Login failed."); return; }
      setMaskedEmail(data.maskedEmail); setStep("otp");
    } catch { setErr("Unable to reach server."); } finally { setLoading(false); }
  }

  async function handleOTP() {
    if (otp.length !== 6) { setErr("Enter the 6-digit OTP."); return; }
    setLoading(true); setErr("");
    try {
      const hashedOtp = await sha512(otp);
      const res = await fetch(`${API}/api/auth/verify-otp`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: email.trim().toLowerCase(), otp: hashedOtp }) });
      const data = await res.json();
      if (!res.ok) { setErr(data.error || "Verification failed."); return; }
      localStorage.setItem("authToken", data.token);
      localStorage.setItem("authUser", JSON.stringify(data.user));
      if (data.user.isTempPassword) { setPendingToken(data.token); setStep("change-pw"); }
      else { onLogin(data.user); }
    } catch { setErr("Unable to reach server."); } finally { setLoading(false); }
  }

  async function handleChangePassword() {
    if (!password || !newPassword || !confirmPassword) { setErr("All fields required."); return; }
    if (newPassword !== confirmPassword) { setErr("Passwords do not match."); return; }
    if (newPassword.length < 8) { setErr("Password must be at least 8 characters."); return; }
    setLoading(true); setErr("");
    try {
      const [hashedCurrent, hashedNew] = await Promise.all([sha512(password), sha512(newPassword)]);
      const res = await fetch(`${API}/api/auth/change-password`, { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${pendingToken}` }, body: JSON.stringify({ currentPassword: hashedCurrent, newPassword: hashedNew }) });
      const data = await res.json();
      if (!res.ok) { setErr(data.error || "Failed to change password."); return; }
      localStorage.setItem("authToken", data.token);
      localStorage.setItem("authUser", JSON.stringify(data.user));
      onLogin(data.user);
    } catch { setErr("Unable to reach server."); } finally { setLoading(false); }
  }

  async function handleForgot() {
    if (!forgotEmail.trim()) { setErr("Enter your email."); return; }
    setLoading(true); setErr("");
    try {
      await fetch(`${API}/api/auth/forgot-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: forgotEmail.trim().toLowerCase() }) });
      setSuccessMsg("If your email is registered, an OTP has been sent."); setStep("forgot-otp");
    } catch { setErr("Unable to reach server."); } finally { setLoading(false); }
  }

  async function handleResetPassword() {
    if (!otp || !newPassword || !confirmPassword) { setErr("All fields required."); return; }
    if (newPassword !== confirmPassword) { setErr("Passwords do not match."); return; }
    if (newPassword.length < 8) { setErr("Password must be at least 8 characters."); return; }
    setLoading(true); setErr("");
    try {
      const [hashedOtp, hashedNew] = await Promise.all([sha512(otp), sha512(newPassword)]);
      const res = await fetch(`${API}/api/auth/reset-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: forgotEmail.trim().toLowerCase(), otp: hashedOtp, newPassword: hashedNew }) });
      const data = await res.json();
      if (!res.ok) { setErr(data.error || "Reset failed."); return; }
      setStep("credentials"); setSuccessMsg("Password reset. Please login."); setOtp(""); setNewPassword(""); setConfirmPassword("");
    } catch { setErr("Unable to reach server."); } finally { setLoading(false); }
  }

  const inputStyle = { marginBottom: "12px" };
  const labelStyle = { color: "var(--text-muted)", fontSize: "12px", margin: "0 0 5px" };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-page)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px", fontFamily: "'Segoe UI',sans-serif" }}>
      <div style={{ maxWidth: "380px", width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <Logo height={44} />
          <p style={{ color: "var(--text-muted)", marginTop: "10px", fontSize: "14px" }}>Employee Portal — Staff Access Only</p>
        </div>

        {step === "credentials" && (
          <Card>
            <p style={{ color: "var(--text-secondary)", fontSize: "15px", fontWeight: 700, margin: "0 0 16px" }}>🔐 Sign In</p>
            {successMsg && <p style={{ color: "#4ade80", fontSize: "13px", marginBottom: "12px" }}>✅ {successMsg}</p>}
            <p style={labelStyle}>Email</p>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@isgdigitalfirst.com" style={inputStyle} onKeyDown={e => e.key === "Enter" && handleCredentials()} />
            <p style={labelStyle}>Password</p>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" style={{ marginBottom: "6px" }} onKeyDown={e => e.key === "Enter" && handleCredentials()} />
            <div style={{ textAlign: "right", marginBottom: "14px" }}>
              <button onClick={() => { setStep("forgot"); setErr(""); setSuccessMsg(""); }} style={{ background: "none", border: "none", color: "var(--nav-active-color)", fontSize: "12px", cursor: "pointer" }}>Forgot password?</button>
            </div>
            {err && <p style={{ color: "#f87171", fontSize: "13px", marginBottom: "10px" }}>{err}</p>}
            <Btn style={{ width: "100%" }} onClick={handleCredentials} disabled={loading}>{loading ? "Signing in..." : "Continue →"}</Btn>
          </Card>
        )}

        {step === "otp" && (
          <Card>
            <p style={{ color: "var(--text-secondary)", fontSize: "15px", fontWeight: 700, margin: "0 0 8px" }}>📱 Two-Factor Authentication</p>
            <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 16px" }}>A 6-digit OTP was sent to <strong style={{ color: "var(--nav-active-color)" }}>{maskedEmail}</strong>. Valid for 5 minutes.</p>
            <Input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit OTP" style={{ marginBottom: "14px", textAlign: "center", letterSpacing: "10px", fontSize: "22px", fontWeight: 700 }} onKeyDown={e => e.key === "Enter" && handleOTP()} />
            {err && <p style={{ color: "#f87171", fontSize: "13px", marginBottom: "10px" }}>{err}</p>}
            <Btn style={{ width: "100%", marginBottom: "8px" }} onClick={handleOTP} disabled={loading || otp.length !== 6}>{loading ? "Verifying..." : "Verify OTP →"}</Btn>
            <Btn variant="ghost" style={{ width: "100%", fontSize: "12px" }} onClick={() => { setStep("credentials"); setOtp(""); setErr(""); }}>← Back</Btn>
          </Card>
        )}

        {step === "change-pw" && (
          <Card>
            <p style={{ color: "#fbbf24", fontSize: "14px", fontWeight: 700, margin: "0 0 6px" }}>🔑 Set Your Password</p>
            <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 16px" }}>Your account uses a temporary password. Please set a permanent one to continue.</p>
            <p style={labelStyle}>Current (Temporary) Password</p>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Temporary password" style={inputStyle} />
            <p style={labelStyle}>New Password <span style={{ color: "var(--text-faint)" }}>(min 8 chars)</span></p>
            <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New password" style={inputStyle} />
            <p style={labelStyle}>Confirm New Password</p>
            <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm password" style={{ marginBottom: "14px" }} onKeyDown={e => e.key === "Enter" && handleChangePassword()} />
            {err && <p style={{ color: "#f87171", fontSize: "13px", marginBottom: "10px" }}>{err}</p>}
            <Btn style={{ width: "100%" }} onClick={handleChangePassword} disabled={loading}>{loading ? "Saving..." : "Set Password & Continue →"}</Btn>
          </Card>
        )}

        {step === "forgot" && (
          <Card>
            <p style={{ color: "var(--text-secondary)", fontSize: "15px", fontWeight: 700, margin: "0 0 8px" }}>🔑 Forgot Password</p>
            <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 14px" }}>Enter your email to receive a reset OTP.</p>
            <Input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="your@isgdigitalfirst.com" style={inputStyle} onKeyDown={e => e.key === "Enter" && handleForgot()} />
            {err && <p style={{ color: "#f87171", fontSize: "13px", marginBottom: "10px" }}>{err}</p>}
            <Btn style={{ width: "100%", marginBottom: "8px" }} onClick={handleForgot} disabled={loading}>{loading ? "Sending..." : "Send Reset OTP →"}</Btn>
            <Btn variant="ghost" style={{ width: "100%", fontSize: "12px" }} onClick={() => { setStep("credentials"); setErr(""); }}>← Back to Login</Btn>
          </Card>
        )}

        {step === "forgot-otp" && (
          <Card>
            <p style={{ color: "var(--text-secondary)", fontSize: "15px", fontWeight: 700, margin: "0 0 8px" }}>🔑 Reset Password</p>
            {successMsg && <p style={{ color: "#4ade80", fontSize: "13px", marginBottom: "10px" }}>✅ {successMsg}</p>}
            <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 14px" }}>OTP sent to <strong style={{ color: "var(--nav-active-color)" }}>{forgotEmail}</strong>.</p>
            <p style={labelStyle}>OTP</p>
            <Input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit OTP" style={{ marginBottom: "12px", textAlign: "center", letterSpacing: "10px", fontSize: "20px" }} />
            <p style={labelStyle}>New Password</p>
            <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 8 characters" style={inputStyle} />
            <p style={labelStyle}>Confirm Password</p>
            <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm" style={{ marginBottom: "14px" }} />
            {err && <p style={{ color: "#f87171", fontSize: "13px", marginBottom: "10px" }}>{err}</p>}
            <Btn style={{ width: "100%", marginBottom: "8px" }} onClick={handleResetPassword} disabled={loading}>{loading ? "Resetting..." : "Reset Password →"}</Btn>
            <Btn variant="ghost" style={{ width: "100%", fontSize: "12px" }} onClick={() => { setStep("credentials"); setErr(""); setOtp(""); setNewPassword(""); setConfirmPassword(""); }}>← Back to Login</Btn>
          </Card>
        )}
      </div>
    </div>
  );
}

// ── REPORT DETAIL ──
function ReportDetail({ report, onBack, onDelete, canDelete = true, hideReEvaluate = false, testStatus, currentUser, test, onTestStatusChange, allEvents = [] }) {
  const [localQuestions, setLocalQuestions] = useState(report.questions || []);
  const [reEval, setReEval] = useState({ running: false, done: 0, total: 0, error: null, success: false });
  const [rounds, setRounds] = useState([]);
  const [cvMeta, setCvMeta] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showVerdictModal, setShowVerdictModal] = useState(false);
  const [roundsLoading, setRoundsLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeConfirm, setFinalizeConfirm] = useState(null); // { status } when confirm panel shown
  const [jrf, setJrf] = useState(null);
  const [showJrfModal, setShowJrfModal] = useState(false);

  useEffect(() => {
    if (!test?.jobRequisitionId) return;
    apiGet(`/api/job-requisitions/${test.jobRequisitionId}`)
      .then(d => setJrf(d))
      .catch(() => {});
  }, [test?.jobRequisitionId]);

  function loadRounds() {
    if (!report?.code) return;
    setRoundsLoading(true);
    apiGet(`/api/rounds?code=${report.code}`)
      .then(d => { if (d) { setRounds(d.rounds || []); setCvMeta(d.cvMeta || null); } })
      .catch(() => {})
      .finally(() => setRoundsLoading(false));
  }

  useEffect(() => { loadRounds(); }, [report?.code]);

  const pendingRound = rounds.find(r => r.assignedTo?.id === currentUser?.id && r.status === 'pending');
  const isHrRole = currentUser && ['hr', 'hr-admin', 'superuser'].includes(currentUser.role);
  const hasCompletedRound2 = rounds.some(r => r.roundNumber === 2 && r.status === 'completed');
  const hasCompletedRound3 = rounds.some(r => r.roundNumber === 3 && r.status === 'completed');
  const canAssignRound = currentUser && ['hr', 'hr-admin', 'hiring-manager', 'superuser'].includes(currentUser.role)
    && (
      ['completed', 'under_review', 'round1_completed', 'round2_completed', 'round3_completed'].includes(testStatus)
      || (isHrRole && testStatus === 'hr_round_assigned' && !(hasCompletedRound2 && hasCompletedRound3))
    );
  const canFinalize = currentUser && ['hr', 'hr-admin', 'superuser'].includes(currentUser.role)
    && testStatus === 'hr_round_completed';

  function getNextRoundLabel() {
    if (['completed', 'under_review'].includes(testStatus)) return 'Round 1';
    if (testStatus === 'round1_completed') return 'Next Round';
    if (testStatus === 'round2_completed') return 'Next Round';
    if (testStatus === 'round3_completed') return 'HR Round';
    return 'Round';
  }

  async function handleFinalize(newStatus) {
    setFinalizeConfirm({ status: newStatus });
  }

  async function confirmFinalize() {
    const { status: newStatus } = finalizeConfirm;
    setFinalizeConfirm(null);
    setFinalizing(true);
    try {
      await apiPost(`/api/tests/${report.code}/finalize`, { status: newStatus });
      if (onTestStatusChange) onTestStatusChange(report.code, newStatus);
    } catch (e) {
      if (shouldShowError(e)) alert('Failed to update status: ' + e.message);
    }
    setFinalizing(false);
  }

  async function downloadCV() {
    try {
      const token = getAuthToken();
      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const r = await fetch(`${API}/api/tests/${report.code}/cv`, { headers });
      if (!r.ok) throw new Error('Failed to download CV');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = cvMeta?.fileName || 'cv';
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) { if (shouldShowError(e)) alert('CV download failed: ' + e.message); }
  }

  async function reEvaluate() {
    setReEval({ running: true, done: 0, total: localQuestions.length, error: null, success: false });
    try {
      const r = await apiPost('/api/ai/re-evaluate', { code: report.code });
      const updated = r.questions;
      setLocalQuestions(updated);
      try {
        await apiPost('/api/reports', { ...report, questions: updated });
        setReEval({ running: false, done: updated.length, total: updated.length, error: null, success: true });
      } catch (e) {
        setReEval({ running: false, done: 0, total: 0, error: 'Saved locally but failed to persist. Try again.', success: false });
      }
    } catch (e) {
      if (shouldShowError(e)) setReEval({ running: false, done: 0, total: 0, error: 'Re-evaluation failed. Try again.', success: false });
    }
  }

  const avg = localQuestions.length > 0
    ? (localQuestions.reduce((a, q) => a + q.score, 0) / localQuestions.length).toFixed(1) : 0;
  const mandatoryQs = localQuestions.filter(q => q.mandatory);
  const mandatoryAvg = mandatoryQs.length > 0
    ? (mandatoryQs.reduce((a, q) => a + q.score, 0) / mandatoryQs.length).toFixed(1) : null;
  const mandatoryPassed = mandatoryQs.filter(q => q.score >= 7).length;
  const rec = avg >= 7
    ? { label: "✅ Shortlist", color: "#4ade80", bg: "#22c55e22" }
    : avg >= 5
      ? { label: "⏸ Hold", color: "#fbbf24", bg: "#f59e0b22" }
      : { label: "❌ Reject", color: "#f87171", bg: "#ef444422" };
  function downloadReport() {
    const wb = XLSX.utils.book_new();
    const recText = rec.label.replace(/[✅⏸❌]\s*/g, "");
    // Summary sheet
    const summarySheet = XLSX.utils.aoa_to_sheet([
      ["Field", "Value"],
      ["Candidate", report.candidateName],
      ["Event Name", report.eventName || "—"],
      ["Hiring Manager", report.hiringManager?.name || "—"],
      ["Track", `${report.trackIcon || ""} ${report.trackName || ""}`.trim()],
      ["Experience", report.expLabel || ""],
      ["Complexity", report.complexity || ""],
      ["Code", report.code],
      ["Overall Score", `${avg}/10`],
      ["Result", recText],
      ...(mandatoryAvg !== null ? [["Mandatory Score", `${mandatoryAvg}/10 (${mandatoryPassed}/${mandatoryQs.length} passed)`]] : []),
      ["Test Created", report.createdAt ? new Date(report.createdAt).toLocaleString() : ""],
      ["Completed", report.completedAt ? new Date(report.completedAt).toLocaleString() : "Pending"],
      ["Time Used", fmt(report.timeUsed || 0)],
    ]);
    XLSX.utils.book_append_sheet(wb, summarySheet, "Summary");
    // Questions sheet
    const qRows = [["#", "Mandatory", "Type", "Topic", "Question", "Candidate Answer", "Score", "Strengths", "Gaps", "Model Answer", "Skipped"]];
    localQuestions.forEach((q, i) => {
      qRows.push([i + 1, q.mandatory ? "Yes" : "No", q.type || "", q.topic || "", q.question || "", q.candidateAnswer || "(No answer)", `${q.score}/10`, q.strengths || "", q.gaps || "", q.modelAnswer || "", q.skipped ? "Yes" : "No"]);
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(qRows), "Questions");
    // Rounds sheet
    if (rounds.length > 0) {
      const finalVerdictForRounds = ['shortlisted', 'on_hold', 'rejected'].includes(testStatus) ? STATUS_STYLES[testStatus]?.label : "—";
      const rRows = [["Round", "Assigned To", "Assigned By", "Assigned At", "Status", "Verdict", "Recommend Round 2", "Feedback", "Submitted At", "Final Verdict"]];
      rounds.forEach(r => {
        rRows.push([
          r.roundLabel || String(r.roundNumber),
          r.assignedTo?.name || "—",
          r.assignedBy?.name || "—",
          r.assignedAt ? new Date(r.assignedAt).toLocaleString() : "—",
          r.status === 'completed' ? "Completed" : "Pending",
          r.verdict ? r.verdict.charAt(0).toUpperCase() + r.verdict.slice(1) : "—",
          r.roundNumber === 1 ? (r.recommendRound2 ? "Yes" : "No") : "N/A",
          r.feedback || "—",
          r.submittedAt ? new Date(r.submittedAt).toLocaleString() : "—",
          finalVerdictForRounds,
        ]);
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rRows), "Rounds");
    }
    XLSX.writeFile(wb, `report_${report.code || "candidate"}.xlsx`);
  }
  return (
    <PageWrap>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px", flexWrap: "wrap", gap: "10px" }}>
        <Logo />
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <Btn variant="ghost" onClick={onBack}>← Back</Btn>
          <Btn variant="ghost" onClick={downloadReport}>📥 Download XLSX</Btn>
          {!hideReEvaluate && !report.skipOnlineTest && (
            <Btn
              onClick={reEvaluate}
              disabled={reEval.running}
              style={{ background: reEval.running ? "rgba(99,102,241,0.3)" : "rgba(99,102,241,0.2)", border: "1px solid rgba(99,102,241,0.4)", color: "#a5b4fc" }}
            >
              {reEval.running ? `⏳ Evaluating ${reEval.done}/${reEval.total}…` : "🔄 Re-evaluate"}
            </Btn>
          )}
          {cvMeta && (
            <Btn variant="ghost" onClick={downloadCV} style={{ color: "#38bdf8", borderColor: "rgba(56,189,248,0.3)" }}>
              ⬇ CV ({cvMeta.fileName?.split('.').pop()?.toUpperCase()})
            </Btn>
          )}
          {canAssignRound && (
            <Btn onClick={() => setShowAssignModal(true)} style={{ background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.4)", color: "#f59e0b" }}>
              {testStatus === 'hr_round_assigned' ? '🔄 Push Back for Technical Round' : `👤 Assign ${getNextRoundLabel()}`}
            </Btn>
          )}
          {pendingRound && (
            <Btn onClick={() => setShowVerdictModal(true)} style={{ background: "rgba(129,140,248,0.15)", border: "1px solid rgba(129,140,248,0.4)", color: "#818cf8" }}>
              ✍ Submit Verdict
            </Btn>
          )}
        </div>
      </div>
      {showAssignModal && test && (
        <AssignRoundModal
          test={{ ...test, status: testStatus }}
          rounds={rounds}
          currentUser={currentUser}
          allEvents={allEvents}
          onClose={() => setShowAssignModal(false)}
          onAssigned={() => { loadRounds(); if (onTestStatusChange) onTestStatusChange(report.code, null); }}
        />
      )}
      {showVerdictModal && pendingRound && (
        <RoundVerdictModal
          round={pendingRound}
          test={test}
          allEvents={allEvents}
          onClose={() => setShowVerdictModal(false)}
          onSubmitted={() => { loadRounds(); if (onTestStatusChange) onTestStatusChange(report.code, null); }}
        />
      )}
      {reEval.success && (
        <div style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)", borderRadius: "10px", padding: "10px 14px", marginBottom: "14px", color: "#4ade80", fontSize: "13px" }}>
          ✅ Re-evaluation complete. Scores and feedback updated.
        </div>
      )}
      {reEval.error && (
        <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: "10px", padding: "10px 14px", marginBottom: "14px", color: "#f87171", fontSize: "13px" }}>
          ⚠️ {reEval.error}
        </div>
      )}
      {test?.skipOnlineTest && (
        <div style={{ background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.4)", borderRadius: "10px", padding: "10px 16px", marginBottom: "14px", display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "18px" }}>🚫</span>
          <div>
            <span style={{ color: "#f87171", fontWeight: 700, fontSize: "13px" }}>Online Test Skipped</span>
            <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "2px 0 0" }}>This candidate was moved directly to the interview rounds without completing the AI online test.</p>
          </div>
        </div>
      )}
      <Card style={{ marginBottom: "14px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginBottom: "5px" }}>
              <h2 style={{ color: "var(--text-primary)", margin: 0, fontSize: "20px" }}>👤 {report.candidateName}</h2>
              {testStatus && <StatusBadge status={testStatus} />}
            </div>
            <p style={{ color: "var(--text-muted)", margin: "0 0 3px", fontSize: "13px" }}>{report.trackIcon} {report.trackName} · {report.expLabel} · <span style={{ textTransform: "capitalize" }}>{report.complexity}</span></p>
            <p style={{ color: "var(--text-faint)", margin: "0 0 8px", fontSize: "12px" }}>⏱ Time used: {fmt(report.timeUsed || 0)}</p>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <div style={{ background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.25)", borderRadius: "8px", padding: "6px 12px" }}>
                <p style={{ color: "#818cf8", fontSize: "10px", fontWeight: 700, margin: "0 0 2px", textTransform: "uppercase" }}>🗓 Test Created</p>
                <p style={{ color: "var(--text-secondary)", fontSize: "12px", margin: 0 }}>{fmtDate(report.createdAt)}</p>
              </div>
              <div style={{ background: report.completedAt ? "rgba(34,197,94,0.1)" : "rgba(251,191,36,0.1)", border: report.completedAt ? "1px solid rgba(34,197,94,0.2)" : "1px solid rgba(251,191,36,0.2)", borderRadius: "8px", padding: "6px 12px" }}>
                <p style={{ color: report.completedAt ? "#4ade80" : "#fbbf24", fontSize: "10px", fontWeight: 700, margin: "0 0 2px", textTransform: "uppercase" }}>
                  {report.completedAt ? '✅ Completed' : '⏳ In progress'}
                </p>
                <p style={{ color: "var(--text-secondary)", fontSize: "12px", margin: 0 }}>{report.completedAt || "—"}</p>
              </div>
              {report.usedQuestionBank && (
                <div style={{ background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.2)", borderRadius: "8px", padding: "6px 12px" }}>
                  <p style={{ color: "#fbbf24", fontSize: "10px", fontWeight: 700, margin: "0 0 2px", textTransform: "uppercase" }}>📂 Source</p>
                  <p style={{ color: "var(--text-secondary)", fontSize: "12px", margin: 0 }}>Question Bank</p>
                </div>
              )}
            </div>
            <p style={{ color: "var(--text-faint)", margin: "8px 0 0", fontSize: "11px" }}>Code: <span style={{ color: "#818cf8", fontFamily: "monospace" }}>{report.code}</span></p>
            {(report.eventName || report.hiringManager?.name || jrf?.jrfNumber) && (
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "6px" }}>
                {report.eventName && <span style={{ background: "rgba(129,140,248,0.12)", color: "#a5b4fc", padding: "2px 9px", borderRadius: "20px", fontSize: "11px" }}>📅 {report.eventName}</span>}
                {report.hiringManager?.name && <span style={{ background: "rgba(251,191,36,0.1)", color: "#fbbf24", padding: "2px 9px", borderRadius: "20px", fontSize: "11px" }}>👤 {report.hiringManager.name}</span>}
                {jrf?.jrfNumber && (
                  <span onClick={() => setShowJrfModal(true)} style={{ background: "rgba(129,140,248,0.18)", color: "#818cf8", padding: "2px 9px", borderRadius: "20px", fontSize: "11px", cursor: "pointer", fontFamily: "monospace", border: "1px solid rgba(129,140,248,0.35)", textDecoration: "underline dotted" }} title="View JRF details">
                    📝 {jrf.jrfNumber}
                  </span>
                )}
              </div>
            )}
          </div>
          <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: "8px" }}>
            {!report.skipOnlineTest && (
            <div>
              <div style={{ color: "var(--text-muted)", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", marginBottom: "3px" }}>Overall Score</div>
              <div style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)", borderRadius: "50px", padding: "8px 22px", color: "#fff", fontWeight: 800, fontSize: "24px" }}>{avg}/10</div>
              <div style={{ marginTop: "6px", background: rec.bg, color: rec.color, borderRadius: "20px", padding: "5px 14px", fontWeight: 700, fontSize: "13px" }}>{rec.label}</div>
            </div>
            )}
            {mandatoryAvg !== null && (
              <div style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.25)", borderRadius: "10px", padding: "8px 14px" }}>
                <div style={{ color: "#fbbf24", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", marginBottom: "3px" }}>⚑ Mandatory Score</div>
                <div style={{ color: "var(--text-primary)", fontWeight: 800, fontSize: "20px" }}>{mandatoryAvg}/10</div>
                <div style={{ color: "var(--text-muted)", fontSize: "11px", marginTop: "2px" }}>{mandatoryPassed}/{mandatoryQs.length} passed (≥7)</div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {canFinalize && !finalizeConfirm && (
        <div style={{ display: "flex", gap: "10px", marginBottom: "16px", padding: "14px 16px", background: "rgba(129,140,248,0.08)", border: "1px solid rgba(129,140,248,0.2)", borderRadius: "12px", alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ color: "var(--text-muted)", fontSize: "13px", fontWeight: 600, flex: 1 }}>🏁 HR Round complete — finalize candidate status:</span>
          <Btn onClick={() => handleFinalize('shortlisted')} disabled={finalizing} style={{ background: "rgba(74,222,128,0.15)", border: "1px solid rgba(74,222,128,0.4)", color: "#4ade80", padding: "8px 18px" }}>✅ Shortlist</Btn>
          <Btn onClick={() => handleFinalize('on_hold')} disabled={finalizing} style={{ background: "rgba(251,191,36,0.15)", border: "1px solid rgba(251,191,36,0.4)", color: "#fbbf24", padding: "8px 18px" }}>⏸ On Hold</Btn>
          <Btn onClick={() => handleFinalize('rejected')} disabled={finalizing} style={{ background: "rgba(248,113,113,0.15)", border: "1px solid rgba(248,113,113,0.4)", color: "#f87171", padding: "8px 18px" }}>❌ Reject</Btn>
        </div>
      )}
      {finalizeConfirm && (
        <div style={{ display: "flex", gap: "10px", marginBottom: "16px", padding: "14px 16px", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: "12px", alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ color: "var(--text-primary)", fontSize: "13px", fontWeight: 600, flex: 1 }}>
            Confirm: set status to <strong style={{ color: STATUS_STYLES[finalizeConfirm.status]?.color }}>{STATUS_STYLES[finalizeConfirm.status]?.label}</strong>?
          </span>
          <Btn onClick={confirmFinalize} style={{ background: "rgba(74,222,128,0.2)", border: "1px solid rgba(74,222,128,0.5)", color: "#4ade80", padding: "8px 18px" }}>Yes, confirm</Btn>
          <Btn onClick={() => setFinalizeConfirm(null)} variant="ghost" style={{ padding: "8px 14px" }}>Cancel</Btn>
        </div>
      )}

      <RoundsHistory rounds={rounds} loading={roundsLoading} currentUser={currentUser} test={test} allEvents={allEvents} onReassigned={() => { loadRounds(); if (onTestStatusChange) onTestStatusChange(report.code, null); }} onRefresh={loadRounds} />

      {localQuestions.map((q, i) => {
        const ts = typeStyle(q.type);
        return (
          <div key={i} style={{ background: "var(--bg-card-inner)", borderRadius: "14px", padding: "16px", border: "1px solid var(--border-card)", marginBottom: "10px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "8px", marginBottom: "10px" }}>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                <Tag bg="#ffffff10" color="#94a3b8">Q{i + 1}</Tag>
                <Tag bg={ts.bg} color={ts.color}>{ts.label}</Tag>
                <Tag bg="#ffffff08" color="#64748b">{q.topic}</Tag>
                {q.skipped && <Tag bg="#f9731622" color="#fb923c">⏭ Skipped</Tag>}
              </div>
              <span style={{ background: scoreBg(q.score), color: scoreColor(q.score), padding: "3px 12px", borderRadius: "20px", fontWeight: 700, fontSize: "13px" }}>{q.score}/10</span>
            </div>
            {(q.startedAt || q.submittedAt) && (
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "10px" }}>
                {q.startedAt && (
                  <div style={{ background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.18)", borderRadius: "8px", padding: "5px 10px" }}>
                    <span style={{ color: "#64748b", fontSize: "10px", fontWeight: 700, textTransform: "uppercase" }}>🕐 Started </span>
                    <span style={{ color: "#a5b4fc", fontSize: "11px" }}>{fmtDate(q.startedAt)}</span>
                  </div>
                )}
                {q.submittedAt && (
                  <div style={{ background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.18)", borderRadius: "8px", padding: "5px 10px" }}>
                    <span style={{ color: "#64748b", fontSize: "10px", fontWeight: 700, textTransform: "uppercase" }}>✅ Submitted </span>
                    <span style={{ color: "#a5b4fc", fontSize: "11px" }}>{fmtDate(q.submittedAt)}</span>
                  </div>
                )}
                {q.durationSecs != null && (
                  <div style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)", borderRadius: "8px", padding: "5px 10px" }}>
                    <span style={{ color: "#64748b", fontSize: "10px", fontWeight: 700, textTransform: "uppercase" }}>⏱ Duration </span>
                    <span style={{ color: "#6ee7b7", fontSize: "11px", fontWeight: 600 }}>{fmt(q.durationSecs)}</span>
                  </div>
                )}
              </div>
            )}
            {(() => {
              const qtext = q.question || "";
              const wantMono = /[│├┤┬┴┼─┌┐└┘║╔╗╚╝╠╣╦╩╬]|<[-\s]+>|-{3,}|\|/.test(qtext);
              return (
                <p style={{ color: "var(--text-secondary)", fontSize: "14px", fontWeight: 500, margin: "0 0 10px", lineHeight: 1.6, whiteSpace: "pre-wrap", wordWrap: "break-word", overflowWrap: "break-word", fontFamily: wantMono ? "monospace" : undefined }}>
                  {qtext.split('\n').map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </p>
              );
            })()}
            <div style={{ background: "var(--bg-card-inner)", borderRadius: "10px", padding: "10px 12px", marginBottom: "10px", border: "1px solid var(--border-card)" }}>
              <p style={{ color: "var(--text-muted)", fontSize: "11px", margin: "0 0 4px", fontWeight: 600 }}>CANDIDATE'S ANSWER</p>
              <p style={{ color: "var(--text-secondary)", fontSize: "13px", margin: 0, lineHeight: 1.6, fontFamily: q.type === "programming" ? "monospace" : "inherit", whiteSpace: "pre-wrap" }}>{q.candidateAnswer || "(No answer)"}</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "8px" }}>
              <div style={{ background: "#22c55e11", border: "1px solid #22c55e22", borderRadius: "10px", padding: "10px" }}>
                <p style={{ color: "#4ade80", fontSize: "11px", margin: "0 0 4px", fontWeight: 600 }}>✅ STRENGTHS</p>
                <p style={{ color: "var(--text-secondary)", fontSize: "12px", margin: 0, lineHeight: 1.5 }}>{q.strengths}</p>
              </div>
              <div style={{ background: "#f59e0b11", border: "1px solid #f59e0b22", borderRadius: "10px", padding: "10px" }}>
                <p style={{ color: "#fbbf24", fontSize: "11px", margin: "0 0 4px", fontWeight: 600 }}>💡 GAPS</p>
                <p style={{ color: "var(--text-secondary)", fontSize: "12px", margin: 0, lineHeight: 1.5 }}>{q.gaps}</p>
              </div>
            </div>
            <div style={{ background: "#6366f111", border: "1px solid #6366f122", borderRadius: "10px", padding: "10px" }}>
              <p style={{ color: "#818cf8", fontSize: "11px", margin: "0 0 4px", fontWeight: 600 }}>📖 MODEL ANSWER</p>
              <p style={{ color: "var(--text-secondary)", fontSize: "12px", margin: 0, lineHeight: 1.6 }}>{q.modelAnswer}</p>
            </div>
          </div>
        );
      })}
      {canDelete && <Btn variant="danger" style={{ width: "100%", marginTop: "8px" }} onClick={onDelete}>🗑 Delete Report</Btn>}
      <div style={{ textAlign: "center", marginTop: "24px", paddingBottom: "8px" }}>
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{ background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "13px", padding: "6px 16px", borderRadius: "20px" }}>
          ↑ Back to top
        </button>
      </div>
      {showJrfModal && test?.jobRequisitionId && (
        <RequisitionDetailModal
          reqId={test.jobRequisitionId}
          currentUser={currentUser}
          onClose={() => setShowJrfModal(false)}
          onUpdated={() => {}}
        />
      )}
    </PageWrap>
  );
}

// ── ROUNDS HISTORY ──
function RoundsHistory({ rounds, loading, currentUser, test, allEvents = [], onReassigned, onRefresh }) {
  const [reassignRound, setReassignRound] = useState(null);

  if (loading) return <div style={{ padding: "12px", color: "var(--text-muted)", fontSize: "13px" }}>Loading rounds...</div>;

  const verdictColors = { shortlist: "#4ade80", hold: "#fbbf24", reject: "#f87171" };
  const verdictLabels = { shortlist: "Shortlist", hold: "Hold", reject: "Reject" };
  const canReassign = currentUser && ['hr', 'hr-admin', 'hiring-manager', 'superuser'].includes(currentUser.role);

  return (
    <div style={{ marginTop: "24px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
        <h3 style={{ color: "var(--text-primary)", fontSize: "15px", fontWeight: 700, margin: 0 }}>
          🎯 Technical Rounds
        </h3>
        {onRefresh && (
          <button onClick={onRefresh} style={{ background: "none", border: "1px solid var(--border-card)", color: "var(--text-muted)", borderRadius: "6px", padding: "3px 10px", fontSize: "11px", cursor: "pointer", fontWeight: 600 }}
            title="Refresh rounds">
            🔄 Refresh
          </button>
        )}
      </div>
      {rounds.length === 0 ? (
        <div style={{ color: "var(--text-muted)", fontSize: "13px", padding: "12px", background: "var(--bg-card-inner)", borderRadius: "8px" }}>
          No technical rounds assigned yet.
        </div>
      ) : rounds.map((r, i) => (
        <div key={r._id || i} style={{ background: "var(--bg-card-inner)", border: "1px solid var(--border-card)", borderRadius: "10px", padding: "14px 16px", marginBottom: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px", flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: "13px", color: "var(--text-primary)" }}>{r.roundLabel}</span>
            {r.verdict && (
              <span style={{ background: `${verdictColors[r.verdict]}22`, color: verdictColors[r.verdict], padding: "2px 9px", borderRadius: "20px", fontSize: "11px", fontWeight: 700 }}>
                {verdictLabels[r.verdict]}
              </span>
            )}
            {r.status === 'pending' && (
              <span style={{ background: "rgba(245,158,11,0.15)", color: "#f59e0b", padding: "2px 9px", borderRadius: "20px", fontSize: "11px", fontWeight: 700 }}>Pending</span>
            )}
            {r.recommendRound2 && (
              <span style={{ background: "rgba(192,132,252,0.15)", color: "#c084fc", padding: "2px 9px", borderRadius: "20px", fontSize: "11px" }}>Recommends Round 2</span>
            )}
            {r.recommendRound3 && (
              <span style={{ background: "rgba(251,146,60,0.15)", color: "#fb923c", padding: "2px 9px", borderRadius: "20px", fontSize: "11px" }}>Recommends Round 3</span>
            )}
            {r.status === 'pending' && (canReassign || r.assignedTo?.id === currentUser?.id) && (
              <button onClick={() => setReassignRound(r)}
                style={{ marginLeft: "auto", background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.3)", color: "#818cf8", borderRadius: "6px", padding: "3px 10px", fontSize: "11px", cursor: "pointer", fontWeight: 600 }}>
                🔄 Reassign
              </button>
            )}
          </div>
          <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px" }}>
            <strong>Assigned to:</strong> {r.assignedTo?.name} ({r.assignedTo?.role}) &nbsp;·&nbsp;
            <strong>By:</strong> {r.assignedBy?.name} &nbsp;·&nbsp;
            {new Date(r.assignedAt).toLocaleDateString()}
          </div>
          {r.feedback && (
            <div style={{ fontSize: "13px", color: "var(--text-secondary)", background: "var(--bg-card)", borderRadius: "6px", padding: "10px 14px", marginTop: "6px", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: "1.7" }}>
              <div style={{ color: "var(--text-muted)", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", marginBottom: "5px" }}>Feedback</div>
              {r.feedback}
            </div>
          )}
        </div>
      ))}
      {reassignRound && test && (
        <ReassignRoundModal
          round={reassignRound}
          test={test}
          allEvents={allEvents}
          currentUser={currentUser}
          onClose={() => setReassignRound(null)}
          onReassigned={() => { setReassignRound(null); if (onReassigned) onReassigned(); }}
        />
      )}
    </div>
  );
}

// ── REASSIGN ROUND MODAL ──
function ReassignRoundModal({ round, test, allEvents = [], currentUser, onClose, onReassigned }) {
  const [users, setUsers] = useState([]);
  const [assigneeId, setAssigneeId] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    async function loadUsers() {
      try {
        let list = [];
        if (round.roundNumber === 1) {
          const data = await apiGet('/api/users/directory');
          list = (data || []).filter(u => ['user', 'hiring-manager', 'hod'].includes(u.role))
            .map(u => ({ id: String(u.id || u._id), name: u.name, email: u.email, _group: u.role === 'hod' ? 'HODs' : u.role === 'hiring-manager' ? 'Hiring Managers' : 'Technical Interviewers' }));
        } else if (round.roundNumber === 2) {
          const data = await apiGet('/api/users/directory');
          list = (data || []).filter(u => ['user', 'hiring-manager', 'hod'].includes(u.role))
            .map(u => ({ id: String(u.id || u._id), name: u.name, email: u.email, _group: u.role === 'hod' ? 'HODs' : u.role === 'hiring-manager' ? 'Hiring Managers' : 'Technical Interviewers' }));
        } else if (round.roundNumber === 3) {
          const [dirData, hrData] = await Promise.all([
            apiGet('/api/users/directory'),
            apiGet('/api/users/hr-staff'),
          ]);
          const dir = (dirData || []).filter(u => ['hiring-manager', 'hod'].includes(u.role))
            .map(u => ({ id: String(u.id || u._id), name: u.name, email: u.email, _group: u.role === 'hod' ? 'HODs' : 'Hiring Managers' }));
          const hr = (hrData || []).filter(u => ['hr', 'hr-admin'].includes(u.role))
            .map(u => ({ id: String(u.id || u._id), name: u.name, email: u.email, _group: 'HR Staff' }));
          const hrIds = new Set(hr.map(u => u.id));
          list = [...hr, ...dir.filter(u => !hrIds.has(u.id))];
        } else if (round.roundNumber === 'hr') {
          const all = await apiGet('/api/users/hr-staff');
          const allHR = (all || []).filter(u => ['hr', 'hr-admin'].includes(u.role));
          const eventMatch = test.eventName && allEvents.find(ev => ev.name === test.eventName);
          const eventHrId = eventMatch?.hrCoordinator?.id ? String(eventMatch.hrCoordinator.id) : null;
          if (eventHrId) {
            list = [
              ...allHR.filter(u => String(u.id || u._id) === eventHrId).map(u => ({ id: String(u.id || u._id), name: u.name, email: u.email, _group: `Event HR (${eventMatch.name})` })),
              ...allHR.filter(u => String(u.id || u._id) !== eventHrId).map(u => ({ id: String(u.id || u._id), name: u.name, email: u.email, _group: 'Other HR Staff' }))
            ];
          } else {
            list = allHR.map(u => ({ id: String(u.id || u._id), name: u.name, email: u.email, _group: 'HR Staff' }));
          }
        }
        // Exclude current assignee
        setUsers(list.filter(u => u.id !== round.assignedTo?.id));
      } catch (e) { if (shouldShowError(e)) setErr('Failed to load users: ' + e.message); }
    }
    loadUsers();
  }, []);

  async function handleSubmit() {
    if (!assigneeId) { setErr('Select a new interviewer.'); return; }
    setSaving(true); setErr('');
    try {
      await apiPost(`/api/rounds/${round._id}/reassign`, { assigneeId });
      onReassigned();
    } catch (e) { if (shouldShowError(e)) setErr(e.message); }
    setSaving(false);
  }

  const groups = {};
  for (const u of users) {
    if (!groups[u._group]) groups[u._group] = [];
    groups[u._group].push(u);
  }

  const modalStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const cardStyle = { background: 'var(--bg-modal)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '24px', minWidth: '360px', maxWidth: '460px', width: '90vw', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' };

  return (
    <div style={modalStyle} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 6px', color: 'var(--text-primary)', fontSize: '15px' }}>🔄 Reassign {round.roundLabel}</h3>
        <p style={{ margin: '0 0 16px', color: 'var(--text-muted)', fontSize: '12px' }}>
          Currently assigned to: <strong style={{ color: 'var(--text-secondary)' }}>{round.assignedTo?.name}</strong>
        </p>
        {err && <div style={{ color: '#f87171', marginBottom: '12px', fontSize: '13px' }}>{err}</div>}
        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--text-secondary)' }}>New Interviewer</label>
        <select value={assigneeId} onChange={e => setAssigneeId(e.target.value)}
          style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-input)', fontSize: '14px', marginBottom: '18px' }}>
          <option value="">— Select new interviewer —</option>
          {Object.entries(groups).map(([grp, list]) => (
            <optgroup key={grp} label={grp}>
              {list.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
            </optgroup>
          ))}
        </select>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn onClick={handleSubmit} disabled={saving || !assigneeId}>
            {saving ? 'Reassigning…' : 'Confirm Reassign'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ── ASSIGN ROUND MODAL ──
function AssignRoundModal({ test, rounds, currentUser, allEvents = [], onClose, onAssigned }) {
  const status = test?.status;

  // Statuses where HR must explicitly choose the next round
  const choiceOptions = {
    round1_completed: [2, 'hr'],
    round2_completed: [3, 'hr'],
    hr_round_assigned: [2, 3],
  };
  const needsChoice = !!choiceOptions[status];

  function getDefault() {
    if (status === 'round1_completed') {
      const last = rounds.filter(r => r.roundNumber === 1).sort((a, b) => new Date(b.assignedAt) - new Date(a.assignedAt))[0];
      return (last?.recommendRound2 && last?.verdict === 'shortlist') ? 2 : 'hr';
    }
    if (status === 'round2_completed') {
      const last = rounds.filter(r => r.roundNumber === 2).sort((a, b) => new Date(b.assignedAt) - new Date(a.assignedAt))[0];
      return (last?.recommendRound3 && last?.verdict === 'shortlist') ? 3 : 'hr';
    }
    if (status === 'hr_round_assigned') {
      const completed = new Set(rounds.filter(r => r.status === 'completed').map(r => r.roundNumber));
      if (!completed.has(3)) return 3;
      if (!completed.has(2)) return 2;
      return null;
    }
    return null;
  }

  const completedRoundNumbers = new Set(rounds.filter(r => r.status === 'completed').map(r => r.roundNumber));

  const [selectedRound, setSelectedRound] = useState(getDefault);
  let autoRound = null;
  if (['completed', 'under_review'].includes(status)) autoRound = 1;
  else if (status === 'round3_completed') autoRound = 'hr';
  const nextRound = autoRound !== null ? autoRound : selectedRound;

  const roundLabels = { 1: 'Technical Round 1', 2: 'Technical Round 2', hr: 'HR Round', 3: 'Round 3' };

  const [users, setUsers] = useState([]);
  const [assigneeId, setAssigneeId] = useState('');
  const [cvFile, setCvFile] = useState(null);
  const [cvError, setCvError] = useState('');
  const [existingCv, setExistingCv] = useState(null);
  const [cvDeleting, setCvDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!test || !nextRound) return;
    setAssigneeId('');
    setUsers([]);
    async function loadUsers() {
      try {
        let list = [];
        if (nextRound === 1) {
          const [dirData, roundsData] = await Promise.all([
            apiGet('/api/users/directory'),
            apiGet(`/api/rounds?code=${test.code}`),
          ]);
          list = (dirData || []).filter(u => ['user', 'hiring-manager', 'hod'].includes(u.role))
            .map(u => ({ ...u, id: String(u.id || u._id), _group: u.role === 'hod' ? 'HODs' : u.role === 'hiring-manager' ? 'Hiring Managers' : 'Technical Interviewers' }));
          setExistingCv(roundsData?.cvMeta || null);
        } else if (nextRound === 2) {
          // Fetch rounds fresh — do not rely on prop which may be empty due to load timing
          const freshData = await apiGet(`/api/rounds?code=${test.code}`);
          const freshRounds = freshData?.rounds || [];
          const lastRound1 = freshRounds.filter(r => r.roundNumber === 1)
            .sort((a, b) => new Date(b.assignedAt) - new Date(a.assignedAt))[0];
          const round1AssigneeId = lastRound1?.assignedTo?.id ? String(lastRound1.assignedTo.id) : null;
          const allData = await apiGet('/api/users/directory');
          list = (allData || []).filter(u => ['user', 'hiring-manager', 'hod'].includes(u.role))
            .map(u => ({ ...u, id: String(u.id || u._id), _group: u.role === 'hod' ? 'HODs' : u.role === 'hiring-manager' ? 'Hiring Managers' : 'Technical Interviewers' }))
            .filter(u => !round1AssigneeId || u.id !== round1AssigneeId);
        } else if (nextRound === 3) {
          const [dirData, hrData] = await Promise.all([
            apiGet('/api/users/directory'),
            apiGet('/api/users/hr-staff'),
          ]);
          const dir = (dirData || []).filter(u => ['hiring-manager', 'hod'].includes(u.role))
            .map(u => ({ ...u, id: String(u.id || u._id), _group: u.role === 'hod' ? 'HODs' : 'Hiring Managers' }));
          const hr = (hrData || []).filter(u => ['hr', 'hr-admin'].includes(u.role))
            .map(u => ({ ...u, id: String(u.id || u._id), _group: 'HR Staff' }));
          const hrIds = new Set(hr.map(u => u.id));
          list = [...hr, ...dir.filter(u => !hrIds.has(u.id))];
        } else if (nextRound === 'hr') {
          const all = await apiGet('/api/users/hr-staff');
          const allHR = (all || []).filter(u => ['hr', 'hr-admin'].includes(u.role));
          const eventMatch = test.eventName && allEvents.find(ev => ev.name === test.eventName);
          const eventHrId = eventMatch?.hrCoordinator?.id ? String(eventMatch.hrCoordinator.id) : null;
          if (eventHrId) {
            const eventHR = allHR.filter(u => String(u.id || u._id) === eventHrId);
            const otherHR = allHR.filter(u => String(u.id || u._id) !== eventHrId);
            list = [
              ...eventHR.map(u => ({ id: String(u.id || u._id), name: u.name, email: u.email, _group: `Event HR (${eventMatch.name})` })),
              ...otherHR.map(u => ({ id: String(u.id || u._id), name: u.name, email: u.email, _group: 'Other HR Staff' }))
            ];
          } else {
            list = allHR.map(u => ({ id: String(u.id || u._id), name: u.name, email: u.email, _group: 'HR Staff' }));
          }
        }
        setUsers(list);
      } catch (e) { if (shouldShowError(e)) setErr('Failed to load users: ' + e.message); }
    }
    loadUsers();
  }, [nextRound]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDeleteCv() {
    setCvDeleting(true);
    try {
      await apiDelete(`/api/tests/${test.code}/cv`);
      setExistingCv(null);
    } catch (e) { if (shouldShowError(e)) setErr('Failed to delete CV: ' + e.message); }
    setCvDeleting(false);
  }

  function handleFileChange(e) {
    const f = e.target.files?.[0];
    if (!f) { setCvFile(null); return; }
    if (f.size > 5 * 1024 * 1024) { setCvError('File must be under 5MB'); setCvFile(null); return; }
    if (!f.name.match(/\.(pdf|doc|docx)$/i)) { setCvError('Only PDF, DOC, and DOCX files allowed'); setCvFile(null); return; }
    setCvError('');
    setCvFile(f);
  }

  async function handleSubmit() {
    if (!assigneeId) { setErr('Select an interviewer.'); return; }
    setSaving(true);
    setErr('');
    try {
      await apiPost(`/api/tests/${test.code}/assign-round`, { assigneeId, roundNumber: nextRound });
      if (cvFile) {
        const fd = new FormData();
        fd.append('cv', cvFile);
        await apiPostFile(`/api/tests/${test.code}/cv`, fd);
      }
      onAssigned();
      onClose();
    } catch (e) { if (shouldShowError(e)) setErr(e.message); }
    setSaving(false);
  }

  const modalStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const cardStyle = { background: 'var(--bg-modal)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '28px', minWidth: '380px', maxWidth: '480px', width: '90vw', boxShadow: '0 24px 64px rgba(0,0,0,0.5)', maxHeight: '90vh', overflowY: 'auto' };

  const groups = {};
  for (const u of users) {
    if (!groups[u._group]) groups[u._group] = [];
    groups[u._group].push(u);
  }

  return (
    <div style={modalStyle} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 16px', color: 'var(--text-primary)', fontSize: '16px' }}>
          {status === 'hr_round_assigned' ? `🔄 Push Back to ${roundLabels[nextRound] || 'Round'}` : `👤 Assign ${roundLabels[nextRound] || 'Round'}`}
        </h3>
        {err && <div style={{ color: '#f87171', marginBottom: '12px', fontSize: '13px' }}>{err}</div>}

        {needsChoice && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 600 }}>Select Round</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {choiceOptions[status].map(r => {
                const isDisabled = status === 'hr_round_assigned' && completedRoundNumbers.has(r);
                return (
                  <button key={r} onClick={() => !isDisabled && setSelectedRound(r)} disabled={isDisabled}
                    style={{ flex: 1, padding: '9px 8px', borderRadius: '8px', border: selectedRound === r ? '2px solid #818cf8' : '1px solid var(--border-card)', background: isDisabled ? 'var(--bg-card-inner)' : selectedRound === r ? 'rgba(129,140,248,0.15)' : 'var(--bg-card-inner)', color: isDisabled ? 'var(--text-faint)' : selectedRound === r ? '#a5b4fc' : 'var(--text-secondary)', cursor: isDisabled ? 'not-allowed' : 'pointer', fontWeight: selectedRound === r && !isDisabled ? 700 : 400, fontSize: '13px', opacity: isDisabled ? 0.45 : 1 }}>
                    {roundLabels[r]}{isDisabled ? ' (done)' : ''}
                  </button>
                );
              })}
            </div>
            {status === 'hr_round_assigned' && (
              <div style={{ marginTop: '8px', padding: '8px 12px', background: 'rgba(251,146,60,0.1)', border: '1px solid rgba(251,146,60,0.3)', borderRadius: '8px', color: '#fb923c', fontSize: '12px' }}>
                ⚠️ The pending HR Round assignment will be cancelled and replaced by this round.
              </div>
            )}
          </div>
        )}

        <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>Interviewer</label>
        <select value={assigneeId} onChange={e => setAssigneeId(e.target.value)}
          style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-input)', fontSize: '14px', marginBottom: '16px' }}>
          <option value="">— Select interviewer —</option>
          {Object.entries(groups).map(([grp, list]) => (
            <optgroup key={grp} label={grp}>
              {list.map(u => <option key={u.id} value={String(u.id)}>{u.name} ({u.email})</option>)}
            </optgroup>
          ))}
        </select>

        {nextRound === 1 && (
          <div style={{ background: 'var(--bg-card-inner)', border: '1px solid var(--border-card)', borderRadius: '10px', padding: '12px', marginBottom: '16px' }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 600, margin: '0 0 10px' }}>📄 Candidate Resume</p>
            {existingCv ? (
              <div style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                  <div>
                    <span style={{ color: '#4ade80', fontSize: '12px', fontWeight: 600 }}>✓ {existingCv.fileName}</span>
                    <span style={{ color: 'var(--text-faint)', fontSize: '11px', marginLeft: '8px' }}>{existingCv.fileSize ? `${(existingCv.fileSize / 1024).toFixed(0)} KB` : ''} · {fmtDate(existingCv.uploadedAt)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <a href={`${API}/api/tests/${test.code}/cv`} target="_blank" rel="noreferrer"
                      style={{ fontSize: '11px', color: '#818cf8', textDecoration: 'none', padding: '3px 8px', border: '1px solid rgba(129,140,248,0.4)', borderRadius: '5px' }}>
                      ⬇ View
                    </a>
                    <button onClick={handleDeleteCv} disabled={cvDeleting}
                      style={{ fontSize: '11px', color: '#f87171', background: 'none', border: '1px solid rgba(248,113,113,0.4)', borderRadius: '5px', padding: '3px 8px', cursor: 'pointer' }}>
                      {cvDeleting ? '…' : '🗑 Delete'}
                    </button>
                  </div>
                </div>
                <p style={{ color: 'var(--text-faint)', fontSize: '11px', margin: '6px 0 0' }}>To replace, select a new file below — it will overwrite the existing one.</p>
              </div>
            ) : (
              <p style={{ color: 'var(--text-muted)', fontSize: '12px', margin: '0 0 8px' }}>No resume uploaded yet.</p>
            )}
            <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange}
              style={{ width: '100%', fontSize: '12px', color: 'var(--text-primary)' }} />
            {cvFile && <div style={{ fontSize: '12px', color: '#4ade80', marginTop: '4px' }}>✓ {cvFile.name} ({(cvFile.size / 1024).toFixed(0)} KB) — will upload on assign</div>}
            {cvError && <div style={{ fontSize: '12px', color: '#f87171', marginTop: '4px' }}>{cvError}</div>}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' }}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn onClick={handleSubmit} disabled={saving || !assigneeId}>
            {saving ? (status === 'hr_round_assigned' ? 'Pushing Back…' : 'Assigning…') : status === 'hr_round_assigned' ? `Push Back to ${roundLabels[nextRound] || 'Round'}` : `Assign ${roundLabels[nextRound] || 'Round'}`}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ── ROUND VERDICT MODAL ──
function RoundVerdictModal({ round, test, allEvents = [], onClose, onSubmitted }) {
  const [verdict, setVerdict] = useState('');
  const [feedback, setFeedback] = useState('');
  const [recommendRound2, setRecommendRound2] = useState(false);
  const [recommendRound3, setRecommendRound3] = useState(false);
  const [nextAssigneeId, setNextAssigneeId] = useState('');
  const [nextUsers, setNextUsers] = useState([]);
  const [loadingNextUsers, setLoadingNextUsers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Determine next round when verdict is shortlist
  const nextRound = verdict === 'shortlist'
    ? (round.roundNumber === 1 ? (recommendRound2 ? 2 : 'hr')
       : (round.roundNumber === 2 ? (recommendRound3 ? 3 : 'hr')
       : (round.roundNumber === 3 ? 'hr' : null)))
    : null;

  useEffect(() => {
    if (!nextRound) { setNextUsers([]); setNextAssigneeId(''); return; }
    setLoadingNextUsers(true);
    setNextAssigneeId('');
    async function loadNextUsers() {
      try {
        let list = [];
        if (nextRound === 2) {
          const round1AssigneeId = round.assignedTo?.id ? String(round.assignedTo.id) : null;
          const allData = await apiGet('/api/users/directory');
          list = (allData || []).filter(u => ['user', 'hiring-manager', 'hod'].includes(u.role))
            .map(u => ({ ...u, id: String(u.id || u._id), _group: u.role === 'hod' ? 'HODs' : u.role === 'hiring-manager' ? 'Hiring Managers' : 'Technical Interviewers' }))
            .filter(u => u.id !== round1AssigneeId);
        } else if (nextRound === 3) {
          const [dirData, hrData] = await Promise.all([apiGet('/api/users/directory'), apiGet('/api/users/hr-staff')]);
          const dir = (dirData || []).filter(u => ['hiring-manager', 'hod'].includes(u.role))
            .map(u => ({ ...u, id: String(u.id || u._id), _group: u.role === 'hod' ? 'HODs' : 'Hiring Managers' }));
          const hr = (hrData || []).filter(u => ['hr', 'hr-admin'].includes(u.role))
            .map(u => ({ ...u, id: String(u.id || u._id), _group: 'HR Staff' }));
          const hrIds = new Set(hr.map(u => u.id));
          list = [...hr, ...dir.filter(u => !hrIds.has(u.id))];
        } else if (nextRound === 'hr') {
          const all = await apiGet('/api/users/hr-staff');
          const allHR = (all || []).filter(u => ['hr', 'hr-admin'].includes(u.role));
          const eventMatch = test?.eventName && allEvents.find(ev => ev.name === test.eventName);
          const eventHrId = eventMatch?.hrCoordinator?.id ? String(eventMatch.hrCoordinator.id) : null;
          if (eventHrId) {
            const eventHR = allHR.filter(u => String(u.id || u._id) === eventHrId);
            const otherHR = allHR.filter(u => String(u.id || u._id) !== eventHrId);
            list = [
              ...eventHR.map(u => ({ id: String(u.id || u._id), name: u.name, email: u.email, _group: `Event HR (${eventMatch.name})` })),
              ...otherHR.map(u => ({ id: String(u.id || u._id), name: u.name, email: u.email, _group: 'Other HR Staff' }))
            ];
          } else {
            list = allHR.map(u => ({ id: String(u.id || u._id), name: u.name, email: u.email, _group: 'HR Staff' }));
          }
        }
        setNextUsers(list);
      } catch { /* no-op */ }
      setLoadingNextUsers(false);
    }
    loadNextUsers();
  }, [nextRound]);

  async function handleSubmit() {
    if (!verdict) { setErr('Select a verdict.'); return; }
    if (!feedback.trim()) { setErr('Feedback is required.'); return; }
    setSaving(true);
    setErr('');
    try {
      await apiPost(`/api/rounds/${round._id}/verdict`, {
        verdict, feedback: feedback.trim(), recommendRound2, recommendRound3,
        ...(nextAssigneeId ? { nextAssigneeId } : {})
      });
      onSubmitted();
      onClose();
    } catch (e) { if (shouldShowError(e)) setErr(e.message); }
    setSaving(false);
  }

  const modalStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const cardStyle = { background: 'var(--bg-modal)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '28px', minWidth: '480px', maxWidth: '640px', width: '90vw', boxShadow: '0 24px 64px rgba(0,0,0,0.5)', maxHeight: '90vh', overflowY: 'auto' };
  const selectStyle = { width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-input)', fontSize: '14px', marginBottom: '16px' };

  const nextUserGroups = {};
  for (const u of nextUsers) {
    if (!nextUserGroups[u._group]) nextUserGroups[u._group] = [];
    nextUserGroups[u._group].push(u);
  }
  const nextRoundLabel = nextRound === 2 ? 'Technical Round 2' : nextRound === 3 ? 'Round 3' : 'HR Round';

  return (
    <div style={modalStyle} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 16px', color: 'var(--text-primary)', fontSize: '16px' }}>
          ✍ Submit Verdict — {round.roundLabel}
        </h3>
        {err && <div style={{ color: '#f87171', marginBottom: '12px', fontSize: '13px' }}>{err}</div>}
        <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>Verdict</label>
        <select value={verdict} onChange={e => { setVerdict(e.target.value); if (e.target.value !== 'shortlist') { setRecommendRound2(false); setRecommendRound3(false); } }}
          style={selectStyle}>
          <option value="">— Select verdict —</option>
          <option value="shortlist">✅ Shortlist</option>
          <option value="hold">⏸ Hold</option>
          <option value="reject">❌ Reject</option>
        </select>

        {round.roundNumber === 1 && verdict === 'shortlist' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', fontSize: '13px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={recommendRound2} onChange={e => setRecommendRound2(e.target.checked)} />
            Recommend 2nd technical round before HR round
          </label>
        )}

        {round.roundNumber === 2 && verdict === 'shortlist' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', fontSize: '13px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={recommendRound3} onChange={e => setRecommendRound3(e.target.checked)} />
            Recommend Round 3 before HR round
          </label>
        )}

        {nextRound && (
          <>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>
              Assign {nextRoundLabel} to <span style={{ color: 'var(--text-muted)' }}>(optional — assign later if needed)</span>
            </label>
            <select value={nextAssigneeId} onChange={e => setNextAssigneeId(e.target.value)} style={selectStyle} disabled={loadingNextUsers}>
              <option value="">{loadingNextUsers ? 'Loading…' : '— Assign later —'}</option>
              {Object.entries(nextUserGroups).map(([grp, list]) => (
                <optgroup key={grp} label={grp}>
                  {list.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
                </optgroup>
              ))}
            </select>
          </>
        )}

        <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>Feedback <span style={{ color: 'var(--text-muted)' }}>({feedback.length}/2000)</span></label>
        <textarea value={feedback} onChange={e => setFeedback(e.target.value.slice(0, 2000))} rows={12} maxLength={2000}
          placeholder="Share your assessment of the candidate's technical skills, communication, and overall suitability..."
          style={{ width: '100%', padding: '14px 16px', borderRadius: '8px', border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-input)', fontSize: '15px', resize: 'vertical', boxSizing: 'border-box', marginBottom: '16px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word', lineHeight: '1.7', minHeight: '200px' }} />

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn onClick={handleSubmit} disabled={saving || !verdict || !feedback.trim()}>
            {saving ? 'Submitting…' : 'Submit Verdict'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ── REPORT ROW ──
function ReportRow({ test, onView, onDelete, canDelete = true }) {
  // Scores come from the server-side aggregation (test.avgScore, test.mandatoryTotal, test.mandatoryPassed)
  const avg = test.avgScore != null ? test.avgScore : null;
  const mandatoryStats = test.mandatoryTotal > 0 ? { total: test.mandatoryTotal, passed: test.mandatoryPassed } : null;
  const rec = avg >= 7
    ? { label: "Shortlist", color: "#4ade80", bg: "#22c55e22" }
    : avg >= 5
      ? { label: "Hold", color: "#fbbf24", bg: "#f59e0b22" }
      : { label: "Reject", color: "#f87171", bg: "#ef444422" };
  return (
    <div style={{ background: "var(--bg-card-inner)", borderRadius: "14px", padding: "14px 18px", marginBottom: "10px", border: "1px solid var(--border-card)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ color: "var(--text-secondary)", fontWeight: 700, margin: "0 0 3px", fontSize: "14px" }}>👤 {test.candidateName}</p>
        <p style={{ color: "var(--text-muted)", margin: "0 0 3px", fontSize: "12px" }}>{test.trackIcon} {test.trackName} · {test.expLabel}</p>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <p style={{ color: "var(--text-faint)", margin: 0, fontSize: "11px" }}>🗓 Created: <span style={{ color: "var(--nav-active-color)" }}>{fmtDate(test.createdAt)}</span></p>
          <p style={{ color: "var(--text-faint)", margin: 0, fontSize: "11px" }}>✅ Completed: <span style={{ color: "#4ade80" }}>{test.completedAt ? fmtDate(test.completedAt) : "Pending"}</span></p>
        </div>
        <p style={{ color: "var(--text-faint)", margin: "2px 0 0", fontSize: "11px" }}>
          Code: <span style={{ color: "var(--nav-active-color)", fontFamily: "monospace" }}>{test.code}</span>
          {test.hasQuestionBank ? <span style={{ color: "#fbbf24" }}> · 📂 Bank</span> : null}
          {test.skipOnlineTest ? <span style={{ color: "#f87171" }}> · 🚫 Online Skipped</span> : null}
        </p>
        {(test.eventName || test.hiringManager?.name || (['round1_assigned', 'round2_assigned', 'hr_round_assigned', 'round3_assigned'].includes(test.status) && test.currentAssignee?.name)) && (
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "4px" }}>
            {test.eventName && <span style={{ background: "rgba(129,140,248,0.1)", color: "var(--nav-active-color)", padding: "1px 7px", borderRadius: "20px", fontSize: "10px" }}>📅 {test.eventName}</span>}
            {test.hiringManager?.name && <span style={{ background: "rgba(251,191,36,0.08)", color: "#fbbf24", padding: "1px 7px", borderRadius: "20px", fontSize: "10px" }}>👤 {test.hiringManager.name}</span>}
            {['round1_assigned', 'round2_assigned', 'hr_round_assigned', 'round3_assigned'].includes(test.status) && test.currentAssignee?.name && (
              <span style={{ background: "rgba(245,158,11,0.1)", color: "#f59e0b", padding: "1px 7px", borderRadius: "20px", fontSize: "10px" }}>🎯 {test.currentAssignee.name}</span>
            )}
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: "4px", alignItems: "center" }}>
          {avg !== null && (
            <>
              <div style={{ color: "var(--nav-active-color)", fontWeight: 800, fontSize: "18px" }}>{avg}/10</div>
              <div style={{ background: rec.bg, color: rec.color, borderRadius: "20px", padding: "2px 10px", fontSize: "11px", fontWeight: 700 }}>{rec.label}</div>
              {mandatoryStats && (
                <div style={{ fontSize: "10px", color: mandatoryStats.passed === mandatoryStats.total ? "#4ade80" : mandatoryStats.passed > 0 ? "#fbbf24" : "#f87171", fontWeight: 600 }}>
                  ⚑ {mandatoryStats.passed}/{mandatoryStats.total} mandatory
                </div>
              )}
            </>
          )}
          {test.status && <StatusBadge status={test.status} />}
        </div>
        <Btn style={{ padding: "8px 14px", fontSize: "12px" }} onClick={onView}>View</Btn>
        {canDelete && <Btn variant="danger" style={{ padding: "8px 12px", fontSize: "12px" }} onClick={onDelete}>🗑</Btn>}
      </div>
    </div>
  );
}

// ── USER MANAGEMENT ──
function UserManagement({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [hiringManagers, setHiringManagers] = useState([]);
  const [hodUsers, setHodUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", role: "hr", hiringManagerId: "", hodId: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const ROLE_OPTS = [{ value: "hr", label: "HR" }, { value: "hr-admin", label: "HR Admin" }, { value: "hiring-manager", label: "Hiring Manager" }, { value: "user", label: "User (Read-Only)" }, { value: "hod", label: "HOD" }, { value: "executive", label: "Executive" }];
  const ROLE_COLORS = { superuser: "#f87171", "hr-admin": "#818cf8", hr: "#4ade80", "hiring-manager": "#fbbf24", user: "#94a3b8", hod: "#38bdf8", executive: "#fb923c" };
  const selStyle = { width: "100%", padding: "10px 12px", borderRadius: "10px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px" };

  async function load() {
    setLoading(true);
    try {
      const [u, hm, dir] = await Promise.all([apiGet("/api/users"), apiGet("/api/users/hiring-managers"), apiGet("/api/users/directory")]);
      setUsers(u); setHiringManagers(hm);
      setHodUsers((dir || []).filter(d => d.role === "hod"));
    } catch (e) { if (shouldShowError(e)) setErr("Failed to load: " + e.message); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  function openCreate() { setEditUser(null); setForm({ name: "", email: "", role: "hr", hiringManagerId: "", hodId: "" }); setErr(""); setShowForm(true); }
  function openEdit(u) { setEditUser(u); setForm({ name: u.name, email: u.email, role: u.role, hiringManagerId: u.hiringManagerId || "", hodId: u.hodId || "" }); setErr(""); setShowForm(true); }

  async function handleSave() {
    if (!form.name.trim() || (!editUser && !form.email.trim())) { setErr("Name and email required."); return; }
    if (form.role === "user" && !form.hiringManagerId) { setErr("Select a hiring manager for this user."); return; }
    if (form.role === "hiring-manager" && !form.hodId) { setErr("Select a HOD for this hiring manager."); return; }
    setSaving(true); setErr("");
    try {
      if (editUser) {
        await apiPatch(`/api/users/${editUser.id}`, { name: form.name, role: form.role, hiringManagerId: form.hiringManagerId || null, hodId: form.hodId || null });
      } else {
        await apiPost("/api/users", { name: form.name, email: form.email, role: form.role, hiringManagerId: form.hiringManagerId || null, hodId: form.hodId || null });
      }
      setSuccessMsg(editUser ? "User updated." : "User created. Welcome email sent."); setShowForm(false); setEditUser(null);
      load(); setTimeout(() => setSuccessMsg(""), 4000);
    } catch (e) { setErr(e.message || "Save failed."); }
    setSaving(false);
  }

  async function handleDeactivate(u) {
    if (!window.confirm(`Deactivate ${u.name}? They will no longer be able to log in.`)) return;
    try { await apiDelete(`/api/users/${u.id}`); setSuccessMsg("User deactivated."); load(); setTimeout(() => setSuccessMsg(""), 3000); }
    catch (e) { setErr("Failed: " + e.message); }
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <h2 style={{ color: "var(--text-primary)", fontSize: "18px", margin: 0 }}>👥 User Management</h2>
        <Btn style={{ padding: "8px 16px", fontSize: "13px" }} onClick={openCreate}>➕ Add User</Btn>
      </div>
      {successMsg && <div style={{ background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.3)", borderRadius: "8px", padding: "10px 14px", marginBottom: "12px", color: "#4ade80", fontSize: "13px" }}>✅ {successMsg}</div>}
      {err && <div style={{ background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)", borderRadius: "8px", padding: "10px 14px", marginBottom: "12px", color: "#f87171", fontSize: "13px" }}>❌ {err}</div>}

      {showForm && (
        <Card style={{ marginBottom: "16px" }}>
          <h3 style={{ color: "var(--text-primary)", fontSize: "15px", margin: "0 0 16px" }}>{editUser ? "Edit User" : "Create New User"}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <div>
              <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 5px" }}>Full Name *</p>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" />
            </div>
            <div>
              <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 5px" }}>Email *</p>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@isgdigitalfirst.com" style={{ opacity: editUser ? 0.6 : 1 }} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: (form.role === "user" || form.role === "hiring-manager") ? "1fr 1fr" : "1fr", gap: "12px", marginBottom: "14px" }}>
            <div>
              <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 5px" }}>Role *</p>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value, hiringManagerId: "", hodId: "" }))} style={selStyle}>
                {ROLE_OPTS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            {form.role === "user" && (
              <div>
                <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 5px" }}>Assigned Hiring Manager *</p>
                <select value={form.hiringManagerId} onChange={e => setForm(f => ({ ...f, hiringManagerId: e.target.value }))} style={selStyle}>
                  <option value="">— Select —</option>
                  {hiringManagers.map(hm => <option key={hm.id} value={String(hm.id)}>{hm.name}</option>)}
                </select>
              </div>
            )}
            {form.role === "hiring-manager" && (
              <div>
                <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 5px" }}>Assigned HOD *</p>
                <select value={form.hodId} onChange={e => setForm(f => ({ ...f, hodId: e.target.value }))} style={{ ...selStyle, borderColor: !form.hodId ? "rgba(248,113,113,0.5)" : "var(--border-input)" }}>
                  <option value="">— Select HOD —</option>
                  {hodUsers.map(h => <option key={String(h.id)} value={String(h.id)}>{h.name}</option>)}
                </select>
                {hodUsers.length === 0 && <p style={{ color: "#f87171", fontSize: "11px", margin: "4px 0 0" }}>No HOD users found. Create a HOD user first.</p>}
              </div>
            )}
          </div>
          {!editUser && <p style={{ color: "var(--text-faint)", fontSize: "11px", margin: "0 0 14px" }}>ℹ️ A temporary password will be emailed. User must change it on first login.</p>}
          <div style={{ display: "flex", gap: "8px" }}>
            <Btn onClick={handleSave} disabled={saving} style={{ padding: "9px 20px", fontSize: "13px" }}>{saving ? "Saving..." : editUser ? "Update" : "Create User"}</Btn>
            <Btn variant="ghost" onClick={() => { setShowForm(false); setEditUser(null); setErr(""); }} style={{ padding: "9px 18px", fontSize: "13px" }}>Cancel</Btn>
          </div>
        </Card>
      )}

      {loading ? <p style={{ color: "var(--text-faint)", fontSize: "13px" }}>Loading...</p> : (
        <div>
          {users.length === 0 && <p style={{ color: "var(--text-faint)", fontSize: "13px" }}>No users found.</p>}
          {users.map(u => (
            <div key={u.id} style={{ background: "var(--bg-card-inner)", borderRadius: "12px", padding: "14px 16px", marginBottom: "8px", border: "1px solid var(--border-card)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap", opacity: u.isActive ? 1 : 0.55 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "3px", flexWrap: "wrap" }}>
                  <span style={{ color: "var(--text-primary)", fontWeight: 700, fontSize: "14px" }}>{u.name}</span>
                  <span style={{ background: `${ROLE_COLORS[u.role] || "#94a3b8"}22`, color: ROLE_COLORS[u.role] || "#94a3b8", padding: "2px 8px", borderRadius: "20px", fontSize: "10px", fontWeight: 700 }}>{u.role}</span>
                  {!u.isActive && <span style={{ color: "#f87171", fontSize: "10px", fontWeight: 700 }}>INACTIVE</span>}
                  {u.isTempPassword && <span style={{ color: "#fbbf24", fontSize: "10px" }}>⚠️ temp pw</span>}
                </div>
                <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: 0 }}>{u.email}</p>
                {u.hiringManagerId && <p style={{ color: "var(--text-faint)", fontSize: "11px", margin: "2px 0 0" }}>Assigned to: {hiringManagers.find(hm => String(hm.id) === String(u.hiringManagerId))?.name || u.hiringManagerId}</p>}
                {u.hodId && u.role === "hiring-manager" && <p style={{ color: "var(--text-faint)", fontSize: "11px", margin: "2px 0 0" }}>HOD: {hodUsers.find(h => String(h.id) === String(u.hodId))?.name || users.find(us => String(us.id) === String(u.hodId))?.name || u.hodId}</p>}
              </div>
              {u.role !== "superuser" && String(u.id) !== String(currentUser?.id) && (
                <div style={{ display: "flex", gap: "6px" }}>
                  <Btn variant="ghost" style={{ padding: "7px 12px", fontSize: "12px" }} onClick={() => openEdit(u)}>✏️ Edit</Btn>
                  {u.isActive && <Btn variant="danger" style={{ padding: "7px 12px", fontSize: "12px" }} onClick={() => handleDeactivate(u)}>🚫 Deactivate</Btn>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── EVENTS MANAGEMENT ──
function EventsManagement({ currentUser, allEvents, onEventsChanged }) {
  const canManage = ["hr", "hr-admin", "superuser"].includes(currentUser?.role);
  const canDelete = ["hr-admin", "superuser"].includes(currentUser?.role);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [eName, setEName] = useState("");
  const [eDate, setEDate] = useState("");
  const [eCommenceAt, setECommenceAt] = useState("");
  const [eHM, setEHM] = useState(null);
  const [eHRC, setEHRC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    apiGet("/api/users/directory").then(setUsers).catch(() => {});
  }, []);

  function openCreate() {
    setEditEvent(null); setEName(""); setEDate(""); setECommenceAt(""); setEHM(null); setEHRC(null); setErr(""); setShowForm(true);
  }
  function openEdit(ev) {
    setEditEvent(ev); setEName(ev.name); setEDate(ev.date || "");
    setECommenceAt(ev.commenceAt || "");
    setEHM(ev.hiringManager || null); setEHRC(ev.hrCoordinator || null); setErr(""); setShowForm(true);
  }
  async function saveEvent() {
    if (!eName.trim()) { setErr("Event name required."); return; }
    if (!eDate) { setErr("Event date required."); return; }
    setSaving(true); setErr("");
    try {
      const body = { name: eName.trim(), date: eDate, commenceAt: eCommenceAt || null, hiringManager: eHM, hrCoordinator: eHRC };
      if (editEvent) await apiPatch(`/api/events/${editEvent.id}`, body);
      else await apiPost("/api/events", body);
      setShowForm(false);
      onEventsChanged();
    } catch (e) {
      const msg = e.message === 'Failed to fetch'
        ? 'Could not reach the server. Please check your connection and try again.'
        : e.message;
      setErr("Failed to save: " + msg);
    }
    setSaving(false);
  }
  async function deleteEvent(ev) {
    if (!window.confirm(`Delete event "${ev.name}"? This cannot be undone.`)) return;
    try { await apiDelete(`/api/events/${ev.id}`); onEventsChanged(); }
    catch (e) { alert("Failed to delete: " + e.message); }
  }

  const hiringManagerUsers = users.filter(u => u.role === 'hiring-manager');
  const hrCoordinatorUsers = users.filter(u => ['hr', 'hr-admin'].includes(u.role));

  const selectStyle = { width: "100%", padding: "10px 12px", borderRadius: "10px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <h2 style={{ color: "var(--text-primary)", fontSize: "18px", margin: 0 }}>📅 Event Management</h2>
        {canManage && !showForm && <Btn onClick={openCreate} style={{ padding: "8px 16px", fontSize: "13px" }}>+ New Event</Btn>}
      </div>

      {showForm && (
        <Card style={{ marginBottom: "16px" }}>
          <h3 style={{ color: "var(--text-primary)", fontSize: "15px", margin: "0 0 14px" }}>{editEvent ? "✏️ Edit Event" : "➕ New Event"}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <div>
              <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 5px" }}>Event Name *</p>
              <Input value={eName} onChange={e => setEName(e.target.value)} placeholder="e.g. Campus Drive Q1 2026" />
            </div>
            <div>
              <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 5px" }}>Event Date *</p>
              <input type="date" value={eDate} onChange={e => setEDate(e.target.value)} style={{ ...selectStyle, boxSizing: "border-box" }} />
            </div>
            <div>
              <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 5px" }}>
                Commencement Date & Time <span style={{ color: "var(--text-faint)", fontStyle: "italic" }}>(tests unlock at this time)</span>
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: "0", background: "var(--bg-input)", border: "1px solid var(--border-input)", borderRadius: "10px", overflow: "hidden", boxSizing: "border-box" }}>
                <span style={{ padding: "0 10px", fontSize: "15px", lineHeight: 1, color: "var(--text-muted)", flexShrink: 0 }}>🗓</span>
                <input
                  type="datetime-local"
                  value={eCommenceAt}
                  onChange={e => setECommenceAt(e.target.value)}
                  style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "var(--text-input)", fontSize: "13px", padding: "10px 8px 10px 0", minWidth: 0 }}
                />
                {eCommenceAt && (
                  <button
                    type="button"
                    onClick={() => setECommenceAt("")}
                    title="Clear commencement date"
                    style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: "0 10px", fontSize: "14px", lineHeight: 1, flexShrink: 0 }}
                  >✕</button>
                )}
              </div>
              {eCommenceAt && <p style={{ color: "var(--text-faint)", fontSize: "11px", margin: "4px 0 0" }}>⏰ {fmtDate(eCommenceAt)}</p>}
            </div>
            <div>
              <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 5px" }}>Hiring Manager</p>
              <select value={eHM?.id || ""} onChange={e => { const u = hiringManagerUsers.find(u => String(u.id) === e.target.value); setEHM(u ? { id: u.id, name: u.name } : null); }} style={selectStyle}>
                <option value="">— None —</option>
                {hiringManagerUsers.map(u => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 5px" }}>HR Coordinator</p>
              <select value={eHRC?.id || ""} onChange={e => { const u = hrCoordinatorUsers.find(u => String(u.id) === e.target.value); setEHRC(u ? { id: u.id, name: u.name } : null); }} style={selectStyle}>
                <option value="">— None —</option>
                {hrCoordinatorUsers.map(u => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
              </select>
            </div>
          </div>
          {err && <p style={{ color: "#f87171", fontSize: "13px", marginBottom: "10px" }}>{err}</p>}
          <div style={{ display: "flex", gap: "8px" }}>
            <Btn onClick={saveEvent} disabled={saving}>{saving ? "Saving…" : "Save Event"}</Btn>
            <Btn variant="ghost" onClick={() => setShowForm(false)}>Cancel</Btn>
          </div>
        </Card>
      )}

      {allEvents.length === 0 && !showForm && <p style={{ color: "var(--text-faint)", fontSize: "13px" }}>No events yet. Create one to get started.</p>}
      {allEvents.map(ev => (
        <div key={ev.id} style={{ background: "var(--bg-card)", borderRadius: "12px", padding: "14px 16px", marginBottom: "8px", border: "1px solid var(--border-card)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px", flexWrap: "wrap" }}>
          <div style={{ flex: 1 }}>
            <div style={{ color: "var(--text-primary)", fontWeight: 700, fontSize: "14px", marginBottom: "4px" }}>{ev.name}</div>
            <div style={{ color: "var(--text-muted)", fontSize: "12px", display: "flex", gap: "14px", flexWrap: "wrap" }}>
              <span>📅 {ev.date}</span>
              {ev.commenceAt && <span>⏰ Opens: {fmtDate(ev.commenceAt)}</span>}
              {ev.hiringManager && <span>👤 HM: {ev.hiringManager.name}</span>}
              {ev.hrCoordinator && <span>🤝 HR Coord: {ev.hrCoordinator.name}</span>}
            </div>
          </div>
          {canManage && (
            <div style={{ display: "flex", gap: "6px" }}>
              <Btn variant="ghost" style={{ padding: "7px 12px", fontSize: "12px" }} onClick={() => openEdit(ev)}>✏️ Edit</Btn>
              {canDelete && <Btn variant="danger" style={{ padding: "7px 12px", fontSize: "12px" }} onClick={() => deleteEvent(ev)}>🗑 Delete</Btn>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── HISTORY VIEW (archived candidate records) — fetches its own data ──
function HistoryView({ hiringManagers, allEvents, canDelete, onDelete, onViewReport }) {
  const [statusF, setStatusF] = useState("all");
  const [dateF, setDateF] = useState("");
  const [resultF, setResultF] = useState("all");
  const [finalVerdictF, setFinalVerdictF] = useState("all");
  const [nameF, setNameF] = useState("");
  const [mandatoryF, setMandatoryF] = useState("all");
  const [hmF, setHmF] = useState("all");
  const [eventF, setEventF] = useState("");
  const [jrfF, setJrfF] = useState("");
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(20);
  const [historyResult, setHistoryResult] = useState({ tests: [], total: 0 });
  const [historyLoading, setHistoryLoading] = useState(false);

  function buildHistoryUrl(page, pSize) {
    const q = new URLSearchParams({ archived: 'true', page: page || historyPage, pageSize: pSize || historyPageSize });
    if (statusF !== 'all' && !finalVerdictF) q.set('status', statusF);
    if (finalVerdictF !== 'all') q.set('finalVerdict', finalVerdictF);
    else if (statusF !== 'all') q.set('status', statusF);
    if (dateF) q.set('date', dateF);
    if (nameF.trim()) q.set('name', nameF.trim());
    if (resultF !== 'all') q.set('result', resultF);
    if (mandatoryF !== 'all') q.set('mandatory', mandatoryF);
    if (hmF !== 'all') q.set('hmId', hmF);
    if (eventF) q.set('event', eventF);
    if (jrfF.trim()) q.set('jrfNumber', jrfF.trim().toUpperCase());
    return `/api/tests/with-scores?${q.toString()}`;
  }

  async function loadHistory(page, pSize) {
    setHistoryLoading(true);
    try {
      const data = await apiGet(buildHistoryUrl(page, pSize));
      setHistoryResult({ tests: data.tests || [], total: data.total || 0 });
    } catch {}
    setHistoryLoading(false);
  }

  useEffect(() => {
    const delay = nameF ? 400 : 0;
    const timer = setTimeout(() => loadHistory(historyPage, historyPageSize), delay);
    return () => clearTimeout(timer);
  }, [historyPage, historyPageSize, statusF, dateF, resultF, finalVerdictF, nameF, mandatoryF, hmF, eventF, jrfF]);

  useEffect(() => { setHistoryPage(1); }, [statusF, dateF, resultF, finalVerdictF, nameF, mandatoryF, hmF, eventF, jrfF]);

  const filterStyle = { width: "100%", padding: "8px 10px", borderRadius: "8px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px" };
  const { tests: pagedHistory, total: historyTotal } = historyResult;
  const historyTotalPages = Math.max(1, Math.ceil(historyTotal / historyPageSize));
  const isFiltered = statusF !== "all" || dateF !== "" || resultF !== "all" || nameF.trim() !== "" || mandatoryF !== "all" || hmF !== "all" || eventF !== "" || finalVerdictF !== "all" || jrfF.trim() !== "";

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <h2 style={{ color: "var(--text-primary)", fontSize: "18px", margin: 0 }}>📁 Historical Data</h2>
        <span style={{ color: "var(--text-faint)", fontSize: "13px" }}>{historyTotal} archived record{historyTotal !== 1 ? "s" : ""}</span>
      </div>

      <Card style={{ marginBottom: "16px", padding: "14px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
          <div><p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 6px" }}>Status</p>
            <select value={statusF} onChange={e => setStatusF(e.target.value)} style={filterStyle}>
              <option value="all">All Tests</option>
              {Object.entries(STATUS_STYLES).map(([key, s]) => (
                <option key={key} value={key}>{s.label}</option>
              ))}
            </select>
          </div>
          <div><p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 6px" }}>Date</p>
            <input type="date" value={dateF} onChange={e => setDateF(e.target.value)} style={{ ...filterStyle, boxSizing: "border-box" }} />
          </div>
          <div><p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 6px" }}>Result</p>
            <select value={resultF} onChange={e => setResultF(e.target.value)} style={filterStyle}>
              <option value="all">All Results</option>
              <option value="shortlist">✅ Shortlisted (≥7)</option>
              <option value="hold">⏸ Hold (5–6.9)</option>
              <option value="reject">❌ Reject (&lt;5)</option>
            </select>
          </div>
          <div><p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 6px" }}>Candidate Name</p>
            <input type="text" value={nameF} onChange={e => setNameF(e.target.value)} placeholder="Search name…" style={{ ...filterStyle, boxSizing: "border-box" }} />
          </div>
          <div><p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 6px" }}>Mandatory Solved</p>
            <select value={mandatoryF} onChange={e => setMandatoryF(e.target.value)} style={filterStyle}>
              <option value="all">All</option>
              <option value="full">✅ Full (all ≥7)</option>
              <option value="partial">⚠️ Partial</option>
              <option value="no">❌ None</option>
            </select>
          </div>
          <div><p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 6px" }}>Hiring Manager</p>
            <select value={hmF} onChange={e => setHmF(e.target.value)} style={filterStyle}>
              <option value="all">All</option>
              {hiringManagers.map(hm => <option key={hm.id} value={String(hm.id)}>{hm.name}</option>)}
            </select>
          </div>
          <div><p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 6px" }}>Event</p>
            <select value={eventF} onChange={e => setEventF(e.target.value)} style={filterStyle}>
              <option value="">All Events</option>
              {allEvents.map(ev => <option key={ev.id} value={ev.name}>{ev.name}</option>)}
            </select>
          </div>
          <div><p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 6px" }}>Final Verdict</p>
            <select value={finalVerdictF} onChange={e => setFinalVerdictF(e.target.value)} style={filterStyle}>
              <option value="all">All</option>
              <option value="shortlisted">✅ Shortlisted</option>
              <option value="on_hold">⏸ On Hold</option>
              <option value="rejected">❌ Rejected</option>
            </select>
          </div>
          <div><p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 6px" }}>Filter by JRF#</p>
            <input type="text" value={jrfF} onChange={e => setJrfF(e.target.value.toUpperCase())} placeholder="e.g. JRF160260001"
              style={{ ...filterStyle, boxSizing: "border-box", fontFamily: "monospace" }} />
          </div>
        </div>
      </Card>

      {historyLoading && <p style={{ color: "var(--text-faint)" }}>Loading…</p>}
      {!historyLoading && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
            <span style={{ color: "var(--text-muted)", fontSize: "13px" }}>{historyTotal} result{historyTotal !== 1 ? "s" : ""}</span>
            <div style={{ display: "flex", gap: "8px" }}>
              <Btn variant="ghost" style={{ padding: "7px 12px", fontSize: "16px" }} onClick={() => loadHistory()} title="Refresh">🔄</Btn>
              {isFiltered && <Btn variant="ghost" style={{ padding: "7px 14px", fontSize: "12px", color: "#f87171", borderColor: "rgba(248,113,113,0.3)" }} onClick={() => { setStatusF("all"); setDateF(""); setResultF("all"); setFinalVerdictF("all"); setNameF(""); setMandatoryF("all"); setHmF("all"); setEventF(""); setJrfF(""); }}>✕ Reset Filters</Btn>}
            </div>
          </div>
          {historyTotal === 0 && <p style={{ color: "var(--text-faint)", fontSize: "13px" }}>No archived records match your filters.</p>}
          {pagedHistory.map(t => (
            <ReportRow key={t.code} test={t} canDelete={canDelete}
              onView={() => onViewReport(t)}
              onDelete={() => onDelete(t.code)}
            />
          ))}
          {historyTotal > 0 && (
            <Pagination page={historyPage} totalPages={historyTotalPages} pageSize={historyPageSize}
              onPage={p => { setHistoryPage(p); loadHistory(p, historyPageSize); }}
              onPageSize={ps => { setHistoryPageSize(ps); setHistoryPage(1); loadHistory(1, ps); }} />
          )}
        </>
      )}
    </div>
  );
}

// ── QUESTION BANKS MANAGEMENT ──
function QuestionBanksManagement({ currentUser }) {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDesc, setUploadDesc] = useState("");
  const [uploadSubmitting, setUploadSubmitting] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");
  const fileInputRef = useRef(null);

  const role = currentUser?.role;
  const canDeleteAll = ["hr", "hr-admin", "superuser"].includes(role);

  async function loadBanks() {
    setLoading(true); setErr("");
    try {
      const data = await apiGet("/api/question-banks");
      setBanks(data);
    } catch (e) {
      if (shouldShowError(e)) setErr("Failed to load question banks.");
    }
    setLoading(false);
  }

  useEffect(() => { loadBanks(); }, []);

  function handleFileSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploadErr(""); setUploadSuccess("");
    if (!file.name.toLowerCase().endsWith('.xlsx')) { setUploadErr("Only .xlsx files are accepted."); return; }
    if (file.size > 5 * 1024 * 1024) { setUploadErr("File too large. Max 5 MB."); return; }
    setUploadFile(file);
  }

  async function handleUpload() {
    if (!uploadFile) return;
    const desc = uploadDesc.trim();
    if (!desc) { setUploadErr("Description is required."); return; }
    if (desc.length > 100) { setUploadErr("Description must be 100 characters or fewer."); return; }
    setUploadSubmitting(true); setUploadErr(""); setUploadSuccess("");
    try {
      const token = getAuthToken();
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('description', desc);
      const res = await fetch(`${API}/api/question-banks/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Upload failed');
      setUploadSuccess(`Uploaded "${json.fileName}" — ${json.questionCount} questions.`);
      setUploadFile(null);
      setUploadDesc("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      loadBanks();
    } catch (e) {
      if (shouldShowError(e)) setUploadErr(e.message || "Upload failed.");
    }
    setUploadSubmitting(false);
  }

  async function handleDelete(fileName) {
    if (!window.confirm(`Delete question bank "${fileName}"? This cannot be undone.`)) return;
    try {
      const token = getAuthToken();
      const res = await fetch(`${API}/api/question-banks/file/${encodeURIComponent(fileName)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || 'Delete failed');
      }
      setBanks(prev => prev.filter(b => b.fileName !== fileName));
    } catch (e) {
      if (shouldShowError(e)) alert("Delete failed: " + e.message);
    }
  }

  function handleDownload(fileName) {
    const token = getAuthToken();
    fetch(`${API}/api/question-banks/file/${encodeURIComponent(fileName)}/download`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error("Download failed"); return r.blob(); })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = fileName; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(e => alert("Download failed: " + e.message));
  }

  function downloadTemplate() {
    const wb = XLSX.utils.book_new();
    const rows = [
      ["question", "topic", "type", "difficulty", "mandatory", "actual answer", "mandatory to answer", "sort order"],
      ["What is polymorphism in OOP?", "OOP", "technical", "medium", "Y", "Polymorphism allows objects of different classes to be treated as objects of a common superclass.", "Y", 1],
      ["Write a function to reverse a string.", "Strings", "programming", "basic", "", "", "", 2],
      ["If a train travels 120 km in 2 hours, what is its speed?", "Aptitude", "aptitude", "basic", "", "60 km/h", "", 3],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 52 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 10 }, { wch: 52 }, { wch: 18 }, { wch: 10 }];
    const refRows = [
      ["Column", "Valid Values / Notes"],
      ["type", "technical | aptitude | programming"],
      ["difficulty", "basic | medium | complex"],
      ["mandatory", "Y = question is always included in interview (not shuffled out). Leave blank = optional."],
      ["actual answer", "Expected/model answer used by AI for scoring. Optional."],
      ["mandatory to answer", "Y = candidate must provide an answer (cannot skip). Leave blank = optional."],
      ["sort order", "Number — lower = shown first. Leave blank to shuffle."],
    ];
    const ws2 = XLSX.utils.aoa_to_sheet(refRows);
    ws2['!cols'] = [{ wch: 22 }, { wch: 72 }];
    XLSX.utils.book_append_sheet(wb, ws, "Questions");
    XLSX.utils.book_append_sheet(wb, ws2, "Reference");
    XLSX.writeFile(wb, "question_bank_template.xlsx");
  }

  return (
    <div>
      <Card style={{ marginBottom: "18px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px", flexWrap: "wrap", gap: "10px" }}>
          <h2 style={{ fontSize: "15px", fontWeight: 700, color: "var(--text-primary)", margin: 0 }}>📚 Question Banks</h2>
          <button onClick={downloadTemplate}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#818cf8", fontSize: "13px", fontWeight: 600, padding: 0, textDecoration: "underline" }}>
            ⬇ Download Template
          </button>
        </div>

        <div style={{ background: "var(--bg-card-inner)", borderRadius: "10px", padding: "16px", marginBottom: "20px", border: "1px solid var(--border-card)" }}>
          <h3 style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-secondary)", margin: "0 0 14px" }}>Upload Question Bank</h3>
          <div style={{ marginBottom: "12px" }}>
            <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>
              Description <span style={{ color: "#f87171" }}>*</span>
              <span style={{ color: "var(--text-faint)", marginLeft: "6px" }}>{uploadDesc.length}/100</span>
            </label>
            <input
              type="text"
              value={uploadDesc}
              onChange={e => setUploadDesc(e.target.value.slice(0, 100))}
              placeholder="e.g. Java Backend — Fresher batch April 2026"
              style={{ width: "100%", padding: "8px 10px", borderRadius: "8px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px", boxSizing: "border-box" }}
            />
          </div>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end" }}>
            <div>
              <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>XLSX File</label>
              <input ref={fileInputRef} type="file" accept=".xlsx" onChange={handleFileSelect}
                style={{ fontSize: "13px", color: "var(--text-secondary)" }} />
            </div>
            <Btn onClick={handleUpload} disabled={!uploadFile || uploadSubmitting}>
              {uploadSubmitting ? "Uploading…" : "Upload"}
            </Btn>
          </div>
          {uploadFile && !uploadErr && (
            <p style={{ color: "var(--text-secondary)", fontSize: "12px", margin: "8px 0 0" }}>
              📄 {uploadFile.name} ({(uploadFile.size / 1024).toFixed(0)} KB) — ready to upload
            </p>
          )}
          {uploadErr && <p style={{ color: "#f87171", fontSize: "12px", margin: "8px 0 0" }}>{uploadErr}</p>}
          {uploadSuccess && <p style={{ color: "#4ade80", fontSize: "12px", margin: "8px 0 0" }}>✅ {uploadSuccess}</p>}
        </div>

        {loading && <p style={{ color: "var(--text-muted)", fontSize: "13px" }}>Loading…</p>}
        {err && <p style={{ color: "#f87171", fontSize: "13px" }}>{err}</p>}
        {!loading && !err && banks.length === 0 && (
          <p style={{ color: "var(--text-muted)", fontSize: "13px", margin: 0 }}>No question banks uploaded yet.</p>
        )}
        {banks.map(b => {
          const canDelete = canDeleteAll || (role === 'hiring-manager' && b.uploadedBy?.id === currentUser?.userId);
          return (
            <div key={b.fileName} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 14px", background: "var(--bg-card-inner)", borderRadius: "8px", marginBottom: "8px", flexWrap: "wrap", border: "1px solid var(--border-card)" }}>
              <div style={{ flex: 1, minWidth: "150px" }}>
                <div style={{ fontWeight: 700, fontSize: "13px", color: "var(--text-primary)", letterSpacing: "0.03em" }}>📄 {b.fileName}</div>
                {b.description && <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "2px" }}>{b.description}</div>}
                {b.uploadedBy && <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>by {b.uploadedBy.name} [{b.uploadedBy.role}]</div>}
              </div>
              <div style={{ fontSize: "12px", color: "var(--text-secondary)", minWidth: "100px" }}>
                {b.questionCount != null ? `${b.questionCount} questions` : "—"}
              </div>
              <div style={{ fontSize: "11px", color: "var(--text-faint)", minWidth: "130px" }}>{fmtDate(b.uploadedAt)}</div>
              <div style={{ display: "flex", gap: "6px" }}>
                <Btn variant="ghost" style={{ padding: "5px 12px", fontSize: "12px" }} onClick={() => handleDownload(b.fileName)}>⬇ Download</Btn>
                {canDelete && (
                  <Btn variant="danger" style={{ padding: "5px 12px", fontSize: "12px" }} onClick={() => handleDelete(b.fileName)}>🗑 Delete</Btn>
                )}
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

// ── RICH TEXT EDITOR ──
function RichTextEditor({ value, onChange, placeholder = "Start typing...", maxLength = 2500 }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = value || '';
    }
  }, []); // init once on mount; DOM owns content after that

  function exec(cmd) {
    editorRef.current.focus();
    document.execCommand(cmd, false, null);
    onChange(editorRef.current.innerHTML);
  }

  function handleInput() {
    onChange(editorRef.current.innerHTML);
  }

  const textLen = value ? value.replace(/<[^>]*>/g, '').length : 0;
  const isEmpty = !value || value.replace(/<[^>]*>/g, '').trim() === '';

  const btnStyle = { padding: '3px 9px', borderRadius: '5px', border: '1px solid var(--border-card)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px', fontFamily: "'Segoe UI', sans-serif", lineHeight: '1.5' };

  return (
    <div style={{ border: '1px solid var(--border-input)', borderRadius: '10px', overflow: 'hidden' }}>
      <div style={{ display: 'flex', gap: '4px', padding: '6px 10px', background: 'var(--bg-card-inner)', borderBottom: '1px solid var(--border-input)', flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" onMouseDown={e => { e.preventDefault(); exec('bold'); }} style={btnStyle} title="Bold"><b>B</b></button>
        <button type="button" onMouseDown={e => { e.preventDefault(); exec('italic'); }} style={btnStyle} title="Italic"><i>I</i></button>
        <button type="button" onMouseDown={e => { e.preventDefault(); exec('underline'); }} style={btnStyle} title="Underline"><u>U</u></button>
        <div style={{ width: '1px', height: '18px', background: 'var(--border-card)', margin: '0 2px', flexShrink: 0 }} />
        <button type="button" onMouseDown={e => { e.preventDefault(); exec('insertUnorderedList'); }} style={btnStyle} title="Bullet list">• Bullet</button>
        <button type="button" onMouseDown={e => { e.preventDefault(); exec('insertOrderedList'); }} style={btnStyle} title="Numbered list">1. Numbered</button>
        <div style={{ width: '1px', height: '18px', background: 'var(--border-card)', margin: '0 2px', flexShrink: 0 }} />
        <button type="button" onMouseDown={e => { e.preventDefault(); exec('removeFormat'); }} style={{ ...btnStyle, color: '#f87171' }} title="Clear formatting">✕ Clear</button>
        <span style={{ marginLeft: 'auto', color: textLen > maxLength * 0.9 ? '#f87171' : 'var(--text-faint)', fontSize: '11px' }}>{textLen}/{maxLength}</span>
      </div>
      <div style={{ position: 'relative', background: 'var(--bg-input)' }}>
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          className="jd-editor"
          onInput={handleInput}
          style={{ minHeight: '140px', maxHeight: '260px', overflowY: 'auto', padding: '10px 12px', color: 'var(--text-input)', fontSize: '13px', lineHeight: '1.6', fontFamily: "'Segoe UI', sans-serif" }}
        />
        {isEmpty && (
          <div style={{ position: 'absolute', top: '10px', left: '12px', color: 'var(--text-faint)', fontSize: '13px', lineHeight: '1.6', pointerEvents: 'none', fontFamily: "'Segoe UI', sans-serif" }}>
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
}

// ── JOB DESCRIPTION VIEW (renders HTML or plain text, fixed height) ──
export function JobDescriptionView({ content, height = '160px' }) {
  if (!content) return <span style={{ color: 'var(--text-faint)', fontSize: '13px' }}>—</span>;
  const hasHtml = /<[a-z][\s\S]*>/i.test(content);
  const baseStyle = { background: 'var(--bg-card-inner)', borderRadius: '10px', padding: '10px 12px', color: 'var(--text-secondary)', fontSize: '13px', lineHeight: '1.6', height, overflowY: 'auto', boxSizing: 'border-box' };
  let sanitized = DOMPurify.sanitize(content, {
    ALLOWED_TAGS: ['p', 'br', 'b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'a', 'table', 'tr', 'td', 'th', 'thead', 'tbody'],
    ALLOWED_ATTR: ['style', 'href', 'target'],
    FORBID_ATTR: ['onerror', 'onclick', 'onload', 'onmouseover', 'onfocus', 'onchange', 'onblur'],
  });
  sanitized = sanitized.replace(/style="[^"]*"/gi, match => {
    const style = match.match(/style="([^"]*)"/i)?.[1] || '';
    const cleaned = style.split(';').filter(rule => !/(color|background)/i.test(rule)).join(';');
    return cleaned ? `style="${cleaned}"` : '';
  });
  if (hasHtml) return <div className="jd-view" dangerouslySetInnerHTML={{ __html: sanitized }} style={baseStyle} />;
  return <div className="jd-view" style={{ ...baseStyle, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{content}</div>;
}

// ── JOB REQUISITION FORM MODAL ──
function RequisitionFormModal({ onClose, onSaved, editReq = null }) {
  const [jobTitle, setJobTitle] = useState(editReq?.jobTitle || "");
  const [jobDescription, setJobDescription] = useState(editReq?.jobDescription || "");
  const [qualification, setQualification] = useState(editReq?.qualification || "");
  const [jobLocation, setJobLocation] = useState(editReq?.jobLocation || "");
  const [businessJustification, setBusinessJustification] = useState(editReq?.businessJustification || "");
  const [requisitionType, setRequisitionType] = useState(editReq?.requisitionType || "New");
  const [employeeName, setEmployeeName] = useState(editReq?.employeeName || "");
  const [positionCount, setPositionCount] = useState(editReq?.positionCount || 1);
  const [budgetMin, setBudgetMin] = useState(editReq?.budgetMin ?? "");
  const [budgetMax, setBudgetMax] = useState(editReq?.budgetMax ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const inputStyle = { width: "100%", padding: "10px 12px", borderRadius: "10px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px", boxSizing: "border-box" };
  const labelStyle = { color: "var(--text-muted)", fontSize: "12px", margin: "0 0 5px", display: "block" };

  async function handleSubmit() {
    if (!jobTitle.trim()) { setErr("Job title required."); return; }
    if (!jobDescription.replace(/<[^>]*>/g, '').trim()) { setErr("Job description required."); return; }
    if (!qualification.trim()) { setErr("Qualification required."); return; }
    if (!jobLocation.trim()) { setErr("Job location required."); return; }
    if (!businessJustification.trim()) { setErr("Business justification required."); return; }
    if (requisitionType === "Replacement" && !employeeName.trim()) { setErr("Employee name is required for Replacement requisitions."); return; }
    if (!positionCount || positionCount < 1) { setErr("Position count must be at least 1."); return; }
    if (budgetMin === "" || budgetMax === "") { setErr("Budget min and max required."); return; }
    if (Number(budgetMax) < Number(budgetMin)) { setErr("Budget max must be ≥ budget min."); return; }
    setSaving(true); setErr("");
    try {
      const body = { jobTitle: jobTitle.trim(), jobDescription: jobDescription.trim(), qualification: qualification.trim(), jobLocation: jobLocation.trim(), businessJustification: businessJustification.trim(), requisitionType, positionCount: Number(positionCount), budgetMin: Number(budgetMin), budgetMax: Number(budgetMax), ...(requisitionType === "Replacement" && { employeeName: employeeName.trim() }) };
      if (editReq) await apiPatch(`/api/job-requisitions/${editReq.id || editReq._id}`, body);
      else await apiPost("/api/job-requisitions", body);
      onSaved();
      onClose();
    } catch (e) { if (shouldShowError(e)) setErr(e.message || "Save failed."); }
    setSaving(false);
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "var(--bg-modal)", border: "1px solid var(--border-card)", borderRadius: "14px", padding: "28px", width: "90vw", maxWidth: "600px", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.5)" }}>
        <h3 style={{ color: "var(--text-primary)", fontSize: "16px", margin: "0 0 20px" }}>{editReq ? "✏️ Edit Requisition" : "📝 New Job Requisition"}</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Job Title *</label>
            <input value={jobTitle} onChange={e => setJobTitle(e.target.value)} placeholder="e.g. Senior Java Developer" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Requisition Type *</label>
            <select value={requisitionType} onChange={e => { setRequisitionType(e.target.value); if (e.target.value === "New") setEmployeeName(""); }} style={inputStyle}>
              <option value="New">New</option>
              <option value="Replacement">Replacement</option>
            </select>
          </div>
          {requisitionType === "Replacement" && (
            <div>
              <label style={labelStyle}>Employee Being Replaced *</label>
              <input value={employeeName} onChange={e => setEmployeeName(e.target.value)} placeholder="Full name of departing employee" style={inputStyle} />
            </div>
          )}
          <div>
            <label style={labelStyle}>Position Count *</label>
            <input type="number" min="1" value={positionCount} onChange={e => setPositionCount(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Budget Min (₹ LPA) *</label>
            <input type="number" min="0" value={budgetMin} onChange={e => setBudgetMin(e.target.value)} placeholder="e.g. 800000" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Budget Max (₹ LPA) *</label>
            <input type="number" min="0" value={budgetMax} onChange={e => setBudgetMax(e.target.value)} placeholder="e.g. 1200000" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Job Location *</label>
            <input value={jobLocation} onChange={e => setJobLocation(e.target.value)} placeholder="e.g. Bangalore / Remote" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Qualification *</label>
            <input value={qualification} onChange={e => setQualification(e.target.value)} placeholder="e.g. B.Tech CS, 5+ yrs Java" style={inputStyle} />
          </div>
        </div>

        <div style={{ marginBottom: "12px" }}>
          <label style={labelStyle}>Job Description *</label>
          <RichTextEditor value={jobDescription} onChange={setJobDescription} placeholder="Describe the role, responsibilities, required skills..." />
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label style={labelStyle}>Business Justification *</label>
          <textarea value={businessJustification} onChange={e => setBusinessJustification(e.target.value.slice(0, 2000))} rows={3} maxLength={2000} placeholder="Why is this position needed?" style={{ ...inputStyle, resize: "vertical", minHeight: "72px", lineHeight: "1.6", fontFamily: "'Segoe UI', sans-serif" }} />
        </div>

        {err && <p style={{ color: "#f87171", fontSize: "13px", marginBottom: "12px" }}>{err}</p>}
        <div style={{ display: "flex", gap: "8px" }}>
          <Btn onClick={handleSubmit} disabled={saving} style={{ padding: "9px 20px", fontSize: "13px" }}>{saving ? "Saving..." : editReq ? "Update Requisition" : "Submit Requisition"}</Btn>
          <Btn variant="ghost" onClick={onClose} style={{ padding: "9px 18px", fontSize: "13px" }}>Cancel</Btn>
        </div>
      </div>
    </div>
  );
}

// ── JOB REQUISITION APPROVAL MODAL ──
function RequisitionApprovalModal({ req, currentUser, onClose, onReviewed }) {
  const [action, setAction] = useState("approved");
  const [comments, setComments] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const isHOD = currentUser?.role === "hod";

  async function handleSubmit() {
    if (action === "rejected" && !comments.trim()) { setErr("Comments are required when rejecting."); return; }
    setSaving(true); setErr("");
    try {
      await apiPost(`/api/job-requisitions/${req.id || req._id}/review`, { action, comments: comments.trim() });
      onReviewed();
      onClose();
    } catch (e) { if (shouldShowError(e)) setErr(e.message || "Action failed."); }
    setSaving(false);
  }

  const inputStyle = { width: "100%", padding: "10px 12px", borderRadius: "10px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px", boxSizing: "border-box" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "var(--bg-modal)", border: "1px solid var(--border-card)", borderRadius: "14px", padding: "28px", width: "90vw", maxWidth: "520px", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.5)" }}>
        <h3 style={{ color: "var(--text-primary)", fontSize: "16px", margin: "0 0 6px" }}>{isHOD ? "HOD Review" : "Executive Review"}</h3>
        <p style={{ color: "var(--text-muted)", fontSize: "13px", margin: "0 0 16px" }}>{req.jobTitle}</p>

        <div style={{ background: "var(--bg-card-inner)", borderRadius: "10px", padding: "12px", marginBottom: "16px" }}>
          {[["Type", req.requisitionType], ...(req.employeeName ? [["Replacing", req.employeeName]] : []), ["Positions", req.positionCount], ["Location", req.jobLocation], ["Qualification", req.qualification], ["Budget", `₹${(req.budgetMin||0).toLocaleString()} – ₹${(req.budgetMax||0).toLocaleString()}`]].map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid var(--border-card)" }}>
              <span style={{ color: "var(--text-muted)", fontSize: "12px" }}>{k}</span>
              <span style={{ color: "var(--text-secondary)", fontSize: "12px", fontWeight: 600 }}>{v}</span>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: "14px" }}>
          <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 5px", fontWeight: 600 }}>Job Description</p>
          <JobDescriptionView content={req.jobDescription} height="160px" />
        </div>

        <div style={{ marginBottom: "16px" }}>
          <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 5px", fontWeight: 600 }}>Business Justification</p>
          <div style={{ background: "var(--bg-card-inner)", borderRadius: "10px", padding: "10px 12px", color: "var(--text-secondary)", fontSize: "13px", lineHeight: "1.6", whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "120px", overflowY: "auto" }}>{req.businessJustification}</div>
        </div>

        {!isHOD && req.hodReview && (
          <div style={{ background: "var(--bg-card-inner)", borderRadius: "10px", padding: "12px", marginBottom: "16px", border: `1px solid ${req.hodReview.action === "approved" ? "rgba(74,222,128,0.35)" : "rgba(248,113,113,0.35)"}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
              <span style={{ color: "var(--text-secondary)", fontSize: "12px", fontWeight: 700 }}>HOD Review — {req.hodReview.reviewedBy?.name}</span>
              <span style={{ color: req.hodReview.action === "approved" ? "#4ade80" : "#f87171", fontSize: "11px", fontWeight: 700 }}>{req.hodReview.action === "approved" ? "✅ Approved" : "❌ Rejected"}</span>
            </div>
            <p style={{ color: "var(--text-muted)", fontSize: "11px", margin: "0 0 4px" }}>{fmtDate(req.hodReview.reviewedAt)}</p>
            {req.hodReview.comments && <p style={{ color: "var(--text-secondary)", fontSize: "12px", margin: 0, fontStyle: "italic" }}>"{req.hodReview.comments}"</p>}
          </div>
        )}

        <div style={{ display: "flex", gap: "10px", marginBottom: "14px" }}>
          {["approved", "rejected"].map(opt => (
            <div key={opt} onClick={() => setAction(opt)} style={{ flex: 1, padding: "12px", borderRadius: "10px", cursor: "pointer", border: `2px solid ${action === opt ? (opt === "approved" ? "#4ade80" : "#f87171") : "var(--border-card)"}`, background: action === opt ? (opt === "approved" ? "rgba(74,222,128,0.1)" : "rgba(248,113,113,0.1)") : "var(--bg-card-inner)", textAlign: "center" }}>
              <div style={{ fontWeight: 700, fontSize: "13px", color: action === opt ? (opt === "approved" ? "#4ade80" : "#f87171") : "var(--text-secondary)" }}>{opt === "approved" ? "✅ Approve" : "❌ Reject"}</div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label style={{ color: "var(--text-muted)", fontSize: "12px", display: "block", marginBottom: "5px" }}>Comments {action === "rejected" ? "*" : "(optional)"}</label>
          <textarea value={comments} onChange={e => setComments(e.target.value.slice(0, 500))} rows={3} placeholder={action === "rejected" ? "Reason for rejection..." : "Optional feedback..."} style={{ ...inputStyle, resize: "vertical", minHeight: "72px", lineHeight: "1.6", fontFamily: "'Segoe UI', sans-serif" }} />
          <p style={{ color: "var(--text-faint)", fontSize: "11px", margin: "3px 0 0", textAlign: "right" }}>{comments.length}/500</p>
        </div>

        {err && <p style={{ color: "#f87171", fontSize: "13px", marginBottom: "12px" }}>{err}</p>}
        <div style={{ display: "flex", gap: "8px" }}>
          <Btn onClick={handleSubmit} disabled={saving} style={{ padding: "9px 20px", fontSize: "13px", background: action === "approved" ? "linear-gradient(135deg,#4ade80,#22c55e)" : "linear-gradient(135deg,#f87171,#ef4444)" }}>{saving ? "Submitting..." : action === "approved" ? "Approve" : "Reject"}</Btn>
          <Btn variant="ghost" onClick={onClose} style={{ padding: "9px 18px", fontSize: "13px" }}>Cancel</Btn>
        </div>
      </div>
    </div>
  );
}

// ── JOB REQUISITION DETAIL MODAL ──
function RequisitionDetailModal({ reqId, currentUser, onClose, onUpdated, onViewReport, onCreateTest }) {
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const canClose = ["hr", "hr-admin", "superuser"].includes(currentUser?.role);
  const canCreateTest = ["hr", "hr-admin", "superuser"].includes(currentUser?.role);
  const canSeeBudget = ["hiring-manager", "hr", "hr-admin", "hod", "executive"].includes(currentUser?.role);

  async function loadDetail() {
    setLoading(true);
    try {
      const data = await apiGet(`/api/job-requisitions/${reqId}`);
      setReq(data);
    } catch (e) { if (shouldShowError(e)) setErr(e.message || "Failed to load."); }
    setLoading(false);
  }

  useEffect(() => { loadDetail(); }, [reqId]);

  async function handleManualClose() {
    if (!window.confirm("Manually close this requisition? This marks it as filled/done.")) return;
    try {
      await apiPost(`/api/job-requisitions/${reqId}/close`, {});
      onUpdated(); loadDetail();
    } catch (e) { if (shouldShowError(e)) alert(e.message || "Close failed."); }
  }

  const rowStyle = { display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid var(--border-card)" };

  if (!req && loading) return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ color: "var(--text-muted)", fontSize: "14px" }}>Loading...</div>
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "var(--bg-modal)", border: "1px solid var(--border-card)", borderRadius: "14px", padding: "28px", width: "90vw", maxWidth: "680px", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.5)" }}>
        {err && <p style={{ color: "#f87171", fontSize: "13px", marginBottom: "12px" }}>{err}</p>}
        {req && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px", flexWrap: "wrap", gap: "8px" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "4px" }}>
                  <h3 style={{ color: "var(--text-primary)", fontSize: "17px", margin: 0 }}>{req.jobTitle}</h3>
                  {req.jrfNumber && <span style={{ color: "var(--nav-active-color)", fontSize: "12px", fontFamily: "monospace", background: "rgba(129,140,248,0.18)", padding: "3px 8px", borderRadius: "6px", border: "1px solid rgba(129,140,248,0.4)", fontWeight: 700 }}>{req.jrfNumber}</span>}
                </div>
                <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: 0 }}>Raised by {req.createdBy?.name} · {fmtDate(req.createdAt)}</p>
              </div>
              <ReqStatusBadge status={req.status} />
            </div>

            {/* Positions progress */}
            <div style={{ background: "var(--bg-card-inner)", borderRadius: "10px", padding: "14px", marginBottom: "16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <span style={{ color: "var(--text-secondary)", fontSize: "13px", fontWeight: 600 }}>Position Fill Progress</span>
                <span style={{ color: "var(--text-muted)", fontSize: "12px" }}>{req.filledPositions || 0} / {req.positionCount} filled · {Math.max(0, req.positionCount - (req.filledPositions || 0))} pending</span>
              </div>
              <div style={{ background: "var(--bg-progress)", borderRadius: "6px", height: "8px", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${Math.min(100, ((req.filledPositions || 0) / req.positionCount) * 100)}%`, background: "linear-gradient(90deg,#4ade80,#22c55e)", borderRadius: "6px", transition: "width 0.4s" }} />
              </div>
            </div>

            {/* Details */}
            <div style={{ background: "var(--bg-card-inner)", borderRadius: "10px", padding: "14px", marginBottom: "16px" }}>
              {[["Type", req.requisitionType], ...(req.employeeName ? [["Replacing", req.employeeName]] : []), ["Location", req.jobLocation], ["Qualification", req.qualification], ...(canSeeBudget ? [["Budget Range", `₹${(req.budgetMin||0).toLocaleString()} – ₹${(req.budgetMax||0).toLocaleString()}`]] : [])].map(([k, v]) => (
                <div key={k} style={rowStyle}><span style={{ color: "var(--text-muted)", fontSize: "12px" }}>{k}</span><span style={{ color: "var(--text-secondary)", fontSize: "12px", fontWeight: 600 }}>{v}</span></div>
              ))}
            </div>

            <div style={{ marginBottom: "16px" }}>
              <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 6px", fontWeight: 600 }}>Job Description</p>
              <JobDescriptionView content={req.jobDescription} height="200px" />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 6px", fontWeight: 600 }}>Business Justification</p>
              <div style={{ background: "var(--bg-card-inner)", borderRadius: "10px", padding: "12px", color: "var(--text-secondary)", fontSize: "13px", lineHeight: "1.6", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{req.businessJustification}</div>
            </div>

            {/* Approval history */}
            {(req.hodReview || req.executiveReview) && (
              <div style={{ marginBottom: "16px" }}>
                <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 8px", fontWeight: 600 }}>Approval History</p>
                {req.hodReview && (
                  <div style={{ background: "var(--bg-card-inner)", borderRadius: "10px", padding: "12px", marginBottom: "8px", border: `1px solid ${req.hodReview.action === "approved" ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                      <span style={{ color: "var(--text-secondary)", fontSize: "12px", fontWeight: 700 }}>HOD Review — {req.hodReview.reviewedBy?.name}</span>
                      <span style={{ color: req.hodReview.action === "approved" ? "#4ade80" : "#f87171", fontSize: "11px", fontWeight: 700 }}>{req.hodReview.action === "approved" ? "✅ Approved" : "❌ Rejected"}</span>
                    </div>
                    <p style={{ color: "var(--text-muted)", fontSize: "11px", margin: "0 0 4px" }}>{fmtDate(req.hodReview.reviewedAt)}</p>
                    {req.hodReview.comments && <p style={{ color: "var(--text-secondary)", fontSize: "12px", margin: 0, fontStyle: "italic" }}>"{req.hodReview.comments}"</p>}
                  </div>
                )}
                {req.executiveReview && (
                  <div style={{ background: "var(--bg-card-inner)", borderRadius: "10px", padding: "12px", border: `1px solid ${req.executiveReview.action === "approved" ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                      <span style={{ color: "var(--text-secondary)", fontSize: "12px", fontWeight: 700 }}>Executive Review — {req.executiveReview.reviewedBy?.name}</span>
                      <span style={{ color: req.executiveReview.action === "approved" ? "#4ade80" : "#f87171", fontSize: "11px", fontWeight: 700 }}>{req.executiveReview.action === "approved" ? "✅ Approved" : "❌ Rejected"}</span>
                    </div>
                    <p style={{ color: "var(--text-muted)", fontSize: "11px", margin: "0 0 4px" }}>{fmtDate(req.executiveReview.reviewedAt)}</p>
                    {req.executiveReview.comments && <p style={{ color: "var(--text-secondary)", fontSize: "12px", margin: 0, fontStyle: "italic" }}>"{req.executiveReview.comments}"</p>}
                  </div>
                )}
              </div>
            )}

            {/* Linked candidates */}
            {req.linkedTests && req.linkedTests.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 8px", fontWeight: 600 }}>Linked Candidates ({req.linkedTests.length})</p>
                <div style={{ border: "1px solid var(--border-card)", borderRadius: "10px", overflow: "hidden" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: "0", background: "var(--bg-card-inner)", padding: "8px 12px", borderBottom: "1px solid var(--border-card)" }}>
                    {["Candidate", "Code", "Status", "Created"].map(h => <span key={h} style={{ color: "var(--text-faint)", fontSize: "11px", fontWeight: 700, textTransform: "uppercase" }}>{h}</span>)}
                  </div>
                  {req.linkedTests.map(t => (
                    <div key={t.code} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: "0", padding: "9px 12px", borderBottom: "1px solid var(--border-card)", alignItems: "center" }}>
                      <span style={{ color: "var(--text-primary)", fontSize: "13px", fontWeight: 500 }}>{t.candidateName}</span>
                      <span
                        onClick={() => onViewReport && onViewReport(t)}
                        style={{ color: "var(--nav-active-color)", fontSize: "12px", fontFamily: "monospace", cursor: onViewReport ? "pointer" : "default", textDecoration: onViewReport ? "underline" : "none" }}
                        title={onViewReport ? "View report" : undefined}
                      >{t.code}</span>
                      <StatusBadge status={t.status} />
                      <span style={{ color: "var(--text-muted)", fontSize: "11px" }}>{fmtDate(t.createdAt)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {req.linkedTests && req.linkedTests.length === 0 && ["pending_hr_action", "under_screening"].includes(req.status) && (
              <p style={{ color: "var(--text-faint)", fontSize: "13px", marginBottom: "16px" }}>No candidates linked yet. HR can create tests linked to this requisition from the Create Test tab.</p>
            )}

            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {canCreateTest && onCreateTest && ["pending_hr_action", "under_screening"].includes(req.status) && (
                <Btn onClick={() => { onCreateTest(req); onClose(); }} style={{ padding: "8px 16px", fontSize: "12px", background: "linear-gradient(135deg,#38bdf8,#0ea5e9)" }}>+ Create Test</Btn>
              )}
              {canClose && !["closed", "new", "rejected_hod", "rejected_executive"].includes(req.status) && (
                <Btn variant="ghost" onClick={handleManualClose} style={{ padding: "8px 16px", fontSize: "12px" }}>Close Requisition</Btn>
              )}
              <Btn variant="ghost" onClick={onClose} style={{ padding: "8px 16px", fontSize: "12px" }}>Close</Btn>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── REQUISITIONS VIEW ──
function RequisitionsView({ currentUser, onViewReport, onCreateTest }) {
  const role = currentUser?.role;
  const isHM = role === "hiring-manager";
  const isHOD = role === "hod";
  const isExecutive = role === "executive";
  const isHR = ["hr", "hr-admin", "superuser"].includes(role);

  const [reqs, setReqs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState("all");
  const [jrfSearch, setJrfSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editReq, setEditReq] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalReq, setApprovalReq] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailReqId, setDetailReqId] = useState(null);

  async function loadReqs(p = page, ps = pageSize, sf = statusFilter, jrf = jrfSearch) {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, pageSize: ps });
      if (sf !== "all") params.set("status", sf);
      if (jrf.trim()) params.set("jrfNumber", jrf.trim().toUpperCase());
      const data = await apiGet(`/api/job-requisitions?${params}`);
      setReqs(data.requisitions || []);
      setTotal(data.total || 0);
    } catch (e) { logger.error("Failed to load requisitions", { component: "requisitions", error: e.message }); }
    setLoading(false);
  }

  useEffect(() => { loadReqs(page, pageSize, statusFilter, jrfSearch); }, [page, pageSize, statusFilter, jrfSearch]);

  function openApproval(req) { setApprovalReq(req); setShowApprovalModal(true); }
  function openDetail(req) { setDetailReqId(String(req.id || req._id)); setShowDetailModal(true); }
  function openEdit(req) { setEditReq(req); setShowFormModal(true); }

  const selStyle = { padding: "8px 12px", borderRadius: "8px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px" };

  const canApproveHOD = isHOD;
  const canApproveExec = isExecutive;

  // Default status filter based on role
  const statusOptions = [
    { value: "all", label: "All Statuses" },
    { value: "new", label: "New" },
    { value: "approved_hod", label: "Approved - HOD" },
    { value: "rejected_hod", label: "Rejected by HOD" },
    { value: "rejected_executive", label: "Rejected by Executive" },
    { value: "pending_hr_action", label: "Pending HR Action" },
    { value: "under_screening", label: "Under Screening" },
    { value: "closed", label: "Closed" },
  ];

  return (
    <div>
      {showFormModal && (
        <RequisitionFormModal
          editReq={editReq}
          onClose={() => { setShowFormModal(false); setEditReq(null); }}
          onSaved={() => loadReqs(1, pageSize, statusFilter)}
        />
      )}
      {showApprovalModal && approvalReq && (
        <RequisitionApprovalModal
          req={approvalReq}
          currentUser={currentUser}
          onClose={() => { setShowApprovalModal(false); setApprovalReq(null); }}
          onReviewed={() => { loadReqs(page, pageSize, statusFilter); }}
        />
      )}
      {showDetailModal && detailReqId && (
        <RequisitionDetailModal
          reqId={detailReqId}
          currentUser={currentUser}
          onClose={() => { setShowDetailModal(false); setDetailReqId(null); }}
          onUpdated={() => loadReqs(page, pageSize, statusFilter, jrfSearch)}
          onViewReport={onViewReport}
          onCreateTest={onCreateTest}
        />
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
        <h2 style={{ color: "var(--text-primary)", fontSize: "18px", margin: 0 }}>📝 Job Requisitions</h2>
        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
          <input type="text" value={jrfSearch} onChange={e => { setJrfSearch(e.target.value.toUpperCase()); setPage(1); }}
            placeholder="Search JRF#…" style={{ ...selStyle, width: "160px", fontFamily: "monospace" }} />
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={selStyle}>
            {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {isHM && (
            <Btn onClick={() => { setEditReq(null); setShowFormModal(true); }} style={{ padding: "8px 16px", fontSize: "13px" }}>+ New Requisition</Btn>
          )}
        </div>
      </div>

      {/* Role-based guidance */}
      {isHOD && (
        <div style={{ background: "var(--bg-card-inner)", border: "1px solid rgba(245,158,11,0.35)", borderRadius: "10px", padding: "10px 14px", marginBottom: "14px", fontSize: "13px", color: "#f59e0b" }}>
          Showing requisitions pending your review. Approve or reject to advance the workflow.
        </div>
      )}
      {isExecutive && (
        <div style={{ background: "var(--bg-card-inner)", border: "1px solid rgba(129,140,248,0.35)", borderRadius: "10px", padding: "10px 14px", marginBottom: "14px", fontSize: "13px", color: "var(--nav-active-color)" }}>
          Showing HOD-approved requisitions awaiting your final approval.
        </div>
      )}

      {loading && <p style={{ color: "var(--text-faint)", fontSize: "13px" }}>Loading...</p>}
      {!loading && reqs.length === 0 && (
        <div style={{ background: "var(--bg-card-inner)", borderRadius: "12px", padding: "32px", textAlign: "center", border: "1px solid var(--border-card)" }}>
          <p style={{ color: "var(--text-faint)", fontSize: "14px", margin: 0 }}>No requisitions found.</p>
          {isHM && <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "8px 0 0" }}>Click <strong>+ New Requisition</strong> to raise your first job requisition.</p>}
        </div>
      )}

      {!loading && reqs.map(req => {
        const rid = String(req.id || req._id);
        const pending = Math.max(0, req.positionCount - (req.filledPositions || 0));
        const canReview = (canApproveHOD && req.status === "new") || (canApproveExec && req.status === "approved_hod");
        const canEdit = isHM && ["new", "rejected_hod", "rejected_executive"].includes(req.status);
        return (
          <div key={rid} style={{ background: "var(--bg-card-inner)", borderRadius: "12px", padding: "14px 16px", marginBottom: "10px", border: "1px solid var(--border-card)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px", flexWrap: "wrap" }}>
                  <span style={{ color: "var(--text-primary)", fontWeight: 700, fontSize: "14px" }}>{req.jobTitle}</span>
                  {req.jrfNumber && <span style={{ color: "var(--nav-active-color)", fontSize: "11px", fontFamily: "monospace", background: "rgba(129,140,248,0.18)", padding: "2px 6px", borderRadius: "4px", border: "1px solid rgba(129,140,248,0.4)" }}>{req.jrfNumber}</span>}
                  <Tag bg="rgba(129,140,248,0.18)" color="var(--nav-active-color)">{req.requisitionType}</Tag>
                  <ReqStatusBadge status={req.status} />
                </div>
                <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
                  <span style={{ color: "var(--text-muted)", fontSize: "12px" }}>📍 {req.jobLocation}</span>
                  <span style={{ color: "var(--text-muted)", fontSize: "12px" }}>👥 {req.positionCount} position{req.positionCount !== 1 ? "s" : ""} · {pending} open</span>
                  {req.linkedTestCount > 0 && <span style={{ color: "var(--nav-active-color)", fontSize: "12px" }}>🔗 {req.linkedTestCount} candidate{req.linkedTestCount !== 1 ? "s" : ""}</span>}
                  <span style={{ color: "var(--text-faint)", fontSize: "12px" }}>{fmtDate(req.createdAt)}</span>
                </div>
                {["rejected_hod", "rejected_executive"].includes(req.status) && (
                  <div style={{ marginTop: "6px" }}>
                    {req.status === "rejected_hod" && req.hodReview?.comments && <p style={{ color: "#f87171", fontSize: "12px", margin: 0 }}>HOD comment: "{req.hodReview.comments}"</p>}
                    {req.status === "rejected_executive" && req.executiveReview?.comments && <p style={{ color: "#f87171", fontSize: "12px", margin: 0 }}>Executive comment: "{req.executiveReview.comments}"</p>}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {canReview && (
                  <Btn onClick={() => openApproval(req)} style={{ padding: "7px 14px", fontSize: "12px", background: "linear-gradient(135deg,#f59e0b,#d97706)" }}>Review</Btn>
                )}
                {canEdit && (
                  <Btn variant="ghost" onClick={() => openEdit(req)} style={{ padding: "7px 12px", fontSize: "12px" }}>✏️ Edit</Btn>
                )}
                <Btn variant="ghost" onClick={() => openDetail(req)} style={{ padding: "7px 12px", fontSize: "12px" }}>View</Btn>
              </div>
            </div>
          </div>
        );
      })}

      {total > pageSize && (
        <Pagination page={page} totalPages={Math.ceil(total / pageSize)} pageSize={pageSize} onPage={p => setPage(p)} onPageSize={ps => { setPageSize(ps); setPage(1); }} />
      )}
    </div>
  );
}

// ── HR DASHBOARD ──
function HRDashboard({ onLogout, currentUser }) {
  const canWrite = ["hr", "hr-admin", "superuser"].includes(currentUser?.role);
  const canDelete = ["hr", "hr-admin", "superuser"].includes(currentUser?.role);
  const [view, setView] = useState(currentUser?.role === "executive" ? "requisitions" : "dashboard");
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [reportFromView, setReportFromView] = useState("reports");
  const [myPendingAssignments, setMyPendingAssignments] = useState([]);
  const [cName, setCName] = useState("");
  const [cTrack, setCTrack] = useState(null);
  const [cExp, setCExp] = useState(null);
  const [cHiringManager, setCHiringManager] = useState(null);
  const [cEventName, setCEventName] = useState("");
  const [cRequisitionId, setCRequisitionId] = useState("");
  const [cSkipOnlineTest, setCSkipOnlineTest] = useState(false);
  const [openRequisitions, setOpenRequisitions] = useState([]);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");
  const [createdCode, setCreatedCode] = useState(null);
  const [availableBanks, setAvailableBanks] = useState([]);
  const [selectedBankFile, setSelectedBankFile] = useState(null);
  const [copyNotification, setCopyNotification] = useState(null);
  const [aiError] = useState(null);
  // Hiring managers and events (for dropdowns)
  const [hiringManagers, setHiringManagers] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  // For hiring-manager role: filter events to only those associated with the logged-in HM
  const eventsForFilter = currentUser?.role === 'hiring-manager'
    ? allEvents.filter(ev => ev.hiringManager && String(ev.hiringManager.id) === String(currentUser.id))
    : allEvents;
  const [archiving, setArchiving] = useState(false);
  const [archiveConfirmCodes, setArchiveConfirmCodes] = useState(null);
  // Bulk upload state
  const [createMode, setCreateMode] = useState("single");
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkRows, setBulkRows] = useState(null);
  const [bulkErr, setBulkErr] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkHiringManager, setBulkHiringManager] = useState(null);
  const [bulkEventName, setBulkEventName] = useState("");
  const [bulkRequisitionId, setBulkRequisitionId] = useState("");
  const [bulkSkipOnlineTest, setBulkSkipOnlineTest] = useState(false);
  const [cResumeFile, setCResumeFile] = useState(null);
  // Reports filtering
  const [reportStatusFilter, setReportStatusFilter] = useState("all");
  const [reportDateFilter, setReportDateFilter] = useState("");
  const [reportResultFilter, setReportResultFilter] = useState("all");
  const [reportFinalVerdictFilter, setReportFinalVerdictFilter] = useState("all");
  const [reportNameFilter, setReportNameFilter] = useState("");
  const [reportHMFilter, setReportHMFilter] = useState("all");
  const [reportEventFilter, setReportEventFilter] = useState("");
  const [reportMandatoryFilter, setReportMandatoryFilter] = useState("all");
  const [reportJRFFilter, setReportJRFFilter] = useState("");
  const [reportPage, setReportPage] = useState(1);
  const [reportPageSize, setReportPageSize] = useState(20);
  const [reportsResult, setReportsResult] = useState({ tests: [], total: 0 });
  const [reportsLoading, setReportsLoading] = useState(false);
  const [dashActivityPage, setDashActivityPage] = useState(1);
  const [dashActivityPageSize, setDashActivityPageSize] = useState(20);
  const [dashResult, setDashResult] = useState({ tests: [], total: 0 });
  const [dashLoading, setDashLoading] = useState(false);
  const [myAllAssignedCodes, setMyAllAssignedCodes] = useState(new Set());

  async function loadTests() {
    setLoading(true);
    try {
      const d = await apiGet("/api/tests");
      setTests(d);
      logger.debug('Loaded all tests', {
        component: 'hr-dashboard',
        testCount: d.length
      });
    } catch (e) {
      logger.error('Failed to load tests', {
        component: 'hr-dashboard',
        error: e.message
      });
      // Do NOT clear existing tests on a transient error — keep the last good list visible
    }
    setLoading(false);
  }

  function loadMyAssignments() {
    apiGet("/api/rounds/my-assignments")
      .then(d => {
        const all = d || [];
        setMyPendingAssignments(all.filter(r => r.status === 'pending'));
        setMyAllAssignedCodes(new Set(all.map(r => r.testCode)));
      })
      .catch(() => {});
  }

  useEffect(() => {
    loadTests();
    apiGet("/api/users/hiring-managers").then(setHiringManagers).catch(() => {});
    apiGet("/api/events").then(setAllEvents).catch(() => {});
    loadMyAssignments();
    if (canWrite) apiGet("/api/question-banks").then(setAvailableBanks).catch(() => {});
  }, []);

  useEffect(() => {
    if (view === "dashboard") loadMyAssignments();
  }, [view]);

  async function doArchive(codes) {
    setArchiveConfirmCodes(null);
    setArchiving(true);
    try {
      await apiPost("/api/tests/archive", { codes });
      await loadTests();
    } catch (e) {
      alert("Archive failed: " + e.message);
    }
    setArchiving(false);
  }

  function buildWithScoresUrl(params) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') q.set(k, v); });
    return `/api/tests/with-scores?${q.toString()}`;
  }

  async function loadReports(page, pSize) {
    setReportsLoading(true);
    try {
      const data = await apiGet(buildWithScoresUrl({
        archived: false,
        status: reportStatusFilter !== 'all' ? reportStatusFilter : undefined,
        date: reportDateFilter || undefined,
        name: reportNameFilter.trim() || undefined,
        result: reportResultFilter !== 'all' ? reportResultFilter : undefined,
        mandatory: reportMandatoryFilter !== 'all' ? reportMandatoryFilter : undefined,
        hmId: reportHMFilter !== 'all' ? reportHMFilter : undefined,
        event: reportEventFilter || undefined,
        finalVerdict: reportFinalVerdictFilter !== 'all' ? reportFinalVerdictFilter : undefined,
        jrfNumber: reportJRFFilter.trim() || undefined,
        page: page ?? reportPage,
        pageSize: pSize ?? reportPageSize,
      }));
      setReportsResult({ tests: data.tests || [], total: data.total || 0 });
    } catch (e) {
      logger.error('Failed to load reports', { component: 'hr-dashboard', error: e.message });
    }
    setReportsLoading(false);
  }

  async function loadDashActivity(page, pSize) {
    setDashLoading(true);
    try {
      const data = await apiGet(buildWithScoresUrl({
        archived: false,
        mine: true,
        page: page ?? dashActivityPage,
        pageSize: pSize ?? dashActivityPageSize,
      }));
      setDashResult({ tests: data.tests || [], total: data.total || 0 });
    } catch (e) {
      logger.error('Failed to load dashboard activity', { component: 'hr-dashboard', error: e.message });
    }
    setDashLoading(false);
  }

  // Fetch reports whenever view or any filter/page changes (debounce name filter)
  useEffect(() => {
    if (view !== 'reports') return;
    const delay = reportNameFilter ? 400 : 0;
    const timer = setTimeout(() => loadReports(reportPage, reportPageSize), delay);
    return () => clearTimeout(timer);
  }, [view, reportPage, reportPageSize, reportStatusFilter, reportDateFilter, reportResultFilter, reportFinalVerdictFilter, reportNameFilter, reportMandatoryFilter, reportHMFilter, reportEventFilter, reportJRFFilter]);

  // Reset to page 1 whenever any filter changes (except page itself)
  useEffect(() => { setReportPage(1); }, [reportStatusFilter, reportDateFilter, reportResultFilter, reportFinalVerdictFilter, reportNameFilter, reportMandatoryFilter, reportHMFilter, reportEventFilter, reportJRFFilter]);

  // Fetch dashboard activity whenever view or pagination changes
  useEffect(() => {
    if (view !== 'dashboard') return;
    loadDashActivity(dashActivityPage, dashActivityPageSize);
  }, [view, dashActivityPage, dashActivityPageSize]);

  // Load open requisitions for test creation linkage (HR/hr-admin/superuser only)
  useEffect(() => {
    if (view !== 'create' || !canWrite) return;
    apiGet("/api/job-requisitions?status=pending_hr_action&pageSize=100")
      .then(d => setOpenRequisitions(d.requisitions || []))
      .catch(() => {});
    // Also load under_screening ones
    apiGet("/api/job-requisitions?status=under_screening&pageSize=100")
      .then(d => setOpenRequisitions(prev => {
        const ids = new Set(prev.map(r => String(r.id || r._id)));
        const extra = (d.requisitions || []).filter(r => !ids.has(String(r.id || r._id)));
        return [...prev, ...extra];
      }))
      .catch(() => {});
  }, [view, canWrite]);

  async function createTest() {
    if (!cName.trim()) { setErr("Enter candidate name."); return; }
    if (!cSkipOnlineTest && !cTrack) { setErr("Select a track."); return; }
    if (!cSkipOnlineTest && !cExp) { setErr("Select experience level."); return; }
    if (!cHiringManager) { setErr("Select a hiring manager."); return; }
    setCreating(true); setErr("");
    const code = genCode();
    const test = {
      code, candidateName: cName.trim(),
      trackId: cSkipOnlineTest ? null : cTrack.id,
      trackName: cSkipOnlineTest ? null : cTrack.name,
      trackIcon: cSkipOnlineTest ? null : cTrack.icon,
      trackTopics: cSkipOnlineTest ? null : cTrack.topics,
      expId: cSkipOnlineTest ? null : cExp.id,
      expLabel: cSkipOnlineTest ? null : cExp.label,
      complexity: cSkipOnlineTest ? null : cExp.complexity,
      hasQuestionBank: cSkipOnlineTest ? false : !!selectedBankFile,
      questionBankFile: cSkipOnlineTest ? null : (selectedBankFile || null),
      createdAt: new Date().toISOString(), status: "pending",
      hiringManager: cHiringManager || null,
      eventName: cEventName.trim() || null,
      jobRequisitionId: cRequisitionId || null,
      skipOnlineTest: cSkipOnlineTest,
    };

    logger.info('Creating test', {
      component: 'hr-dashboard',
      testCode: code,
      candidateName: cName.trim(),
      track: cTrack?.name,
      experience: cExp?.label,
      hasBank: !!selectedBankFile
    });

    try {
      await apiPost("/api/tests", test);
    } catch (ex) {
      logger.error('Failed to create test', {
        component: 'hr-dashboard',
        testCode: code,
        error: ex.message
      });
      setErr("Failed to save test. Is the backend running?");
      setCreating(false);
      return;
    }
    if (cResumeFile) {
      try {
        const fd = new FormData();
        fd.append('cv', cResumeFile);
        await apiPostFile(`/api/tests/${code}/cv`, fd);
      } catch (e) {
        logger.warn('CV upload failed after test creation', { component: 'hr-dashboard', testCode: code, error: e.message });
      }
    }
    setCreatedCode(code); setCreating(false); setSelectedBankFile(null); setCResumeFile(null); loadTests();
  }

  function buildSkipReport(t) {
    return {
      candidateName: t.candidateName, trackIcon: t.trackIcon, trackName: t.trackName,
      expLabel: t.expLabel, complexity: t.complexity, code: t.code,
      questions: [], createdAt: t.createdAt, completedAt: t.completedAt,
      hiringManager: t.hiringManager, eventName: t.eventName, skipOnlineTest: true,
    };
  }

  async function openReportByCode(testCode) {
    try {
      const rep = await apiGet(`/api/reports/${testCode}`);
      if (rep) {
        const t = tests.find(t => t.code === testCode);
        setSelectedReport(t ? { candidateName: t.candidateName, trackIcon: t.trackIcon, trackName: t.trackName, expLabel: t.expLabel, complexity: t.complexity, code: t.code, ...rep, createdAt: t.createdAt, completedAt: t.completedAt || rep.completedAt } : rep);
        setSelectedTest(t || null);
        setReportFromView('reports');
        setView('reportDetail');
      }
    } catch (e) {
      const t = tests.find(t => t.code === testCode);
      if (t?.skipOnlineTest) {
        setSelectedReport(buildSkipReport(t));
        setSelectedTest(t);
        setReportFromView('reports');
        setView('reportDetail');
      } else if (shouldShowError(e)) {
        alert('Failed to load report: ' + e.message);
      }
    }
  }

  function createTestForRequisition(req) {
    const rid = String(req.id || req._id);
    setOpenRequisitions(prev => prev.some(r => String(r.id || r._id) === rid) ? prev : [...prev, req]);
    setCRequisitionId(rid);
    setBulkRequisitionId(rid);
    if (req.createdBy?.id) {
      const hm = hiringManagers.find(h => String(h.id) === String(req.createdBy.id));
      if (hm) { setCHiringManager(hm); setBulkHiringManager(hm); }
    }
    setCreateMode("single");
    setCreatedCode(null);
    setView("create");
  }

  async function deleteTest(code) {
    if (!window.confirm("Delete this test and report?")) return;
    logger.info('Deleting test', {
      component: 'hr-dashboard',
      testCode: code
    });
    try {
      await apiDelete(`/api/tests/${code}`);
    } catch (e) {
      logger.error('Failed to delete test', {
        component: 'hr-dashboard',
        testCode: code,
        error: e.message
      });
    }
    loadTests(); if (view === "reportDetail") setView("reports");
  }

  function handleNav(v) {
    logger.debug('Navigation to HR view', {
      component: 'hr-dashboard',
      view: v
    });
    setView(v); setCreatedCode(null); setErr("");
    setSelectedBankFile(null);
    setBulkFile(null); setBulkRows(null); setBulkErr(""); setBulkResults(null); setCreateMode("single");
    setCHiringManager(null); setCEventName("");
    setBulkHiringManager(null); setBulkEventName(""); setBulkRequisitionId(""); setBulkSkipOnlineTest(false);
    setCSkipOnlineTest(false);
    if (v === "events") apiGet("/api/events").then(setAllEvents).catch(() => {});
  }

  async function handleBulkFile(e) {
    const file = e.target.files[0]; if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) { setBulkErr("Only .xlsx files are accepted."); return; }
    if (file.size > 5 * 1024 * 1024) { setBulkErr("File too large. Max 5 MB."); return; }
    setBulkFile(file); setBulkErr(""); setBulkLoading(true); setBulkRows(null); setBulkResults(null);
    try {
      const rows = await parseBulkXlsx(file);
      setBulkRows(rows);
    } catch (ex) {
      setBulkErr(ex.message);
    }
    setBulkLoading(false);
  }

  async function submitBulkTests() {
    if (!bulkRows || bulkRows.length === 0) return;
    const bulkEventHasHM = bulkEventName && allEvents.find(ev => ev.name === bulkEventName)?.hiringManager?.id;
    if (!bulkHiringManager && !bulkEventHasHM) { setBulkErr("Select a hiring manager or choose an event that has a hiring manager."); return; }
    setBulkSubmitting(true); setBulkResults(null);
    try {
      const data = await apiPost("/api/bulk-tests", { rows: bulkRows, hiringManager: bulkHiringManager || null, eventName: bulkEventName.trim() || null, jobRequisitionId: bulkRequisitionId || null, skipOnlineTest: bulkSkipOnlineTest });
      setBulkResults(data.results || []);
      loadTests();
    } catch (ex) {
      setBulkErr("Failed to submit: " + ex.message);
    }
    setBulkSubmitting(false);
  }

  function downloadBulkTemplate() {
    const wb = XLSX.utils.book_new();

    // Sheet 1 — data area + inline reference below
    const dataRows = [
      // ── Headers ──
      ["Candidate Name", "Skill Track", "Experience Level", "Question Bank"],
      // ── Example rows ──
      ["John Doe", "Java, SQL, NoSQL & Microservices", "1–3 Years", "java_questions.xlsx"],
      ["Jane Smith", "Python, LangChain, LangGraph & NoSQL", "Fresher", ""],
      // ── Blank separator ──
      [],
      // ── Inline reference: Skill Track options ──
      ["--- VALID VALUES ---", "", "", ""],
      ["Skill Track (use exactly as shown):", "", "", ""],
      ["", "Java, SQL, NoSQL & Microservices", "", ""],
      ["", "Python, LangChain, LangGraph & NoSQL", "", ""],
      ["", "Agentic AI, LLM & AI Product Build", "", ""],
      ["", "Angular, HTML, JavaScript & CSS", "", ""],
      // ── Blank ──
      [],
      // ── Inline reference: Experience Level options ──
      ["Experience Level (use exactly as shown):", "", "", ""],
      ["", "Fresher", "", ""],
      ["", "1–3 Years", "", ""],
      ["", "4–6 Years", "", ""],
      ["", "7–10 Years", "", ""],
      // ── Blank ──
      [],
      // ── Question Bank note ──
      ["Question Bank:", "Filename of .xlsx file in the server's question-banks/ folder.", "", ""],
      ["", "e.g. java_senior.xlsx  |  Leave blank to use AI-generated questions.", "", ""],
    ];
    const ws = XLSX.utils.aoa_to_sheet(dataRows);
    ws['!cols'] = [{ wch: 38 }, { wch: 44 }, { wch: 18 }, { wch: 34 }];
    XLSX.utils.book_append_sheet(wb, ws, "Bulk Upload");
    XLSX.writeFile(wb, "bulk_test_template.xlsx");
  }

  if (view === "reportDetail" && selectedReport) {
    return (
      <ReportDetail
          report={selectedReport}
          onBack={() => setView(reportFromView)}
          onDelete={() => deleteTest(selectedReport.code)}
          canDelete={canDelete}
          hideReEvaluate={reportFromView === "history"}
          testStatus={selectedTest?.status}
          currentUser={currentUser}
          test={selectedTest}
          allEvents={allEvents}
          onTestStatusChange={(code, newStatus) => {
            if (newStatus) {
              setSelectedTest(t => t ? { ...t, status: newStatus } : t);
              setTests(prev => prev.map(t => t.code === code ? { ...t, status: newStatus } : t));
            } else {
              // Reload test to get latest status after round assignment/verdict
              apiGet("/api/tests").then(d => {
                setTests(d);
                const updated = d.find(t => t.code === code);
                if (updated) setSelectedTest(updated);
              }).catch(() => {});
            }
          }}
        />
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-page)", padding: "24px", fontFamily: "'Segoe UI',sans-serif" }}>
      <div style={{ maxWidth: "780px", margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "22px" }}>
          <Logo height={40} />
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {currentUser && (
              <div style={{ textAlign: "right" }}>
                <div style={{ color: "var(--text-secondary)", fontSize: "13px", fontWeight: 600 }}>{currentUser.name}</div>
                <div style={{ color: "var(--text-faint)", fontSize: "11px", textTransform: "uppercase" }}>{currentUser.role}</div>
              </div>
            )}
            <Btn variant="ghost" style={{ padding: "8px 14px", fontSize: "13px" }} onClick={onLogout}>🚪 Logout</Btn>
          </div>
        </div>
        <APIStatus />
        <NavTabs active={view} onChange={handleNav} role={currentUser?.role} />
        
        {/* AI availability banner */}
        {aiError && (
          <div style={{
            background: "#fbbf24",
            color: "#1f2937",
            padding: "10px 16px",
            borderRadius: "8px",
            marginBottom: "16px",
            fontSize: "13px"
          }}>
            ⚠️ AI unavailable: {aiError}. Using fallback questions/feedback.
          </div>
        )}
        
        {/* Notification Toast */}
        {copyNotification && (
          <div style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            background: "#22c55e",
            color: "#fff",
            padding: "12px 16px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            fontSize: "13px",
            animation: "fadeInOut 2.5s ease-in-out",
            zIndex: 1000
          }}>
            {copyNotification}
          </div>
        )}

        {/* DASHBOARD */}
        {view === "dashboard" && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "12px", marginBottom: "22px" }}>
              {[
                ["📋", "Total Tests", tests.length, "#818cf8"],
                ["✅", "Completed", tests.filter(t => t.status === "completed").length, "#4ade80"],
                ["⏳", "Pending", tests.filter(t => t.status === "pending").length, "#fbbf24"],
                ["🔄", "Interview In-Progress", tests.filter(t => ["under_review","round1_assigned","round2_assigned","hr_round_assigned","round3_assigned"].includes(t.status)).length, "#38bdf8"]
              ].map(([icon, label, val, color]) => (
                <Card key={label} style={{ textAlign: "center", padding: "18px" }}>
                  <div style={{ fontSize: "22px" }}>{icon}</div>
                  <div style={{ color, fontWeight: 800, fontSize: "26px", margin: "6px 0 2px" }}>{val}</div>
                  <div style={{ color: "var(--text-muted)", fontSize: "12px" }}>{label}</div>
                </Card>
              ))}
            </div>
            <Card style={{ marginBottom: "18px", borderColor: "rgba(245,158,11,0.3)" }}>
              <h3 style={{ color: "var(--text-primary)", fontSize: "14px", margin: "0 0 12px" }}>
                📝 Assigned to Me {myPendingAssignments.length > 0 ? `— ${myPendingAssignments.length} pending` : ""}
              </h3>
              {myPendingAssignments.length === 0
                ? <p style={{ color: "var(--text-muted)", fontSize: "13px", margin: 0 }}>No pending interview assignments.</p>
                : myPendingAssignments.map(a => (
                  <div key={a._id} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 12px", background: "var(--bg-card-inner)", borderRadius: "8px", marginBottom: "8px", flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 600, fontSize: "13px", color: "var(--text-primary)", flex: 1 }}>{a.candidateName}</span>
                    <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>{a.trackName}</span>
                    <span style={{ fontSize: "12px", color: "var(--text-secondary)" }}>{a.roundLabel}</span>
                    <Btn style={{ padding: "5px 12px", fontSize: "12px" }} onClick={() => openReportByCode(a.testCode)}>View Report</Btn>
                  </div>
                ))
              }
            </Card>
            <Card>
              {(() => {
                const { tests: activityPaged, total: activityTotal } = dashResult;
                const activityTotalPages = Math.max(1, Math.ceil(activityTotal / dashActivityPageSize));
                const assignedStatuses = new Set(['round1_assigned', 'round2_assigned', 'hr_round_assigned', 'round3_assigned']);
                return (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                      <h3 style={{ color: "var(--text-primary)", fontSize: "14px", margin: 0 }}>🕐 Past Activities</h3>
                      <span style={{ color: "var(--text-faint)", fontSize: "12px" }}>{activityTotal} record{activityTotal !== 1 ? "s" : ""}</span>
                    </div>
                    {dashLoading && <p style={{ color: "var(--text-muted)", fontSize: "13px" }}>Loading...</p>}
                    {!dashLoading && activityTotal === 0 && <p style={{ color: "var(--text-muted)", fontSize: "13px" }}>No activity yet.</p>}
                    {!dashLoading && activityPaged.map((t, idx) => (
                      <div key={t.code} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: idx < activityPaged.length - 1 ? "1px solid var(--border-card)" : "none", gap: "8px", flexWrap: "wrap" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ color: "var(--text-secondary)", fontSize: "13px", fontWeight: 600, margin: "0 0 2px" }}>
                            👤 {t.candidateName} {t.hasQuestionBank ? <span style={{ color: "#fbbf24", fontSize: "11px" }}>· 📂 Bank</span> : null}
                          </p>
                          <p style={{ color: "var(--text-muted)", fontSize: "11px", margin: "0 0 1px" }}>{t.trackIcon} {t.trackName} · <span style={{ color: "var(--nav-active-color)", fontFamily: "monospace" }}>{t.code}</span></p>
                          <p style={{ color: "var(--text-faint)", fontSize: "11px", margin: 0 }}>
                            🗓 {fmtDate(t.createdAt)}{t.completedAt ? ` · ✅ ${fmtDate(t.completedAt)}` : ""}
                            {t.hiringManager?.name && <span style={{ color: "var(--text-faint)" }}> · 👤 {t.hiringManager.name}</span>}
                            {assignedStatuses.has(t.status) && t.currentAssignee?.name && (
                              <span style={{ color: "#f59e0b" }}> · 🎯 {t.currentAssignee.name}</span>
                            )}
                          </p>
                        </div>
                        <StatusBadge status={t.status} />
                      </div>
                    ))}
                    {!dashLoading && activityTotal > 0 && (
                      <Pagination page={dashActivityPage} totalPages={activityTotalPages} pageSize={dashActivityPageSize}
                        onPage={p => { setDashActivityPage(p); loadDashActivity(p, dashActivityPageSize); }}
                        onPageSize={ps => { setDashActivityPageSize(ps); setDashActivityPage(1); loadDashActivity(1, ps); }} />
                    )}
                  </>
                );
              })()}
            </Card>
          </div>
        )}

        {/* CREATE TEST */}
        {view === "create" && (
          <div style={{ maxWidth: "560px" }}>
            {/* Mode tabs */}
            <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
              {[["single", "➕ Single Test"], ["bulk", "📋 Bulk Upload"]].map(([m, label]) => (
                <button key={m} onClick={() => { setCreateMode(m); setCreatedCode(null); setBulkResults(null); setBulkFile(null); setBulkRows(null); setBulkErr(""); }}
                  style={{ padding: "9px 20px", borderRadius: "10px", border: `2px solid ${createMode === m ? "#818cf8" : "var(--border-input)"}`, background: createMode === m ? "rgba(129,140,248,0.15)" : "var(--bg-card-inner)", color: createMode === m ? "#818cf8" : "var(--text-muted)", fontWeight: 600, fontSize: "13px", cursor: "pointer" }}>
                  {label}
                </button>
              ))}
            </div>

            {/* BULK UPLOAD */}
            {createMode === "bulk" && (
              <Card>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                  <h3 style={{ color: "var(--text-primary)", margin: 0, fontSize: "16px" }}>📋 Bulk Create Tests</h3>
                  <button onClick={downloadBulkTemplate}
                    style={{ padding: "7px 14px", borderRadius: "8px", border: "1px solid rgba(251,191,36,0.4)", background: "rgba(251,191,36,0.08)", color: "#fbbf24", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}>
                    ⬇ Download Template
                  </button>
                </div>
                <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 14px", lineHeight: 1.6 }}>
                  Upload an Excel (.xlsx) file with columns: <strong style={{ color: "var(--text-secondary)" }}>Candidate Name, Skill Track, Experience Level, Question Bank</strong>. The Question Bank column should contain the filename (e.g. <code style={{ color: "#818cf8" }}>java_senior.xlsx</code>) of an .xlsx file already present on the server in the <code style={{ color: "#818cf8" }}>question-banks/</code> folder. Leave blank to use AI-generated questions.
                </p>
                <div style={{ background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: "10px", padding: "14px", marginBottom: "14px" }}>
                  <input type="file" accept=".xlsx" onChange={handleBulkFile} style={{ display: "none" }} id="bulk-upload" />
                  <label htmlFor="bulk-upload" style={{ display: "inline-block", padding: "8px 18px", borderRadius: "8px", border: "1px solid rgba(99,102,241,0.4)", color: "#818cf8", fontSize: "13px", cursor: "pointer", fontWeight: 600 }}>
                    {bulkFile ? `📄 ${bulkFile.name}` : "📤 Choose Bulk XLSX"}
                  </label>
                  {bulkLoading && <p style={{ color: "#94a3b8", fontSize: "12px", margin: "8px 0 0" }}>Parsing file...</p>}
                  {bulkErr && <p style={{ color: "#f87171", fontSize: "12px", margin: "8px 0 0" }}>❌ {bulkErr}</p>}
                  {bulkRows && !bulkResults && (
                    <p style={{ color: "#4ade80", fontSize: "12px", margin: "8px 0 0" }}>✅ {bulkRows.length} row{bulkRows.length !== 1 ? "s" : ""} parsed. Ready to create.</p>
                  )}
                </div>

                {/* Link to Job Requisition for bulk */}
                {canWrite && openRequisitions.length > 0 && (
                  <div style={{ background: "rgba(56,189,248,0.06)", border: "1px solid rgba(56,189,248,0.2)", borderRadius: "10px", padding: "14px", marginBottom: "14px" }}>
                    <p style={{ color: "#38bdf8", fontSize: "13px", fontWeight: 600, margin: "0 0 8px" }}>
                      📝 Link to Job Requisition <span style={{ color: "var(--text-muted)", fontWeight: 400, fontSize: "12px" }}>(Optional — applies to all)</span>
                    </p>
                    <select
                      value={bulkRequisitionId}
                      onChange={e => {
                        const rid = e.target.value;
                        setBulkRequisitionId(rid);
                        if (rid) {
                          const selReq = openRequisitions.find(r => String(r.id || r._id) === rid);
                          if (selReq?.createdBy?.id) {
                            const hm = hiringManagers.find(h => String(h.id) === String(selReq.createdBy.id));
                            if (hm) setBulkHiringManager(hm);
                          }
                        }
                      }}
                      style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px" }}
                    >
                      <option value="">— No requisition —</option>
                      {openRequisitions.map(r => {
                        const rid = String(r.id || r._id);
                        const pending = Math.max(0, r.positionCount - (r.filledPositions || 0));
                        return <option key={rid} value={rid}>{r.jrfNumber ? `[${r.jrfNumber}] ` : ""}{r.jobTitle} — {r.requisitionType} · {pending} open position{pending !== 1 ? "s" : ""}</option>;
                      })}
                    </select>
                    {bulkRequisitionId && (() => {
                      const selReq = openRequisitions.find(r => String(r.id || r._id) === bulkRequisitionId);
                      return selReq?.jrfNumber ? (
                        <p style={{ color: "#38bdf8", fontSize: "12px", margin: "6px 0 0", fontFamily: "monospace" }}>JRF#: <strong>{selReq.jrfNumber}</strong></p>
                      ) : null;
                    })()}
                  </div>
                )}

                {/* Hiring Manager + Event Name for bulk */}
                {(() => {
                  const bulkSelectedEvent = bulkEventName && allEvents.find(ev => ev.name === bulkEventName);
                  const bulkEventHasHM = bulkSelectedEvent?.hiringManager?.id;
                  const bulkReqLocked = !!bulkRequisitionId;
                  const bulkHMLocked = !!bulkEventHasHM || bulkReqLocked;
                  return (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "14px" }}>
                      <div>
                        <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 5px" }}>📅 Event Name <span style={{ color: "var(--text-faint)" }}>(applies to all)</span></p>
                        <select value={bulkEventName} onChange={e => {
                          const val = e.target.value;
                          setBulkEventName(val);
                          if (val) {
                            const ev = allEvents.find(ev => ev.name === val);
                            if (ev?.hiringManager?.id) {
                              const hm = hiringManagers.find(h => String(h.id) === String(ev.hiringManager.id));
                              if (hm) setBulkHiringManager(hm);
                            }
                          }
                        }}
                          style={{ width: "100%", padding: "9px 10px", borderRadius: "8px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px" }}>
                          <option value="">— No Event —</option>
                          {allEvents.map(ev => <option key={ev.id} value={ev.name}>{ev.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 5px" }}>
                          👤 Hiring Manager{!bulkHMLocked && " *"} <span style={{ color: "var(--text-faint)" }}>(applies to all)</span>
                        </p>
                        <select value={bulkHiringManager?.id || ""}
                          onChange={e => { const hm = hiringManagers.find(h => String(h.id) === e.target.value); setBulkHiringManager(hm || null); }}
                          disabled={bulkHMLocked}
                          style={{ width: "100%", padding: "9px 10px", borderRadius: "8px", border: `1px solid ${!bulkHiringManager && !bulkHMLocked ? "rgba(248,113,113,0.4)" : "var(--border-input)"}`, background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px", opacity: bulkHMLocked ? 0.6 : 1, cursor: bulkHMLocked ? "not-allowed" : "default" }}>
                          <option value="">— Select Hiring Manager —</option>
                          {hiringManagers.map(hm => <option key={hm.id} value={String(hm.id)}>{hm.name}</option>)}
                        </select>
                        {bulkEventHasHM && <p style={{ color: "var(--text-muted)", fontSize: "11px", margin: "4px 0 0" }}>Auto-filled from event</p>}
                        {!bulkEventHasHM && bulkReqLocked && <p style={{ color: "var(--text-muted)", fontSize: "11px", margin: "4px 0 0" }}>Auto-filled from requisition</p>}
                      </div>
                    </div>
                  );
                })()}

                {/* Skip Online Test for bulk */}
                <div style={{ background: "rgba(248,113,113,0.06)", border: `1px solid ${bulkSkipOnlineTest ? "rgba(248,113,113,0.5)" : "rgba(248,113,113,0.2)"}`, borderRadius: "10px", padding: "12px 14px", marginBottom: "14px" }}>
                  <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", cursor: "pointer" }}>
                    <input type="checkbox" checked={bulkSkipOnlineTest} onChange={e => setBulkSkipOnlineTest(e.target.checked)}
                      style={{ marginTop: "2px", accentColor: "#f87171", width: "15px", height: "15px", flexShrink: 0 }} />
                    <div>
                      <span style={{ color: "#f87171", fontSize: "13px", fontWeight: 700 }}>🚫 Skip Online Test</span>
                      <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "3px 0 0", lineHeight: 1.5 }}>
                        All candidates in this batch skip the AI interview and are moved directly to Round 1 assessment.
                      </p>
                    </div>
                  </label>
                </div>

                {/* Preview table */}
                {bulkRows && !bulkResults && (
                  <div style={{ marginBottom: "14px", overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                      <thead>
                        <tr>{["#", "Candidate Name", "Skill Track", "Experience", "Question Bank"].map(h => (
                          <th key={h} style={{ padding: "6px 8px", textAlign: "left", color: "var(--text-muted)", borderBottom: "1px solid var(--border-card)", whiteSpace: "nowrap" }}>{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody>
                        {bulkRows.slice(0, 10).map((r, i) => (
                          <tr key={i} style={{ background: i % 2 === 0 ? "var(--bg-card-inner)" : "transparent" }}>
                            <td style={{ padding: "5px 8px", color: "var(--text-faint)" }}>{i + 1}</td>
                            <td style={{ padding: "5px 8px", color: "var(--text-secondary)" }}>{r.candidateName}</td>
                            <td style={{ padding: "5px 8px", color: "var(--text-muted)" }}>{r.skillTrack || <span style={{ color: "#f87171" }}>—</span>}</td>
                            <td style={{ padding: "5px 8px", color: "var(--text-muted)" }}>{r.experienceLevel || <span style={{ color: "#f87171" }}>—</span>}</td>
                            <td style={{ padding: "5px 8px", color: "#818cf8" }}>{r.questionBankFile || <span style={{ color: "var(--text-faint)" }}>AI</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {bulkRows.length > 10 && <p style={{ color: "var(--text-faint)", fontSize: "11px", margin: "6px 0 0" }}>+{bulkRows.length - 10} more rows…</p>}
                  </div>
                )}

                {/* Results after submission */}
                {bulkResults && (
                  <div style={{ marginBottom: "14px" }}>
                    <div style={{ background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.25)", borderRadius: "8px", padding: "10px 14px", marginBottom: "10px" }}>
                      <span style={{ color: "#4ade80", fontWeight: 700, fontSize: "13px" }}>
                        ✅ {bulkResults.filter(r => r.success).length} created
                      </span>
                      {bulkResults.filter(r => !r.success).length > 0 && (
                        <span style={{ color: "#f87171", fontWeight: 700, fontSize: "13px", marginLeft: "14px" }}>
                          ❌ {bulkResults.filter(r => !r.success).length} failed
                        </span>
                      )}
                    </div>
                    <div style={{ maxHeight: "220px", overflowY: "auto" }}>
                      {bulkResults.map((r, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "10px", padding: "6px 8px", borderRadius: "6px", marginBottom: "4px", background: r.success ? "rgba(74,222,128,0.06)" : "rgba(248,113,113,0.06)" }}>
                          <span style={{ fontSize: "12px", flexShrink: 0 }}>{r.success ? "✅" : "❌"}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ color: "var(--text-secondary)", fontSize: "12px", fontWeight: 600 }}>{r.candidateName}</span>
                            {r.success && <span style={{ color: "#818cf8", fontSize: "12px", marginLeft: "10px", fontFamily: "monospace", fontWeight: 700 }}>{r.code}</span>}
                            {!r.success && <p style={{ color: "#f87171", fontSize: "11px", margin: "2px 0 0" }}>{r.error}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                      {bulkResults.some(r => r.success) && (
                        <button onClick={() => {
                          const wb = XLSX.utils.book_new();
                          const rows = [
                            ["Candidate Name", "Test Code", "Status"],
                            ...bulkResults.map(r => [r.candidateName, r.success ? r.code : "", r.success ? "Created" : `Failed: ${r.error}`])
                          ];
                          const ws = XLSX.utils.aoa_to_sheet(rows);
                          ws['!cols'] = [{ wch: 28 }, { wch: 16 }, { wch: 50 }];
                          XLSX.utils.book_append_sheet(wb, ws, "Test Codes");
                          XLSX.writeFile(wb, `test_codes_${new Date().toISOString().slice(0, 10)}.xlsx`);
                        }}
                          style={{ flex: 1, padding: "9px", borderRadius: "8px", border: "1px solid rgba(74,222,128,0.35)", background: "rgba(74,222,128,0.1)", color: "#4ade80", fontWeight: 600, fontSize: "13px", cursor: "pointer" }}>
                          ⬇ Download Test Codes
                        </button>
                      )}
                      <button onClick={() => { setBulkFile(null); setBulkRows(null); setBulkResults(null); setBulkErr(""); }}
                        style={{ flex: 1, padding: "9px", borderRadius: "8px", border: "1px solid rgba(99,102,241,0.3)", background: "rgba(99,102,241,0.1)", color: "#818cf8", fontWeight: 600, fontSize: "13px", cursor: "pointer" }}>
                        Upload Another File
                      </button>
                    </div>
                  </div>
                )}

                {bulkRows && !bulkResults && (
                  <Btn style={{ width: "100%" }} onClick={submitBulkTests} disabled={bulkSubmitting}>
                    {bulkSubmitting ? "Creating tests…" : `Create ${bulkRows.length} Test${bulkRows.length !== 1 ? "s" : ""} →`}
                  </Btn>
                )}
              </Card>
            )}

            {createMode === "single" && !createdCode && (
              <Card>
                <h3 style={{ color: "var(--text-primary)", margin: "0 0 16px", fontSize: "16px" }}>➕ Create Candidate Test</h3>

                {/* 1. Candidate Name */}
                <p style={{ color: "var(--text-secondary)", fontSize: "13px", fontWeight: 600, margin: "0 0 8px" }}>👤 Candidate Name</p>
                <Input value={cName} onChange={e => setCName(e.target.value)} placeholder="Enter candidate full name..." style={{ marginBottom: "16px" }} />

                {/* 2. Candidate Resume */}
                <div style={{ background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.2)", borderRadius: "12px", padding: "16px", marginBottom: "16px" }}>
                  <p style={{ color: "#4ade80", fontSize: "13px", fontWeight: 600, margin: "0 0 8px" }}>
                    📄 Candidate Resume <span style={{ color: "var(--text-muted)", fontWeight: 400, fontSize: "12px" }}>(Optional — PDF / DOC / DOCX, max 5MB)</span>
                  </p>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={e => setCResumeFile(e.target.files?.[0] || null)}
                    style={{ color: "var(--text-secondary)", fontSize: "13px", width: "100%" }}
                  />
                  {cResumeFile && (
                    <p style={{ color: "#4ade80", fontSize: "12px", margin: "6px 0 0" }}>
                      Selected: <strong>{cResumeFile.name}</strong> ({(cResumeFile.size / 1024).toFixed(0)} KB)
                      <span onClick={() => setCResumeFile(null)} style={{ color: "#f87171", marginLeft: "10px", cursor: "pointer" }}>✕ Remove</span>
                    </p>
                  )}
                </div>

                {/* 3. Link to Job Requisition */}
                {canWrite && openRequisitions.length > 0 && (
                  <div style={{ background: "rgba(56,189,248,0.06)", border: "1px solid rgba(56,189,248,0.2)", borderRadius: "12px", padding: "16px", marginBottom: "16px" }}>
                    <p style={{ color: "#38bdf8", fontSize: "13px", fontWeight: 600, margin: "0 0 8px" }}>
                      📝 Link to Job Requisition <span style={{ color: "var(--text-muted)", fontWeight: 400, fontSize: "12px" }}>(Optional)</span>
                    </p>
                    <select
                      value={cRequisitionId}
                      onChange={e => {
                        const rid = e.target.value;
                        setCRequisitionId(rid);
                        if (rid) {
                          const selReq = openRequisitions.find(r => String(r.id || r._id) === rid);
                          if (selReq?.createdBy?.id) {
                            const hm = hiringManagers.find(h => String(h.id) === String(selReq.createdBy.id));
                            if (hm) setCHiringManager(hm);
                          }
                        }
                      }}
                      style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px" }}
                    >
                      <option value="">— No requisition —</option>
                      {openRequisitions.map(r => {
                        const rid = String(r.id || r._id);
                        const pending = Math.max(0, r.positionCount - (r.filledPositions || 0));
                        return <option key={rid} value={rid}>{r.jrfNumber ? `[${r.jrfNumber}] ` : ""}{r.jobTitle} — {r.requisitionType} · {pending} open position{pending !== 1 ? "s" : ""}</option>;
                      })}
                    </select>
                    {cRequisitionId && (() => {
                      const selReq = openRequisitions.find(r => String(r.id || r._id) === cRequisitionId);
                      return selReq?.jrfNumber ? (
                        <p style={{ color: "#38bdf8", fontSize: "12px", margin: "6px 0 0", fontFamily: "monospace" }}>JRF#: <strong>{selReq.jrfNumber}</strong></p>
                      ) : null;
                    })()}
                  </div>
                )}

                {/* 4. Event Name + Hiring Manager */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" }}>
                  <div>
                    <p style={{ color: "var(--text-secondary)", fontSize: "13px", fontWeight: 600, margin: "0 0 8px" }}>📅 Event Name</p>
                    <select value={cEventName} onChange={e => {
                      const val = e.target.value;
                      setCEventName(val);
                      if (val) {
                        const ev = allEvents.find(ev => ev.name === val);
                        if (ev?.hiringManager?.id) {
                          const hm = hiringManagers.find(h => String(h.id) === String(ev.hiringManager.id));
                          if (hm) setCHiringManager(hm);
                        }
                      }
                    }}
                      style={{ width: "100%", padding: "10px 12px", borderRadius: "10px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px" }}>
                      <option value="">— No Event —</option>
                      {allEvents.map(ev => <option key={ev.id} value={ev.name}>{ev.name}</option>)}
                    </select>
                  </div>
                  <div>
                    {(() => {
                      const selectedEvent = cEventName && allEvents.find(ev => ev.name === cEventName);
                      const eventHasHM = selectedEvent?.hiringManager?.id;
                      const reqLocked = !!cRequisitionId;
                      const isLocked = !!eventHasHM || reqLocked;
                      return (
                        <>
                          <p style={{ color: "var(--text-secondary)", fontSize: "13px", fontWeight: 600, margin: "0 0 8px" }}>👤 Hiring Manager *</p>
                          <select value={cHiringManager?.id || ""}
                            onChange={e => { const hm = hiringManagers.find(h => String(h.id) === e.target.value); setCHiringManager(hm || null); }}
                            disabled={isLocked}
                            style={{ width: "100%", padding: "10px 12px", borderRadius: "10px", border: `1px solid ${!cHiringManager ? "rgba(248,113,113,0.5)" : "var(--border-input)"}`, background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px", opacity: isLocked ? 0.6 : 1, cursor: isLocked ? "not-allowed" : "default" }}>
                            <option value="">— Select Hiring Manager —</option>
                            {hiringManagers.map(hm => <option key={hm.id} value={String(hm.id)}>{hm.name}</option>)}
                          </select>
                          {reqLocked && <p style={{ color: "var(--text-muted)", fontSize: "11px", margin: "4px 0 0" }}>Auto-filled from requisition</p>}
                          {!reqLocked && eventHasHM && <p style={{ color: "var(--text-muted)", fontSize: "11px", margin: "4px 0 0" }}>Auto-filled from event</p>}
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* 5. Skip Online Test */}
                <div style={{ background: "rgba(248,113,113,0.06)", border: `1px solid ${cSkipOnlineTest ? "rgba(248,113,113,0.5)" : "rgba(248,113,113,0.2)"}`, borderRadius: "12px", padding: "14px 16px", marginBottom: "16px" }}>
                  <label style={{ display: "flex", alignItems: "flex-start", gap: "12px", cursor: "pointer" }}>
                    <input type="checkbox" checked={cSkipOnlineTest} onChange={e => setCSkipOnlineTest(e.target.checked)}
                      style={{ marginTop: "2px", accentColor: "#f87171", width: "16px", height: "16px", flexShrink: 0 }} />
                    <div>
                      <span style={{ color: "#f87171", fontSize: "13px", fontWeight: 700 }}>🚫 Skip Online Test</span>
                      <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "3px 0 0", lineHeight: 1.5 }}>
                        Candidate skips the AI interview. The test is created as completed and the candidate is moved directly to Round 1 assessment.
                      </p>
                    </div>
                  </label>
                </div>

                {/* 6–8. Skill Track, Experience Level, Question Bank — greyed out when skip is on */}
                <div style={{ opacity: cSkipOnlineTest ? 0.4 : 1, pointerEvents: cSkipOnlineTest ? "none" : "auto", transition: "opacity 0.2s" }}>
                  {cSkipOnlineTest && (
                    <p style={{ color: "#f87171", fontSize: "12px", margin: "0 0 10px", textAlign: "center" }}>
                      Not required when online test is skipped
                    </p>
                  )}

                  <p style={{ color: "var(--text-secondary)", fontSize: "13px", fontWeight: 600, margin: "0 0 8px" }}>🎯 Skill Track</p>
                  {TRACKS.map(t => (
                    <div key={t.id} onClick={() => setCTrack(t)} style={{ padding: "10px 14px", borderRadius: "10px", marginBottom: "6px", cursor: "pointer", border: `2px solid ${cTrack?.id === t.id ? "#818cf8" : "var(--border-card)"}`, background: cTrack?.id === t.id ? "rgba(129,140,248,0.12)" : "var(--bg-card-inner)", color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: "10px" }}>
                      <span style={{ fontSize: "16px" }}>{t.icon}</span><span style={{ fontSize: "13px", fontWeight: 500 }}>{t.name}</span>
                    </div>
                  ))}

                  <p style={{ color: "var(--text-secondary)", fontSize: "13px", fontWeight: 600, margin: "16px 0 8px" }}>📅 Experience Level</p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "20px" }}>
                    {EXPERIENCE_LEVELS.map(e => (
                      <div key={e.id} onClick={() => setCExp(e)} style={{ padding: "10px", borderRadius: "10px", cursor: "pointer", border: `2px solid ${cExp?.id === e.id ? e.color : "var(--border-card)"}`, background: cExp?.id === e.id ? `${e.color}22` : "var(--bg-card-inner)", color: "var(--text-secondary)", textAlign: "center" }}>
                        <div style={{ fontWeight: 700, fontSize: "13px" }}>{e.label}</div>
                        <div style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "capitalize" }}>{e.complexity}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.2)", borderRadius: "12px", padding: "16px", marginBottom: "16px" }}>
                    <p style={{ color: "#fbbf24", fontSize: "13px", fontWeight: 600, margin: "0 0 8px" }}>
                      📂 Question Bank <span style={{ color: "var(--text-muted)", fontWeight: 400, fontSize: "12px" }}>(Optional)</span>
                    </p>
                    <select
                      value={selectedBankFile || ""}
                      onChange={e => setSelectedBankFile(e.target.value || null)}
                      style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px" }}
                    >
                      <option value="">— No question bank (AI generates questions) —</option>
                      {availableBanks.map(b => (
                        <option key={b.fileName} value={b.fileName}>
                          {b.fileName}{b.description ? ` — ${b.description}` : ""}{b.questionCount != null ? ` (${b.questionCount} questions)` : ""}
                        </option>
                      ))}
                    </select>
                    {availableBanks.length === 0 && (
                      <p style={{ color: "var(--text-faint)", fontSize: "11px", margin: "6px 0 0" }}>
                        No question banks available. Upload one in the Question Banks tab.
                      </p>
                    )}
                  </div>
                </div>

                {err && <p style={{ color: "#f87171", fontSize: "13px", marginBottom: "10px" }}>{err}</p>}
                <Btn style={{ width: "100%" }} onClick={createTest} disabled={creating}>
                  {creating ? "Creating..." : "Generate Test Code →"}
                </Btn>
              </Card>
            )}

            {createMode === "single" && createdCode && (
              <Card>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "40px", marginBottom: "10px" }}>🎉</div>
                  <h3 style={{ color: "var(--text-primary)", margin: "0 0 6px" }}>Test Created!</h3>
                  <p style={{ color: "var(--text-muted)", fontSize: "13px", margin: "0 0 20px" }}>Share this code with <strong style={{ color: "var(--text-primary)" }}>{cName}</strong></p>
                  <div style={{ background: "rgba(99,102,241,0.15)", border: "2px dashed #6366f1", borderRadius: "14px", padding: "20px", marginBottom: "16px" }}>
                    <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 8px" }}>TEST CODE</p>
                    <div style={{ display: "flex", gap: "12px", alignItems: "center", justifyContent: "center", marginBottom: "8px" }}>
                      <p style={{ color: "#818cf8", fontSize: "32px", fontWeight: 800, letterSpacing: "4px", margin: 0, fontFamily: "monospace" }}>{createdCode}</p>
                      <Btn style={{ padding: "8px 14px", fontSize: "12px", background: "rgba(99,102,241,0.3)", border: "1px solid #6366f1" }} onClick={() => { navigator.clipboard.writeText(createdCode); setCopyNotification("Code copied!"); setTimeout(() => setCopyNotification(null), 2500); }}>📋 Copy</Btn>
                    </div>
                    <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: 0 }}>Candidate enters this code to begin</p>
                  </div>
                  <div style={{ background: "var(--bg-card-inner)", borderRadius: "10px", padding: "12px", marginBottom: "16px", textAlign: "left" }}>
                    {[
                      ["Candidate", cName],
                      ["Track", `${cTrack?.icon} ${cTrack?.name}`],
                      ["Experience", `${cExp?.label} · ${cExp?.complexity}`],
                      ["Hiring Manager", cHiringManager?.name || "—"],
                      ...(cRequisitionId ? (() => {
                        const selReq = openRequisitions.find(r => String(r.id || r._id) === cRequisitionId);
                        return selReq?.jrfNumber ? [["Linked JRF", selReq.jrfNumber]] : [];
                      })() : []),
                      ["Question Source", selectedBankFile ? `📂 ${selectedBankFile}` : "🤖 AI Generated"],
                      ["Created At", fmtDate(new Date().toISOString())]
                    ].map(([k, v]) => (
                      <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid var(--border-card)" }}>
                        <span style={{ color: "var(--text-muted)", fontSize: "12px" }}>{k}</span>
                        <span style={{ color: k === "Linked JRF" ? "#818cf8" : "var(--text-secondary)", fontSize: "12px", fontWeight: 500, fontFamily: k === "Linked JRF" ? "monospace" : "inherit" }}>{v}</span>
                      </div>
                    ))}
                  </div>
                  <Btn style={{ width: "100%" }} onClick={() => {
                    setCreatedCode(null); setCName(""); setCTrack(null); setCExp(null);
                    setSelectedBankFile(null); setCResumeFile(null); setCEventName("");
                    if (!cRequisitionId) { setCHiringManager(null); }
                  }}>
                    Create Another Test
                  </Btn>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* REQUISITIONS */}
        {view === "requisitions" && (
          <RequisitionsView
            currentUser={currentUser}
            onViewReport={async (t) => {
              if (t.skipOnlineTest) {
                setSelectedReport(buildSkipReport(t));
                setSelectedTest(t);
                setReportFromView("requisitions");
                setView("reportDetail");
                return;
              }
              let rep;
              try { rep = await apiGet(`/api/reports/${t.code}`); } catch (e) {
                if (e.message?.includes('403')) { alert('You do not have permission to view this report.'); return; }
                if (e.message?.includes('404')) try { rep = await apiGet(`/api/sessions/${t.code}`); } catch {}
              }
              if (rep) {
                const base = { candidateName: t.candidateName, trackIcon: t.trackIcon, trackName: t.trackName, expLabel: t.expLabel, complexity: t.complexity, code: t.code };
                setSelectedReport({ ...base, ...rep, createdAt: t.createdAt, completedAt: t.completedAt || rep.completedAt });
                setSelectedTest(t);
                setReportFromView("requisitions");
                setView("reportDetail");
              } else {
                alert(t.status === 'pending' ? 'Test not yet started — no report available.' : 'No report data available for this test.');
              }
            }}
            onCreateTest={canWrite ? createTestForRequisition : undefined}
          />
        )}

        {/* USERS */}
        {view === "users" && <UserManagement currentUser={currentUser} />}

        {/* QUESTION BANKS */}
        {view === "qbanks" && <QuestionBanksManagement currentUser={currentUser} />}

        {/* EVENTS */}
        {view === "events" && <EventsManagement currentUser={currentUser} allEvents={allEvents} onEventsChanged={() => apiGet("/api/events").then(setAllEvents).catch(() => {})} />}

        {/* HISTORY */}
        {view === "history" && (
          <HistoryView
            hiringManagers={hiringManagers}
            allEvents={eventsForFilter}
            canDelete={canDelete}
            onDelete={deleteTest}
            onViewReport={async (t) => {
              if (t.skipOnlineTest) {
                setSelectedReport(buildSkipReport(t));
                setSelectedTest(t);
                setReportFromView("history");
                setView("reportDetail");
                return;
              }
              let rep;
              try { rep = await apiGet(`/api/reports/${t.code}`); } catch (e) {
                if (e.message?.includes('403')) { alert('You do not have permission to view this report.'); return; }
                if (e.message?.includes('404')) try { rep = await apiGet(`/api/sessions/${t.code}`); } catch {}
              }
              if (rep) {
                const base = { candidateName: t.candidateName, trackIcon: t.trackIcon, trackName: t.trackName, expLabel: t.expLabel, complexity: t.complexity, code: t.code };
                setSelectedReport({ ...base, ...rep, createdAt: t.createdAt, completedAt: t.completedAt || rep.completedAt });
                setSelectedTest(t);
                setReportFromView("history");
                setView("reportDetail");
              } else {
                alert(t.status === 'pending' ? 'Test not yet started — no report available.' : 'No report data available for this test.');
              }
            }}
          />
        )}

        {/* REPORTS */}
        {view === "reports" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h2 style={{ color: "var(--text-primary)", fontSize: "18px", margin: 0 }}>📋 Candidate Reports</h2>
              <span style={{ color: "var(--text-faint)", fontSize: "13px" }}>{reportsResult.total} test{reportsResult.total !== 1 ? "s" : ""}</span>
            </div>

            {/* Assigned to Me */}
            {myPendingAssignments.length > 0 && (
              <div style={{ marginBottom: "20px", background: "var(--bg-card)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: "12px", padding: "16px" }}>
                <div style={{ fontWeight: 700, fontSize: "14px", color: "var(--text-primary)", marginBottom: "12px" }}>
                  📝 Assigned to Me — {myPendingAssignments.length} pending
                </div>
                {myPendingAssignments.map(a => (
                  <div key={a._id} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 12px", background: "var(--bg-card-inner)", borderRadius: "8px", marginBottom: "8px", flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 600, fontSize: "13px", color: "var(--text-primary)", flex: 1 }}>{a.candidateName}</span>
                    <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>{a.trackName}</span>
                    <span style={{ fontSize: "12px", color: "var(--text-secondary)" }}>{a.roundLabel}</span>
                    <Btn style={{ padding: "5px 12px", fontSize: "12px" }} onClick={() => openReportByCode(a.testCode)}>View Report</Btn>
                  </div>
                ))}
              </div>
            )}

            {/* Filters */}
            <Card style={{ marginBottom: "16px", padding: "14px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
                <div>
                  <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 6px" }}>Filter by Status</p>
                  <select value={reportStatusFilter} onChange={e => setReportStatusFilter(e.target.value)}
                    style={{ width: "100%", padding: "8px 10px", borderRadius: "8px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px" }}>
                    <option value="all">All Tests</option>
                    {Object.entries(STATUS_STYLES).map(([key, s]) => (
                      <option key={key} value={key}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 6px" }}>Filter by Date</p>
                  <input type="date" value={reportDateFilter} onChange={e => setReportDateFilter(e.target.value)}
                    style={{ width: "100%", padding: "8px 10px", borderRadius: "8px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px", boxSizing: "border-box" }} />
                </div>
                <div>
                  <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 6px" }}>Filter by Result</p>
                  <select value={reportResultFilter} onChange={e => setReportResultFilter(e.target.value)}
                    style={{ width: "100%", padding: "8px 10px", borderRadius: "8px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px" }}>
                    <option value="all">All Results</option>
                    <option value="shortlist">✅ Shortlisted (≥7)</option>
                    <option value="hold">⏸ Hold (5–6.9)</option>
                    <option value="reject">❌ Reject (&lt;5)</option>
                  </select>
                </div>
                <div>
                  <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 6px" }}>Search by Name</p>
                  <input type="text" value={reportNameFilter} onChange={e => setReportNameFilter(e.target.value)}
                    placeholder="Type candidate name…"
                    style={{ width: "100%", padding: "8px 10px", borderRadius: "8px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px", boxSizing: "border-box" }} />
                </div>
                <div>
                  <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 6px" }}>Mandatory Questions Solved</p>
                  <select value={reportMandatoryFilter} onChange={e => setReportMandatoryFilter(e.target.value)}
                    style={{ width: "100%", padding: "8px 10px", borderRadius: "8px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px" }}>
                    <option value="all">All</option>
                    <option value="full">✅ Full (all mandatory ≥7)</option>
                    <option value="partial">⚠️ Partial (some passed)</option>
                    <option value="no">❌ None passed</option>
                  </select>
                </div>
                <div>
                  <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 6px" }}>Filter by Hiring Manager</p>
                  <select value={reportHMFilter} onChange={e => setReportHMFilter(e.target.value)}
                    style={{ width: "100%", padding: "8px 10px", borderRadius: "8px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px" }}>
                    <option value="all">All Hiring Managers</option>
                    {hiringManagers.map(hm => <option key={hm.id} value={String(hm.id)}>{hm.name}</option>)}
                  </select>
                </div>
                <div>
                  <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 6px" }}>Filter by Event</p>
                  <select value={reportEventFilter} onChange={e => setReportEventFilter(e.target.value)}
                    style={{ width: "100%", padding: "8px 10px", borderRadius: "8px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px" }}>
                    <option value="">All Events</option>
                    {eventsForFilter.map(ev => <option key={ev.id} value={ev.name}>{ev.name}</option>)}
                  </select>
                </div>
                <div>
                  <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 6px" }}>Final Verdict</p>
                  <select value={reportFinalVerdictFilter} onChange={e => setReportFinalVerdictFilter(e.target.value)}
                    style={{ width: "100%", padding: "8px 10px", borderRadius: "8px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px" }}>
                    <option value="all">All</option>
                    <option value="shortlisted">✅ Shortlisted</option>
                    <option value="on_hold">⏸ On Hold</option>
                    <option value="rejected">❌ Rejected</option>
                  </select>
                </div>
                <div>
                  <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 6px" }}>Filter by JRF#</p>
                  <input type="text" value={reportJRFFilter} onChange={e => setReportJRFFilter(e.target.value.toUpperCase())}
                    placeholder="e.g. JRF160260001"
                    style={{ width: "100%", padding: "8px 10px", borderRadius: "8px", border: "1px solid var(--border-input)", background: "var(--bg-input)", color: "var(--text-input)", fontSize: "13px", boxSizing: "border-box", fontFamily: "monospace" }} />
                </div>
              </div>
            </Card>

            {reportsLoading && <p style={{ color: "var(--text-faint)" }}>Loading...</p>}
            {!reportsLoading && (() => {
              const { tests: filtered, total: filteredTotal } = reportsResult;
              const isFiltered = reportStatusFilter !== "all" || reportDateFilter !== "" || reportResultFilter !== "all" || reportNameFilter.trim() !== "" || reportMandatoryFilter !== "all" || reportHMFilter !== "all" || reportEventFilter !== "" || reportFinalVerdictFilter !== "all" || reportJRFFilter.trim() !== "";
              const reportTotalPages = Math.max(1, Math.ceil(filteredTotal / reportPageSize));

              async function downloadFilteredXLSX() {
                // Fetch ALL filtered records (no pagination) + all reports for question details + rounds
                let allFiltered = [];
                try {
                  const r = await apiGet(buildWithScoresUrl({
                    archived: false,
                    status: reportStatusFilter !== 'all' ? reportStatusFilter : undefined,
                    date: reportDateFilter || undefined,
                    name: reportNameFilter.trim() || undefined,
                    result: reportResultFilter !== 'all' ? reportResultFilter : undefined,
                    mandatory: reportMandatoryFilter !== 'all' ? reportMandatoryFilter : undefined,
                    hmId: reportHMFilter !== 'all' ? reportHMFilter : undefined,
                    event: reportEventFilter || undefined,
                    finalVerdict: reportFinalVerdictFilter !== 'all' ? reportFinalVerdictFilter : undefined,
                    jrfNumber: reportJRFFilter.trim() || undefined,
                    all: true,
                  }));
                  allFiltered = r.tests || [];
                } catch {}
                let allReports = [];
                try { allReports = await apiGet("/api/reports"); } catch {}
                const repByCode = {};
                for (const r of allReports) { if (r.code) repByCode[r.code] = r; }

                // Fetch rounds for all codes in one call
                const roundsByCode = {};
                if (allFiltered.length > 0) {
                  try {
                    const codes = allFiltered.map(t => t.code).join(',');
                    const rb = await apiGet(`/api/rounds/bulk?codes=${encodeURIComponent(codes)}`);
                    Object.assign(roundsByCode, rb.roundsByCode || {});
                  } catch {}
                }

                const wb = XLSX.utils.book_new();
                const summaryRows = [["Code", "Candidate Name", "Track", "Experience", "Complexity", "Status", "Created", "Completed", "Overall Score", "Mandatory Score", "Result", "Event Name", "Hiring Manager", "Final Verdict"]];
                for (const t of allFiltered) {
                  const score = t.avgScore;
                  const mScore = t.mandatoryTotal > 0
                    ? (repByCode[t.code]?.questions?.filter(q => q.mandatory).reduce((s, q) => s + (q.score || 0), 0) / t.mandatoryTotal).toFixed(1)
                    : null;
                  const recLabel = score != null ? (score >= 7 ? "Shortlist" : score >= 5 ? "Hold" : "Reject") : "";
                  const finalVerdict = ['shortlisted', 'on_hold', 'rejected'].includes(t.status) ? STATUS_STYLES[t.status]?.label : "—";
                  summaryRows.push([t.code, t.candidateName, `${t.trackIcon || ""} ${t.trackName || ""}`.trim(), t.expLabel || "", t.complexity || "", STATUS_STYLES[t.status]?.label || t.status || "", fmtDate(t.createdAt), fmtDate(t.completedAt) || "Pending", score != null ? `${score}/10` : "", mScore != null ? `${mScore}/10` : "—", recLabel, t.eventName || "", t.hiringManager?.name || "", finalVerdict]);
                }
                XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), "Summary");

                // Rounds sheet — one row per round across all tests
                const roundsRows = [["Code", "Candidate", "Round", "Assigned To", "Assigned By", "Assigned At", "Status", "Verdict", "Recommend Round 2", "Feedback", "Submitted At", "Final Verdict"]];
                for (const t of allFiltered) {
                  const testRounds = roundsByCode[t.code] || [];
                  const finalVerdictForRounds = ['shortlisted', 'on_hold', 'rejected'].includes(t.status) ? STATUS_STYLES[t.status]?.label : "—";
                  for (const r of testRounds) {
                    roundsRows.push([
                      t.code,
                      t.candidateName || "—",
                      r.roundLabel || String(r.roundNumber),
                      r.assignedTo?.name || "—",
                      r.assignedBy?.name || "—",
                      r.assignedAt ? new Date(r.assignedAt).toLocaleString() : "—",
                      r.status === 'completed' ? "Completed" : "Pending",
                      r.verdict ? r.verdict.charAt(0).toUpperCase() + r.verdict.slice(1) : "—",
                      r.roundNumber === 1 ? (r.recommendRound2 ? "Yes" : "No") : "N/A",
                      r.feedback || "—",
                      r.submittedAt ? new Date(r.submittedAt).toLocaleString() : "—",
                      finalVerdictForRounds,
                    ]);
                  }
                }
                XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(roundsRows), "Rounds");

                for (const t of allFiltered) {
                  const rep = repByCode[t.code] || null;
                  const hasQuestions = rep && Array.isArray(rep.questions) && rep.questions.length > 0;
                  const avg = t.avgScore != null ? String(t.avgScore) : null;
                  const mandatoryQsSheet = hasQuestions ? rep.questions.filter(q => q.mandatory) : [];
                  const mandatoryAvgSheet = mandatoryQsSheet.length > 0
                    ? (mandatoryQsSheet.reduce((s, q) => s + (q.score || 0), 0) / mandatoryQsSheet.length).toFixed(1) : null;
                  const mandatoryPassedSheet = mandatoryQsSheet.filter(q => q.score >= 7).length;
                  const recLabel = avg != null ? (parseFloat(avg) >= 7 ? "Shortlist" : parseFloat(avg) >= 5 ? "Hold" : "Reject") : "—";
                  const finalVerdictSheet = ['shortlisted', 'on_hold', 'rejected'].includes(t.status) ? STATUS_STYLES[t.status]?.label : "—";
                  const sheetData = [
                    ["Candidate", t.candidateName || "—"],
                    ["Track", `${t.trackIcon || ""} ${t.trackName || ""}`.trim()],
                    ["Experience", t.expLabel || ""],
                    ["Code", t.code],
                    ["Overall Score", avg != null ? `${avg}/10` : "—"],
                    ...(mandatoryAvgSheet !== null ? [["Mandatory Score", `${mandatoryAvgSheet}/10 (${mandatoryPassedSheet}/${mandatoryQsSheet.length} passed)`]] : []),
                    ["Result", recLabel],
                    ["Final Verdict", finalVerdictSheet],
                    [],
                  ];
                  if (hasQuestions) {
                    sheetData.push(["#", "Mandatory", "Type", "Topic", "Question", "Candidate Answer", "Score", "Strengths", "Gaps", "Model Answer", "Skipped"]);
                    rep.questions.forEach((q, i) => {
                      sheetData.push([i + 1, q.mandatory ? "Yes" : "No", q.type || "", q.topic || "", q.question || "", q.candidateAnswer || "(No answer)", `${q.score}/10`, q.strengths || "", q.gaps || "", q.modelAnswer || "", q.skipped ? "Yes" : "No"]);
                    });
                  } else {
                    sheetData.push(["Status", t.status || "—"]);
                    sheetData.push(["Note", "No detailed report available"]);
                  }
                  const baseName = (t.candidateName || t.code).replace(/[\\\/\?\*\[\]:]/g, "").slice(0, 25);
                  let sheetName = baseName || `Sheet${wb.SheetNames.length + 1}`;
                  let attempt = 2;
                  while (wb.SheetNames.includes(sheetName)) { sheetName = `${baseName.slice(0, 22)}_${attempt++}`; }
                  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sheetData), sheetName);
                }
                XLSX.writeFile(wb, `reports-${new Date().toISOString().slice(0, 10)}.xlsx`);
              }

              return (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                    <span style={{ color: "var(--text-muted)", fontSize: "13px" }}>{filteredTotal} result{filteredTotal !== 1 ? "s" : ""}</span>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Btn variant="ghost" style={{ padding: "7px 12px", fontSize: "16px" }} onClick={() => loadReports()} title="Refresh">🔄</Btn>
                      {isFiltered && (
                        <Btn variant="ghost" style={{ padding: "7px 14px", fontSize: "12px", color: "#f87171", borderColor: "rgba(248,113,113,0.3)" }} onClick={() => { setReportStatusFilter("all"); setReportDateFilter(""); setReportResultFilter("all"); setReportFinalVerdictFilter("all"); setReportNameFilter(""); setReportMandatoryFilter("all"); setReportHMFilter("all"); setReportEventFilter(""); setReportJRFFilter(""); }}>✕ Reset Filters</Btn>
                      )}
                      {isFiltered && filteredTotal > 0 && !archiveConfirmCodes && (
                        <Btn variant="ghost" style={{ padding: "7px 14px", fontSize: "12px", color: "#fb923c", borderColor: "rgba(251,146,60,0.3)" }} disabled={archiving} onClick={() => setArchiveConfirmCodes(filtered.map(t => t.code))}>📁 Archive ({filteredTotal})</Btn>
                      )}
                      {archiveConfirmCodes && (
                        <>
                          <span style={{ fontSize: "12px", color: "var(--text-muted)", alignSelf: "center" }}>Archive {archiveConfirmCodes.length} record(s)?</span>
                          <Btn variant="ghost" style={{ padding: "7px 12px", fontSize: "12px", color: "#fb923c", borderColor: "rgba(251,146,60,0.5)" }} disabled={archiving} onClick={() => doArchive(archiveConfirmCodes)}>{archiving ? "Archiving…" : "Yes"}</Btn>
                          <Btn variant="ghost" style={{ padding: "7px 12px", fontSize: "12px" }} disabled={archiving} onClick={() => setArchiveConfirmCodes(null)}>No</Btn>
                        </>
                      )}
                      {isFiltered && filteredTotal > 0 && (
                        <Btn variant="ghost" style={{ padding: "7px 14px", fontSize: "12px" }} onClick={downloadFilteredXLSX}>⬇ Download XLSX ({filteredTotal})</Btn>
                      )}
                    </div>
                  </div>
                  {filteredTotal === 0 && (
                    <p style={{ color: "var(--text-faint)", fontSize: "13px" }}>No tests match your filters.</p>
                  )}
                  {filtered.map(t => (
                    <ReportRow key={t.code} test={t} canDelete={canDelete}
                      onView={async () => {
                          if (t.skipOnlineTest) {
                            setSelectedReport(buildSkipReport(t));
                            setSelectedTest(t);
                            setReportFromView("reports");
                            setView("reportDetail");
                            return;
                          }
                          let rep;
                          try {
                            rep = await apiGet(`/api/reports/${t.code}`);
                          } catch (e) {
                            if (e.message && e.message.includes('403')) {
                              alert('You do not have permission to view this report.');
                              return;
                            }
                            if (e.message && (e.message.includes('404') || e.message.includes('not found'))) {
                              try { rep = await apiGet(`/api/sessions/${t.code}`); } catch { rep = null; }
                            }
                          }
                          if (!rep) {
                            try {
                              const stored = sessionStorage.getItem(`report:${t.code}`) || sessionStorage.getItem(`session:${t.code}`);
                              if (stored) rep = JSON.parse(stored);
                            } catch {}
                          }
                          if (rep) {
                            const base = { candidateName: t.candidateName, trackIcon: t.trackIcon, trackName: t.trackName, expLabel: t.expLabel, complexity: t.complexity, code: t.code };
                            setSelectedReport({ ...base, ...rep, createdAt: t.createdAt, completedAt: t.completedAt || rep.completedAt });
                            setSelectedTest(t);
                            setReportFromView("reports");
                            setView("reportDetail");
                          } else {
                            alert(t.status === 'pending' ? 'Test not yet started — no report available.' : 'No report or session data available for this test.');
                          }
                      }}
                      onDelete={() => deleteTest(t.code)}
                    />
                  ))}
                  {filteredTotal > 0 && (
                    <Pagination page={reportPage} totalPages={reportTotalPages} pageSize={reportPageSize}
                      onPage={p => { setReportPage(p); loadReports(p, reportPageSize); }}
                      onPageSize={ps => { setReportPageSize(ps); setReportPage(1); loadReports(1, ps); }} />
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

// ── CANDIDATE PORTAL ──
function CandidatePortal() {
  const [phase, setPhase] = useState("enter-code");
  const [code, setCode] = useState("");
  const [candName, setCandName] = useState(""); // optional name entered by candidate
  const [testData, setTestData] = useState(null);
  const [err, setErr] = useState("");
  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadingQ, setLoadingQ] = useState(false);
  const [qTimer, setQTimer] = useState(120);
  const [totalTimer, setTotalTimer] = useState(TOTAL_INTERVIEW_SECS);
  const [session, setSession] = useState({ attempted: 0, questions: [] });
  const [prevQuestions, setPrevQuestions] = useState([]);
  const [progCount, setProgCount] = useState(0);
  const [autoSub, setAutoSub] = useState(false);
  const [skipConfirm, setSkipConfirm] = useState(false);
  const [bankQueue, setBankQueue] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [instructionsChecked, setInstructionsChecked] = useState(false);
  const [hasExistingSession, setHasExistingSession] = useState(false);
  const [codeLanguage, setCodeLanguage] = useState('java');
  const codeLanguageRef = useRef('java');
  const [codeOutput, setCodeOutput] = useState(null); // { stdout, stderr, code } | { error }
  const codeOutputRef = useRef(null);
  const [runningCode, setRunningCode] = useState(false);

  logger.debug('CandidatePortal mounted', { component: 'candidate-portal' });

  const sessionRef = useRef(session);
  const testDataRef = useRef(testData);
  const questionRef = useRef(question);
  const answerRef = useRef(answer);
  const submittingRef = useRef(submitting);
  const phaseRef = useRef(phase);
  const progCountRef = useRef(progCount);
  const prevQuestionsRef = useRef(prevQuestions);
  const totalTimerRef = useRef(totalTimer);
  const bankQueueRef = useRef(bankQueue);

  useEffect(() => { sessionRef.current = session; }, [session]);
  useEffect(() => { testDataRef.current = testData; }, [testData]);
  useEffect(() => { questionRef.current = question; }, [question]);
  useEffect(() => { answerRef.current = answer; }, [answer]);
  useEffect(() => { submittingRef.current = submitting; }, [submitting]);
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { progCountRef.current = progCount; }, [progCount]);
  useEffect(() => { prevQuestionsRef.current = prevQuestions; }, [prevQuestions]);
  useEffect(() => { totalTimerRef.current = totalTimer; }, [totalTimer]);
  useEffect(() => { bankQueueRef.current = bankQueue; }, [bankQueue]);
  useEffect(() => { codeLanguageRef.current = codeLanguage; }, [codeLanguage]);
  useEffect(() => { codeOutputRef.current = codeOutput; }, [codeOutput]);

  // Lock browser navigation during interview to prevent accidental back/refresh
  useEffect(() => {
    if (phase !== "interview") return;
    // Push a dummy state so the back button hits it first instead of navigating away
    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
    };
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    const handleKeyDown = (e) => {
      // Block F5 and Ctrl+R / Ctrl+Shift+R (page refresh)
      if (e.key === 'F5' || (e.ctrlKey && (e.key === 'r' || e.key === 'R'))) {
        e.preventDefault();
      }
    };
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [phase]);

  const qIntervalRef = useRef(null);
  const totalIntervalRef = useRef(null);
  const questionStartRef = useRef(null); // ISO timestamp when current question was shown
  const heartbeatIntervalRef = useRef(null);
  const sessionTokenRef = useRef(null); // UUID persisted in sessionStorage per test code

  function startQTimer(secs) {
    clearInterval(qIntervalRef.current);
    setQTimer(secs);
    qIntervalRef.current = setInterval(() => {
      setQTimer(t => {
        if (t <= 1) {
          clearInterval(qIntervalRef.current);
          if (!submittingRef.current && phaseRef.current === "interview") {
            setAutoSub(true);
            doSubmit(false, true);
          }
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  }

  function stopQTimer() { clearInterval(qIntervalRef.current); }

  async function runCode() {
    const code = answerRef.current;
    if (!code.trim() || runningCode) return;
    setRunningCode(true); setCodeOutput(null); codeOutputRef.current = null;
    try {
      const r = await fetch(`${API}/api/code/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language: codeLanguageRef.current, code }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setCodeOutput(d); codeOutputRef.current = d;
    } catch (e) {
      const out = { error: e.message };
      setCodeOutput(out); codeOutputRef.current = out;
    } finally {
      setRunningCode(false);
    }
  }

  function startTotalTimer() {
    clearInterval(totalIntervalRef.current);
    totalIntervalRef.current = setInterval(() => {
      setTotalTimer(t => {
        if (t <= 1) { clearInterval(totalIntervalRef.current); finishInterview(sessionRef.current); return 0; }
        return t - 1;
      });
    }, 1000);
  }

  const doSubmit = useCallback(async (skipped = false, auto = false) => {
    if (submittingRef.current) return;
    submittingRef.current = true; setSubmitting(true); stopQTimer(); setSkipConfirm(false);
    const submittedAt = new Date().toISOString();
    const startedAt = questionStartRef.current || submittedAt;
    const durationSecs = Math.round((new Date(submittedAt) - new Date(startedAt)) / 1000);
    const td = testDataRef.current;
    const q = questionRef.current;
    const ans = skipped ? "" : answerRef.current;
    const cur = sessionRef.current;
    const curProg = progCountRef.current;
    // For programming questions, bundle language + execution output into the answer for AI scoring
    let finalAnswer = skipped ? '(Skipped)' : (ans || '(No answer)');
    if (!skipped && questionRef.current?.type === 'programming' && ans) {
      finalAnswer = `Language: ${codeLanguageRef.current}\n\n${ans}`;
      const out = codeOutputRef.current;
      if (out && !out.error && (out.stdout || out.stderr)) {
        if (out.stdout) finalAnswer += `\n\nOutput:\n${out.stdout.slice(0, 2000)}`;
        if (out.stderr) finalAnswer += `\n\nErrors:\n${out.stderr.slice(0, 500)}`;
      }
    }

    logger.info('Question submission started', {
      component: 'candidate-portal',
      testCode: td.code,
      questionNumber: cur.attempted + 1,
      questionType: q.type,
      skipped: !!skipped,
      autoSubmitted: auto,
      answerLength: ans?.length || 0
    });
    
    // Score and persist server-side — AI scores never travel back to the browser
    try {
      await callAIFeedbackAndSave(
        td.code, q.question, q.topic, q.type, q.mandatory || false,
        skipped, finalAnswer,
        q.actualAnswer, startedAt, submittedAt, durationSecs
      );
    } catch (e) {
      logger.error("feedbackAndSave failed", {
        component: 'candidate-portal',
        testCode: td.code,
        error: e.message
      });
      // Backend saves with fallback scores on AI error; a network failure here is non-fatal —
      // the question will be missing from the report but the interview can continue.
    }

    // Track only structural state locally — no scores stored in browser
    const newQ = { question: q.question, topic: q.topic, type: q.type, mandatory: q.mandatory || false, skipped: !!skipped, candidateAnswer: finalAnswer, startedAt, submittedAt, durationSecs };
    if (q.type === "programming") { setProgCount(c => c + 1); progCountRef.current = curProg + 1; }
    const newSession = { attempted: cur.attempted + 1, questions: [...cur.questions, newQ] };
    setSession(newSession); sessionRef.current = newSession;

    // Save session progress to enable resume functionality (include timers)
    try {
      const progress = { ...newSession, totalTimer: totalTimerRef.current, qTimer };
      await apiPost("/api/sessions", { testCode: td.code, session: progress });
    } catch (e) {
      logger.warn("Failed to save session progress to server", {
        component: 'candidate-portal',
        testCode: td.code,
        error: e.message
      });
      // fallback to sessionStorage (cleared when tab closes — avoids persisting PII)
      try {
        sessionStorage.setItem(`session:${td.code}`, JSON.stringify({ ...newSession, totalTimer: totalTimerRef.current, qTimer }));
      } catch {}
    }
    
    submittingRef.current = false; setSubmitting(false); setAutoSub(false);
    if (newSession.attempted >= TOTAL_QUESTIONS) { await finishInterview(newSession); }
    else { fetchNextQuestion(newSession, progCountRef.current, prevQuestionsRef.current, td); }
  }, []);

  async function finishInterview(finalSession) {
    logger.info('Interview finished', {
      component: 'candidate-portal',
      testCode: testDataRef.current?.code,
      totalQuestions: finalSession.attempted,
    });
    clearInterval(totalIntervalRef.current); clearInterval(qIntervalRef.current);
    clearInterval(heartbeatIntervalRef.current);
    if (sessionTokenRef.current) sessionStorage.removeItem(`st_${testDataRef.current?.code}`);
    await saveReport(); setPhase("done");
  }

  async function saveReport() {
    const td = testDataRef.current;
    if (!td?.code) {
      logger.error("No test data available for saveReport", { component: 'candidate-portal' });
      return;
    }
    // Questions are already in MongoDB (pushed per-answer via feedbackAndSave).
    // Just mark the test/report as completed with timing metadata.
    const now = new Date().toISOString();
    try {
      await apiPost(`/api/tests/${td.code}/complete`, {
        completedAt: now,
        timeUsed: TOTAL_INTERVIEW_SECS - totalTimerRef.current,
        usedQuestionBank: !!bankQueueRef.current,
      });
      logger.info('Test marked completed', { component: 'candidate-portal', testCode: td.code });
    } catch (e) {
      logger.error('Failed to mark test completed', { component: 'candidate-portal', testCode: td.code, error: e.message });
    }
    try { await apiDelete(`/api/sessions/${td.code}`); } catch {}
  }

  async function fetchNextQuestion(curSession, curProg, curPrevQ, td) {
    setLoadingQ(true); setAnswer(""); answerRef.current = ""; setAutoSub(false); setSkipConfirm(false); setErr(""); setCodeOutput(null); codeOutputRef.current = null;
    const tn = (td.trackName || '').toLowerCase();
    const defLang = tn.includes('python') || tn.includes('langchain') || tn.includes('agentic') ? 'python'
      : tn.includes('angular') || tn.includes('javascript') ? 'javascript'
      : 'java';
    setCodeLanguage(defLang); codeLanguageRef.current = defLang;
    const att = curSession.attempted;
    // count how many aptitude questions have already been asked
    const aptCount = curSession.questions.filter(q => q.type === "aptitude").length;
    let q;
    const queue = bankQueueRef.current;
    if (queue && queue.length > 0) {
      q = queue[0];
      const newQueue = queue.slice(1);
      setBankQueue(newQueue); bankQueueRef.current = newQueue;
    } else {
      // Quota-based: 20% programming + 10% aptitude (mathematical puzzles), scales with TOTAL_QUESTIONS
      // For 5 questions: MAX_PROG_Q=1 (Q4, att=3), MAX_APT_Q=1 (always Q2, att=1)
      const MAX_PROG_Q = Math.max(1, Math.floor(TOTAL_QUESTIONS * 0.20));
      const MAX_APT_Q  = Math.max(1, Math.floor(TOTAL_QUESTIONS * 0.10));
      const needsProg = curProg < MAX_PROG_Q && att === Math.floor(TOTAL_QUESTIONS * 0.6);
      const needsApt  = aptCount < MAX_APT_Q && att === 1;
      try {
        q = await callAIQuestion(td.code, curPrevQ, needsProg, needsApt);
      } catch (e) {
        logger.error("AI question generation failed", { component: 'candidate-portal', error: e.message });
        q = fallbackQuestion(att, td.trackName, td.complexity);
      }
      if (needsProg && q.type !== "programming") {
        q = fallbackProgQuestion(att, td.trackName, td.complexity);
      } else if (needsApt && q.type !== "aptitude") {
        q = fallbackAptitudeQuestion(att, td.complexity);
      }
    }
    setQuestion(q); questionRef.current = q;
    questionStartRef.current = new Date().toISOString(); // record when question was shown to candidate
    setPrevQuestions(p => { const n = [...p, q.question]; prevQuestionsRef.current = n; return n; });
    setPhase("interview"); setLoadingQ(false);
    // compute per-question timer. If test was created from a question bank, allocate
    // equal share of remaining global time; otherwise fall back to complexity rules.
    let nextSecs;
    if (td.hasQuestionBank) {
      const rem = TOTAL_QUESTIONS - curSession.attempted;
      nextSecs = rem > 0 ? Math.ceil(totalTimerRef.current / rem) : 0;
    } else {
      const base = td.complexity === "complex" ? CONFIG.TIMER_DURATIONS.complex : td.complexity === "medium" ? CONFIG.TIMER_DURATIONS.medium : CONFIG.TIMER_DURATIONS.basic;
      nextSecs = q.type === "programming" ? base * CONFIG.PROGRAMMING_MULTIPLIER : base;
    }
    startQTimer(nextSecs);
  }

  async function verifyCode() {
    if (!code.trim()) { setErr("Enter the test code."); return; }
    setErr("");
    
    logger.info('Verifying test code', {
      component: 'candidate-portal',
      testCode: code.trim().toUpperCase()
    });
    
    try {
      const t = await apiGet(`/api/tests/${code.trim().toUpperCase()}`);
      if (t.notYetOpen) {
        setErr(`⏰ Test not yet available. This event opens on ${fmtDate(t.commenceAt)}.`);
        return;
      }
      if (t.status === "completed") {
        logger.warn('Test code already completed', {
          component: 'candidate-portal',
          testCode: code.trim().toUpperCase()
        });
        setErr("❌ Code expired. This test has already been completed.");
        return;
      }
      // Check if another browser session is actively using this test code
      if (t.isLocked) {
        // Allow the same browser to proceed (they have the token in sessionStorage)
        const existingToken = sessionStorage.getItem(`st_${t.code}`);
        if (!existingToken) {
          setErr("⚠️ This test is currently in progress in another browser. Please contact HR if this is unexpected.");
          return;
        }
      }
      if (candName.trim()) {
        // override name from HR if candidate provided their own
        t.candidateName = candName.trim();
      }
      setTestData(t); testDataRef.current = t;
      
      logger.info('Test code verified', {
        component: 'candidate-portal',
        testCode: t.code,
        candidateName: t.candidateName,
        track: t.trackName,
        hasBank: !!t.hasQuestionBank
      });
      
      if (t.hasQuestionBank) {
        try {
          const bankData = await apiGet(`/api/question-banks/${t.code}`);
          const qlist = bankData.questions || [];
          const queue = buildBankQueue(qlist);
          setBankQueue(queue); bankQueueRef.current = queue;
          logger.debug('Question bank loaded', {
            component: 'candidate-portal',
            testCode: t.code,
            bankSize: qlist.length,
            queueSize: queue.length
          });
        } catch (err) {
          logger.warn("Failed to load question bank", {
            component: 'candidate-portal',
            testCode: t.code,
            error: err.message
          });
        }
      }
      // Check if candidate has an existing in-progress session (for resume)
      try {
        const existing = await apiGet(`/api/sessions/${t.code}`);
        setHasExistingSession(!!(existing && existing.questions && existing.questions.length > 0));
      } catch (_) {
        setHasExistingSession(false);
      }
      setPhase("confirmed");
    } catch (e) {
      const errorMsg = e.message?.includes("404") || e.message?.includes("not found")
        ? "Invalid code. Please check with your HR."
        : "Error verifying code. Is the backend running?";
      logger.error('Code verification failed', {
        component: 'candidate-portal',
        testCode: code.trim().toUpperCase(),
        error: e.message
      });
      setErr(errorMsg);
    }
  }

  // when an interview begins we also create initial session/report documents so HR can
  // view the candidate even if they abandon before answering any question
  async function startInterview() {
    logger.info('Interview started', {
      component: 'candidate-portal',
      testCode: testDataRef.current?.code,
      candidateName: testDataRef.current?.candidateName
    });

    const td = testDataRef.current;
    if (!td) return;

    // Acquire session lock — get or create a stable token for this browser tab
    const stKey = `st_${td.code}`;
    let token = sessionStorage.getItem(stKey);
    if (!token) {
      token = (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36));
      sessionStorage.setItem(stKey, token);
    }
    sessionTokenRef.current = token;
    try {
      await apiPost(`/api/tests/${td.code}/lock`, { sessionToken: token });
    } catch (lockErr) {
      if (lockErr.message?.includes("423") || lockErr.message?.includes("in progress")) {
        setErr("⚠️ This test is already open in another browser. Close that session before continuing.");
        return;
      }
      // Non-lock errors (network, etc.) — allow continuing
    }

    // Start heartbeat to keep the lock alive (every 55 seconds)
    clearInterval(heartbeatIntervalRef.current);
    heartbeatIntervalRef.current = setInterval(async () => {
      try { await apiPost(`/api/tests/${td.code}/heartbeat`, { sessionToken: sessionTokenRef.current }); } catch (_) {}
    }, 55000);

    // attempt to resume existing progress
    try {
      const savedSession = await apiGet(`/api/sessions/${td.code}`);
      logger.info("Resuming from saved session", {
        component: 'candidate-portal',
        testCode: td.code,
        questionsAttempted: savedSession.attempted,
        resumedTotalTimer: savedSession.totalTimer
      });
      // Restore saved timer so candidate continues from where they left off
      if (savedSession.totalTimer != null && savedSession.totalTimer > 0) {
        setTotalTimer(savedSession.totalTimer);
        totalTimerRef.current = savedSession.totalTimer;
      }
      startTotalTimer();
      setSession(savedSession);
      sessionRef.current = savedSession;
      const prevQ = savedSession.questions.map(q => q.question);
      setPrevQuestions(prevQ);
      prevQuestionsRef.current = prevQ;
      const progC = savedSession.questions.filter(q => q.type === "programming").length;
      setProgCount(progC);
      progCountRef.current = progC;
      // For question bank tests: skip already-answered questions so resume continues from the right position
      if (bankQueueRef.current && savedSession.questions.length > 0) {
        const answeredSet = new Set(savedSession.questions.map(q => q.question));
        const remaining = bankQueueRef.current.filter(q => !answeredSet.has(q.question));
        setBankQueue(remaining);
        bankQueueRef.current = remaining;
      }
      fetchNextQuestion(savedSession, progC, prevQ, td);
    } catch (e) {
      // no existing session, start fresh and create placeholder documents
      logger.info("Starting fresh interview (no saved session)", {
        component: 'candidate-portal',
        testCode: td.code
      });
      startTotalTimer();
      const initSession = { attempted: 0, questions: [] };
      setSession(initSession);
      sessionRef.current = initSession;
      // create blank session/report in backend (allow HR to open early)
      try {
        await apiPost("/api/sessions", { testCode: td.code, session: { ...initSession, totalTimer: totalTimerRef.current, qTimer } });
      } catch (err) {
        logger.warn("Failed to persist initial session", {
          component: 'candidate-portal',
          testCode: td.code,
          error: err.message
        });
      }
      try {
        const partial = { code: td.code, candidateName: td.candidateName, trackName: td.trackName, trackIcon: td.trackIcon, expLabel: td.expLabel, complexity: td.complexity, questions: [] };
        await apiPost("/api/reports", partial);
      } catch (err) {
        logger.warn("Failed to persist initial report", {
          component: 'candidate-portal',
          testCode: td.code,
          error: err.message
        });
      }
      fetchNextQuestion(initSession, 0, [], td);
    }
  }

  const qTimerColor = qTimer <= 30 ? "#ef4444" : qTimer <= 60 ? "#f97316" : "#22c55e";
  const totalTimerColor = totalTimer <= 600 ? "#ef4444" : totalTimer <= 1800 ? "#f97316" : "#94a3b8";
  const qNum = session.attempted + 1;

  if (phase === "enter-code") return (
    <div style={{ minHeight: "100vh", background: "var(--bg-page)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px", fontFamily: "'Segoe UI',sans-serif" }}>
      <div style={{ maxWidth: "360px", width: "100%", textAlign: "center" }}>
        <Logo height={44} />
        <p style={{ color: "var(--text-muted)", marginTop: "10px", marginBottom: "24px", fontSize: "14px" }}>Candidate Interview Portal</p>
        <Card>
          <p style={{ color: "var(--text-secondary)", fontSize: "13px", fontWeight: 600, margin: "0 0 8px" }}>🔑 Enter Your Test Code</p>
          <Input value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="e.g. SEL-482910" style={{ marginBottom: "12px", fontFamily: "monospace", letterSpacing: "2px", fontSize: "16px", textAlign: "center" }} />
          <p style={{ color: "var(--text-secondary)", fontSize: "13px", fontWeight: 600, margin: "8px 0 4px" }}>👤 Your name (optional)</p>
          <Input value={candName} onChange={e => setCandName(e.target.value)} placeholder="Enter your name" style={{ marginBottom: "12px" }} />
          {err && <p style={{ color: "#f87171", fontSize: "13px", marginBottom: "10px" }}>{err}</p>}
          <Btn style={{ width: "100%" }} onClick={verifyCode}>Verify Code →</Btn>
        </Card>
      </div>
    </div>
  );

  if (phase === "confirmed" && testData) return (
    <div style={{ minHeight: "100vh", background: "var(--bg-page)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px", fontFamily: "'Segoe UI',sans-serif" }}>
      <div style={{ maxWidth: "440px", width: "100%", textAlign: "center" }}>
        <Logo height={40} />
        <div style={{ margin: "22px 0 16px" }}>
          <div style={{ fontSize: "40px", marginBottom: "8px" }}>👋</div>
          <h2 style={{ color: "var(--text-primary)", fontSize: "20px", margin: "0 0 5px" }}>Hello, {testData.candidateName}!</h2>
          <p style={{ color: "var(--text-muted)", fontSize: "14px", margin: 0 }}>Your interview is ready.</p>
        </div>
        <Card style={{ marginBottom: "16px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            {[["Track", `${testData.trackIcon} ${testData.trackName}`], ["Experience", testData.expLabel], ["Questions", `${TOTAL_QUESTIONS} Questions`], ["Duration", "90 Minutes"]].map(([k, v]) => (
              <div key={k} style={{ background: "var(--bg-card-inner)", borderRadius: "10px", padding: "10px" }}>
                <p style={{ color: "var(--text-faint)", fontSize: "11px", margin: "0 0 3px" }}>{k}</p>
                <p style={{ color: "var(--text-secondary)", fontSize: "13px", fontWeight: 600, margin: 0 }}>{v}</p>
              </div>
            ))}
          </div>
          <div style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.2)", borderRadius: "10px", padding: "10px", marginTop: "12px" }}>
            <p style={{ color: "#fbbf24", fontSize: "12px", margin: 0 }}>⚠️ Once started, the timer cannot be paused. Ensure you are in a quiet environment.</p>
          </div>
        </Card>
        <div style={{ background: "var(--bg-card-inner)", border: "1px solid var(--border-card)", borderRadius: "12px", padding: "16px", marginBottom: "18px", textAlign: "left" }}>
          <p style={{ color: "var(--nav-active-color)", fontSize: "11px", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", margin: "0 0 8px" }}>🛡️ Data Privacy Consent — DPDP Act, 2023</p>
          <p style={{ color: "var(--text-secondary)", fontSize: "12px", lineHeight: "1.7", margin: "0 0 14px" }}>
            By proceeding, you (<strong style={{ color: "var(--text-primary)" }}>Data Principal</strong>) voluntarily provide your personal data — including your name, interview responses, and assessment results — to <strong style={{ color: "var(--text-primary)" }}>ISG (Data Fiduciary)</strong> for the purpose of evaluating your candidacy for the applied role.<br /><br />
            Your data will be processed lawfully and only for this assessment purpose, in accordance with the <strong style={{ color: "var(--text-primary)" }}>Digital Personal Data Protection (DPDP) Act, 2023</strong>. It will be retained for a limited period as required for recruitment decisions and will not be shared with third parties without your consent, except as required by law.<br /><br />
            You have the right to access, correct, or request erasure of your personal data by contacting your HR representative.
          </p>
          <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={e => setConsentChecked(e.target.checked)}
              style={{ marginTop: "2px", width: "15px", height: "15px", accentColor: "#6366f1", cursor: "pointer", flexShrink: 0 }}
            />
            <span style={{ color: "var(--text-secondary)", fontSize: "12px", lineHeight: "1.6" }}>
              I have read and understood the above. I freely give my consent to the collection and processing of my personal data for this interview assessment as described.
            </span>
          </label>
        </div>
        <div style={{ background: "var(--bg-card-inner)", border: "1px solid var(--border-card)", borderRadius: "12px", padding: "16px", marginBottom: "18px", textAlign: "left" }}>
          <p style={{ color: "var(--nav-active-color)", fontSize: "11px", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", margin: "0 0 10px" }}>📋 Instructions for the Question Paper</p>
          <ul style={{ color: "var(--text-secondary)", fontSize: "12px", lineHeight: "1.8", margin: "0 0 14px", paddingLeft: "18px" }}>
            <li>Elaborate and document how you arrived at the solution to a particular problem.</li>
            <li>Do write the answers clearly, to avoid confusion during the marking.</li>
          </ul>
          <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={instructionsChecked}
              onChange={e => setInstructionsChecked(e.target.checked)}
              style={{ marginTop: "2px", width: "15px", height: "15px", accentColor: "#6366f1", cursor: "pointer", flexShrink: 0 }}
            />
            <span style={{ color: "var(--text-secondary)", fontSize: "12px", lineHeight: "1.6" }}>
              I have read and understood the instructions for this interview assessment.
            </span>
          </label>
        </div>
        {(() => {
          const canStart = consentChecked && instructionsChecked;
          return (
            <Btn style={{ padding: "14px 40px", fontSize: "15px", opacity: canStart ? 1 : 0.4, cursor: canStart ? "pointer" : "not-allowed" }} onClick={canStart ? startInterview : undefined}>{hasExistingSession ? "Resume Interview →" : "Begin Interview →"}</Btn>
          );
        })()}
      </div>
    </div>
  );

  if (phase === "interview") {
    const ts = typeStyle(question?.type);
    return (
      <div style={{ minHeight: "100vh", background: "var(--bg-page)", padding: "20px", fontFamily: "'Segoe UI',sans-serif" }}
        onCopy={e => e.preventDefault()} onCut={e => e.preventDefault()} onContextMenu={e => e.preventDefault()}>
        <div style={{ maxWidth: "700px", margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <Logo height={30} />
            <div style={{ display: "flex", gap: "8px" }}>
              <div style={{ background: "var(--bg-card-inner)", borderRadius: "10px", padding: "6px 12px", border: `1px solid ${totalTimerColor}44`, color: totalTimerColor, fontSize: "13px", fontWeight: 600 }}>🕐 {fmt(totalTimer)}</div>
              <div style={{ background: "var(--bg-card-inner)", borderRadius: "10px", padding: "6px 12px", border: `2px solid ${qTimerColor}`, color: qTimerColor, fontWeight: 700, fontSize: "16px" }}>⏱ {fmt(qTimer)}</div>
              <div style={{ background: "var(--bg-card-inner)", borderRadius: "10px", padding: "6px 12px", color: "var(--text-muted)", fontSize: "13px" }}>{qNum}/{TOTAL_QUESTIONS}</div>
            </div>
          </div>
          <ProgressBar value={qNum - 1} total={TOTAL_QUESTIONS} />
          {loadingQ ? (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <div style={{ fontSize: "36px", marginBottom: "12px" }}>⏳</div>
              <p style={{ color: "var(--text-muted)", fontSize: "15px" }}>Loading next question...</p>
            </div>
          ) : (
            <>
              <p style={{ color: "var(--text-faint)", fontSize: "12px", margin: "0 0 12px" }}>👤 {testData?.candidateName} · {testData?.trackIcon} {testData?.trackName}</p>
              <div style={{ background: "var(--bg-card)", borderRadius: "16px", padding: "20px", border: "1px solid var(--border-card)", marginBottom: "14px", userSelect: "none", WebkitUserSelect: "none", MozUserSelect: "none" }}>
                <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
                  <Tag bg={ts.bg} color={ts.color}>{ts.label}</Tag>
                  <Tag bg="var(--bg-card-inner)" color="var(--text-muted)">{question?.topic}</Tag>
                  {bankQueueRef.current !== null && <Tag bg="#f59e0b11" color="#fbbf24">📂 Bank</Tag>}
                </div>
                {(() => {
                  const qtext = question?.question || "";
                  const wantMono = /[│├┤┬┴┼─┌┐└┘║╔╗╚╝╠╣╦╩╬]|<[-\s]+>|-{3,}|\|/.test(qtext);
                  return (
                    <div style={{
                      color: "var(--text-secondary)",
                      fontSize: "15px",
                      lineHeight: 1.8,
                      fontWeight: 500,
                      pointerEvents: "none",
                      whiteSpace: "pre-wrap",
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      fontFamily: wantMono ? "monospace" : undefined
                    }}>
                      {qtext.split('\n').map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              {question?.type === "programming" ? (
                <div>
                  {/* Language selector + Run button */}
                  <div style={{ display: "flex", gap: "8px", marginBottom: "8px", alignItems: "center", flexWrap: "wrap" }}>
                    <select value={codeLanguage}
                      onChange={e => { setCodeLanguage(e.target.value); codeLanguageRef.current = e.target.value; setCodeOutput(null); codeOutputRef.current = null; }}
                      style={{ background: "#161b22", border: "1px solid #30363d", borderRadius: "8px", color: "#e6edf3", padding: "6px 10px", fontSize: "13px", cursor: "pointer", fontFamily: "monospace" }}>
                      {CODE_LANGUAGES.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
                    </select>
                    <Btn variant="ghost" onClick={runCode} disabled={runningCode || !answer.trim()}
                      style={{ fontSize: "12px", padding: "6px 16px" }}>
                      {runningCode ? "⏳ Running..." : "▶ Run Code"}
                    </Btn>
                    {codeOutput && (
                      <span style={{ fontSize: "12px", color: codeOutput.error || (codeOutput.code !== 0 && codeOutput.code !== null) ? "#f87171" : "#4ade80" }}>
                        {codeOutput.error ? "⚠ Connection error" : codeOutput.code === 0 ? "✓ Ran successfully" : `⚠ Exit code ${codeOutput.code}`}
                      </span>
                    )}
                  </div>
                  {/* Code textarea with Tab-key indent support */}
                  <textarea value={answer}
                    onChange={e => { setAnswer(e.target.value); setCodeOutput(null); codeOutputRef.current = null; }}
                    onPaste={e => e.preventDefault()}
                    onKeyDown={e => {
                      if (e.key === "Tab") {
                        e.preventDefault();
                        const ta = e.target;
                        const s = ta.selectionStart, end = ta.selectionEnd;
                        const v = ta.value.substring(0, s) + "  " + ta.value.substring(end);
                        setAnswer(v); answerRef.current = v;
                        requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = s + 2; });
                      }
                    }}
                    placeholder="Write your code here..."
                    style={{ width: "100%", minHeight: "220px", background: "#0d1117", border: "1px solid #30363d", borderRadius: "10px", padding: "14px", color: "#e6edf3", fontSize: "13px", fontFamily: "monospace", lineHeight: 1.7, resize: "vertical", outline: "none", boxSizing: "border-box", tabSize: 2 }} />
                  {/* Execution output panel */}
                  {codeOutput && (
                    <div style={{ marginTop: "8px", background: "#0d1117", border: `1px solid ${codeOutput.error || (codeOutput.code !== 0 && codeOutput.code !== null) ? "#f8717155" : "#30363d"}`, borderRadius: "10px", padding: "12px 14px" }}>
                      <div style={{ color: "#8b949e", fontSize: "11px", fontFamily: "monospace", marginBottom: "6px", letterSpacing: "0.06em" }}>OUTPUT</div>
                      {codeOutput.error ? (
                        <div style={{ color: "#f87171", fontSize: "12px", fontFamily: "monospace" }}>{codeOutput.error}</div>
                      ) : (
                        <>
                          {codeOutput.stdout ? <pre style={{ color: "#3fb950", margin: 0, fontSize: "12px", fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{codeOutput.stdout}</pre> : null}
                          {codeOutput.stderr ? <pre style={{ color: "#f85149", margin: 0, fontSize: "12px", fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{codeOutput.stderr}</pre> : null}
                          {!codeOutput.stdout && !codeOutput.stderr && <span style={{ color: "#6e7681", fontSize: "12px", fontFamily: "monospace" }}>(no output)</span>}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <textarea value={answer} onChange={e => setAnswer(e.target.value)}
                  onPaste={e => e.preventDefault()}
                  placeholder="Type your answer here..."
                  style={{ width: "100%", minHeight: "150px", background: "var(--bg-input)", border: "1px solid var(--border-input)", borderRadius: "12px", padding: "14px", color: "var(--text-input)", fontSize: "14px", fontFamily: "inherit", lineHeight: 1.6, resize: "vertical", outline: "none", boxSizing: "border-box" }} />
              )}
              {autoSub && <p style={{ color: "#f97316", fontSize: "13px", marginTop: "6px" }}>⏰ Time's up! Auto-submitted.</p>}
              {err && <p style={{ color: "#f87171", fontSize: "13px" }}>{err}</p>}
              {skipConfirm && (
                <div style={{ background: "#f9731611", border: "1px solid #f9731633", borderRadius: "10px", padding: "12px 14px", marginTop: "10px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
                  <p style={{ color: "#fdba74", margin: 0, fontSize: "13px" }}>⚠️ Skipping scores 0. Confirm?</p>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <Btn style={{ padding: "7px 14px", fontSize: "12px" }} onClick={() => { setSkipConfirm(false); doSubmit(true, false); }}>Yes, Skip</Btn>
                    <Btn variant="ghost" style={{ padding: "7px 14px", fontSize: "12px" }} onClick={() => setSkipConfirm(false)}>Cancel</Btn>
                  </div>
                </div>
              )}
              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                <Btn style={{ flex: 1 }} onClick={() => doSubmit(false, false)} disabled={submitting || !answer.trim()}>
                  {submitting ? "Submitting..." : "Submit Answer ✓"}
                </Btn>
                <Btn variant="warning" onClick={() => setSkipConfirm(true)} disabled={submitting || skipConfirm}>⏭ Skip</Btn>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (phase === "done") return (
    <div style={{ minHeight: "100vh", background: "var(--bg-page)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px", fontFamily: "'Segoe UI',sans-serif" }}>
      <div style={{ maxWidth: "400px", width: "100%", textAlign: "center" }}>
        <Logo height={42} />
        <div style={{ margin: "26px 0 20px" }}>
          <div style={{ fontSize: "50px", marginBottom: "12px" }}>🎉</div>
          <h1 style={{ color: "var(--text-primary)", fontSize: "22px", margin: "0 0 8px" }}>Interview Complete!</h1>
          <p style={{ color: "var(--text-muted)", fontSize: "14px", margin: "0 0 20px" }}>Thank you, <strong style={{ color: "var(--text-secondary)" }}>{testData?.candidateName}</strong>!</p>
          <Card>
            <p style={{ color: "#4ade80", fontSize: "13px", margin: "0 0 8px" }}>✅ Your interview has been submitted successfully.</p>
            <p style={{ color: "var(--text-faint)", fontSize: "12px", margin: 0 }}>The ISG HR team will review your responses and get back to you shortly. You may now close this window.</p>
          </Card>
        </div>
      </div>
    </div>
  );

  return null;
}

// ── ROOT ──
export default function App() {
  const [mode, setMode] = useState("select");
  const [currentUser, setCurrentUser] = useState(() => getStoredUser());
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  function handleLogin(user) {
    setCurrentUser(user);
    setMode("hr");
  }

  function handleLogout() {
    localStorage.removeItem("authToken");
    localStorage.removeItem("authUser");
    setCurrentUser(null);
    setMode("select");
  }

  return (
    <ThemeContext.Provider value={theme}>
      <ThemeToggle theme={theme} setTheme={setTheme} />
      {mode === "select" ? (
        <div style={{ minHeight: "100vh", background: "var(--bg-page)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px", fontFamily: "'Segoe UI',sans-serif" }}>
          <div style={{ maxWidth: "420px", width: "100%", textAlign: "center" }}>
            <Logo height={50} />
            <h1 style={{ color: "var(--text-primary)", fontSize: "22px", fontWeight: 800, margin: "16px 0 28px" }}>Pariksha - Technical Evaluation</h1>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
              {[
                ["🏢", "Employee Portal", "Create tests & view reports", "hr-login"],
                ["🧑‍💻", "Candidate Portal", "Enter test code to begin", "candidate"]
              ].map(([icon, title, sub, target]) => (
                <div key={target} onClick={() => setMode(target)}
                  style={{ background: "var(--bg-card)", borderRadius: "16px", padding: "26px 16px", cursor: "pointer", border: "1px solid var(--border-card)" }}>
                  <div style={{ fontSize: "34px", marginBottom: "10px" }}>{icon}</div>
                  <p style={{ color: "var(--text-secondary)", fontWeight: 700, fontSize: "15px", margin: "0 0 4px" }}>{title}</p>
                  <p style={{ color: "var(--text-faint)", fontSize: "12px", margin: 0 }}>{sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : mode === "hr-login" ? (
        <LoginPage onLogin={handleLogin} />
      ) : mode === "hr" && currentUser ? (
        <HRDashboard onLogout={handleLogout} currentUser={currentUser} />
      ) : (
        <CandidatePortal />
      )}
    </ThemeContext.Provider>
  );
}

